// ===========================================================================
// App orchestrator: the client state machine that ties the landing screen, the
// chat screen, the signaling client, and the WebRTC peer together.
//
// States: landing -> (token + getUserMedia + connect) -> searching -> in-call,
// returning to searching on skip / peer-left, and back to landing on stop.
// ASCII only.
// ===========================================================================

import { t, currentLanguage } from './i18n.js';
import { mount, toast, el } from './dom.js';
import { SERVER_HTTP } from './config.js';
import { getFingerprint } from './device-signal.js';
import { getStoredAge, verifyAge, releaseSelfieCamera, RETAKES_BEFORE_HUMAN, watchForApproval } from './age.js';
import { rememberBand } from './band-memory.js';
import { getKeepsake, storeKeepsake, keepsakeMe } from './keepsake.js';
import { rememberChat, forgetChat, chatToResume } from './chat-seed.js';
import { Signaling } from './signaling.js';
import { channels } from './socket-channels.js';
import { createVisitor } from './visitor.js';
import { PeerConnection } from './rtc.js';
import { SfuPeer } from './sfu-client.js';
import { renderLanding } from './ui/landing.js';
import { createAudioEnhancer, createVideoEnhancer, createFocusSegmenter } from './media-effects.js';
import { renderChat } from './ui/chat.js';
import { renderVerify } from './ui/verify.js';
import { renderSupport } from './ui/support.js';
import { renderRooms, renderGroupChat } from './ui/rooms.js';
import { AccountClient } from './account.js';
import { openBilling } from './ui/billing.js';
import { openAccount } from './ui/account-panel.js';
import { showDeviceNotice, ackKeepsakeNotice, keepsakeStatus, clearKeepsake } from './ui/devices.js';
import { openRegulars, createBell, showNoticeToast } from './ui/regulars.js';
import { showCallingSheet, showOfferSheet, setCallChrome, callErrorText } from './ui/call-sheets.js';
import { createCommunityClient, renderCommunityCard, openCommunityLanding, renderCommunitySection, renderCommunityPanel } from './ui/community.js';
import { newMessageId } from './ui/message-ui.js';
import { createStaffInboxClient, hasStaffThread, staffUnread, sameStaffThread } from './staff-inbox.js';
import { playJoinTone, playNoticeTone, primeAudio, getJoinSoundOn } from './tones.js';
import { startTabAttention, noteMessage, noteMatched, noteDecided } from './tab-attention.js';
import { pickReportPicture } from './report-picture.js';
import { preferredLanguage, rememberLanguage } from './chat-lang.js';
import { createRemoteVideoWatch } from './video-presence.js';
import { seesPerson, createProbeVideo, lookForPerson } from './person-check.js';
import { ClientMsg, ServerMsg, ChatMode, STAFF_PERSON, isShareableFile, videoProvider, VIDEO_SLOTS, GROUP_VIDEO_MAX_BITRATE, RECONNECT_GRACE_MS, noteLimitFor } from '@lumi/shared';


/**
 * Try to obtain a session token. Returns { status: 'ok', token, policy, ... },
 * or { status: 'verification_required' } when the server wants an age check, or
 * throws for a ban / bad input / outage.
 */
/**
 * The session token last minted, and when it lapses.
 *
 * A session token lives two minutes, and before this every render of the
 * landing minted a fresh one -- because the landing opens a lobby socket, and
 * opening it starts with obtainSession. Reloading the page a few times while
 * testing, or a socket dropping and rejoining, was enough to walk straight
 * into the per-device rate limit on /api/session: 35 of them in one fifteen
 * minute window from a single browser, each one answered 429, which the client
 * shows as "Lumi is unavailable right now".
 *
 * The token is the same for the same person on the same device for its whole
 * life, so minting a second one inside that window buys nothing at all.
 */
let cachedSession = null;

/** Forget it: the answer would be different now (a new age proof, a sign-in). */
function dropCachedSession() {
  cachedSession = null;
}

/**
 * What a session answer says about who the page is: an account token whose
 * device was signed out, and a notice of a sign-in to the kept name from
 * another device. Set by createApp, which has the page to tell.
 */
let sessionHeard = () => {};

async function obtainSession(birthYear, fingerprint, ageToken, accountToken) {
  // Reuse while there is comfortably more than a connect's worth of life left.
  // Thirty seconds of margin, because the token has to survive the upgrade it
  // is about to be used for, not merely the moment it is handed over.
  if (cachedSession && cachedSession.expiresAt - Date.now() > 30_000 && cachedSession.ageToken === (ageToken || null)) {
    return cachedSession.value;
  }
  const res = await fetch(`${SERVER_HTTP}/api/session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // The keepsake rides along so the server can recognize this device (see
    // keepsake.js); undefined when there is none, and the server issues one.
    body: JSON.stringify({ birthYear, fingerprint, ageToken, accountToken, keepsake: getKeepsake()?.key }),
  });
  if (res.status === 403) {
    const body = await res.json().catch(() => ({}));
    if (body.error === 'under_age') {
      throw new Error(body.message || 'You must be at least 13 to use Lumi.');
    }
    throw new Error('Access to Lumi has been restricted from this device.');
  }
  if (res.status === 429) {
    // Throttled, not broken. Saying "unavailable" here sent the client round
    // again immediately, which kept the bucket empty and turned a moment of
    // throttling into a permanent outage for that person.
    const retry = Number(res.headers.get('retry-after')) || 60;
    const err = new Error(`Too many attempts from here. Try again in ${retry} seconds.`);
    err.retryAfter = retry;
    err.throttled = true;
    throw err;
  }
  if (res.status === 400) throw new Error('Please choose a valid birth year.');
  if (res.status === 401) {
    const body = await res.json().catch(() => ({}));
    if (body.error === 'verification_required') {
      dropCachedSession();
      return { status: 'verification_required' };
    }
    throw new Error('Lumi is unavailable right now. Please try again.');
  }
  if (!res.ok) throw new Error('Lumi is unavailable right now. Please try again.');
  const body = await res.json();
  // Present only on issuance: a fresh key, or a replacement after the band
  // changed. Storing it is what lets the next visit be the same person.
  if (body.keepsake) storeKeepsake(body.keepsake);
  sessionHeard(body, accountToken || null);
  const value = { status: 'ok', ...body };
  // exp is seconds since the epoch, from the server.
  if (body.exp) {
    cachedSession = { value, expiresAt: Number(body.exp) * 1000, ageToken: ageToken || null };
  }
  return value;
}

export function createApp(root) {
  // Count messages that arrive while this tab is behind something else, and
  // put the count in the tab title. Started once, here, because the listeners
  // are on document and window and outlive every screen.
  startTabAttention();
  let signaling = null;
  let peer = null;
  let localStream = null; // raw capture (self-view)
  // Client-side media effects (applied to your own stream before it is sent).
  let audioEnhancer = null; // noise-suppression node wrapper (#9)
  let videoEnhancer = null; // canvas processor for blur (#8) + filters (#11)
  let rawCamTrack = null; // the unprocessed camera track (to swap back to)
  const effectPrefs = (() => {
    try {
      return JSON.parse(localStorage.getItem('lumi.effects') || '{}') || {};
    } catch {
      return {};
    }
  })();
  effectPrefs.filter = effectPrefs.filter || 'none';
  let sendStream = null; // what we send to the peer (mic routed through a gain node)
  let audioCtx = null;
  let micGain = null;
  let iceConfig = null;
  let chatApi = null;
  let selfId = '';
  let remoteReady = false;
  let pendingIce = [];
  // SFU (mediasoup) media path. When the server runs an SFU, media is relayed
  // through it (peers never see each other's IP) instead of peer-to-peer.
  let sfuEnabled = false;
  let sfuPeer = null;
  let sfuReqId = 0;
  const sfuPending = new Map(); // reqId -> { resolve, reject }
  let intent = '1on1'; // '1on1' | 'group'
  // What they picked on the start screen. The rooms hub opens on this, so
  // choosing Text and then pressing Group does not hand back a video room.
  let chosenMode = ChatMode.VIDEO;
  let handle = null; // optional display name the user picked
  let interest = null; // optional interests text (for interest matching)
  let videoAllowed = true; // whether this band may turn the camera on (policy)
  let myBand = null; // 'adult' | 'minor', from the session; decides whether links off Lumi are clickable
  let isModerator = false; // admin-granted peer-moderation power (kick for 15 min)
  let isStaff = false; // verified Lumi staff (admin-granted)
  // True while Lumi staff have a TEXT drop-in open with this session. While it
  // is, what you type goes to them rather than into the void -- you have no
  // partner yet, which is the whole reason they are talking to you.
  let adminChatOpen = false;
  let isPlusMember = false; // Lumi Plus perks active for this session
  let moderateImages = false; // server has an image moderator -> sample camera frames
  let modSampler = null; // interval handle for camera-frame sampling
  let modSampleVideo = null; // offscreen <video> the sampler draws from
  let asrEnabled = false; // server has speech-to-text -> sample audio (moderation + captions)
  let audioActive = false; // audio sampler loop running
  let audioTimer = null; // next-clip timer
  let audioRecorder = null; // the in-flight MediaRecorder
  let roomsApi = null; // the rooms-hub api, when browsing rooms
  let groupApi = null; // the group-room screen api, when in a group room
  let currentRoomMode = ChatMode.VIDEO; // the kind of room you are in, if any
  // Whether this room's video has been announced in the chat yet. Lives out
  // here because entering a room resets it and the handler reads it.
  let sawVideoState = false;
  let currentRoom = null; // the joined room descriptor
  let joinCode = null; // a group invite code parsed from the URL (/r/CODE), if any
  // "Come and talk to me": the code somebody arrived WITH (/i/CODE), and the
  // one this person can share. Two different things that must not be confused
  // -- the first is used once at HELLO, the second is shown for as long as
  // they are here.
  let arrivingInvite = null;
  let myInviteCode = null;
  let invitesAllowed = false; // from WELCOME: may this band hand out a link
  const roomHandles = new Map(); // peerId -> handle (group rooms)
  const mutedPeers = new Set(); // group peers I have muted (hide their media/text/files)
  const account = new AccountClient(); // optional adult account (Plus/Room Pro/cosmetics)
  // Whether the page was signed in the last time accountChanged ran, so a
  // sign-in is told from every other change that goes through that door.
  let wasSignedIn = account.loggedIn;
  let maxVideoBitrate = 0; // personal bitrate ceiling from WELCOME (a Plus perk)
  let videoSlots = null; // the group room's camera budget: { used, max, holders }
  // Regulars. `social` is WELCOME's word on whether the wave exists for this
  // band; the server refuses either way, so the client only decides what to
  // show. `lastPairId` is the chat that just ended -- the only one a wave is
  // for. The bells (chat header, rooms hub) share one unread count.
  let social = false;
  let messagesOn = false; // WELCOME's word on whether a regular's row gets a Message button
  let lastPairId = null;
  // The pair the server has said you are already regulars in (PAIR_REGULARS).
  // Held by id, because the chat it is about may have ended by the time the
  // wave would be offered, and the offer is for exactly one pair.
  let regularPairId = null;
  let regularsState = { regulars: [], invisible: false, quietHours: null, recent: [], blocked: [] }; // the last REGULARS
  // ...and `regularsState.staff`, which no REGULARS sets: where the conversation
  // with Lumi Staff stands, { count, unread, lastAt }, or absent until a peek
  // has said (setStaffThread). Every copy of the state below carries it over.
  let regularsPanel = null; // the open panel's api, while it is open
  let unreadNotices = 0;
  let landingRegularsCount = 0; // from /api/regulars/count, for the landing link
  let keepsakeName = null; // the username on this device's keepsake, when it was named (server keepsake-auth.js)
  let keepsakeUnnamed = false; // the server honours this device's keepsake and nobody has named it: the regulars panel's nudge
  const bells = new Set(); // bell apis on screen
  // ---- Calls between regulars (5.B4; ui/call-sheets.js) ----
  // `callsOn` is WELCOME's word on whether a regular can be called from here.
  // A call becomes an ordinary 1:1 pair; `inCall` is what makes its end return
  // you to your regulars rather than to the queue. `pairedNow` is whether a
  // pair is up at all, so an offer that arrives mid-chat is declined unseen.
  let callsOn = false;
  let inCall = false;
  let pairedNow = false;
  // A 1:1 whose socket just dropped keeps its media up while the server holds
  // the pair (RECONNECT_GRACE_MS), and not a moment longer: this is the timer
  // that tears it down if nobody hands the chat back in time.
  let mediaGrace = null;
  // The pair the SFU peer was last rebuilt for after a transport failure, so
  // a transport that keeps failing is rebuilt once and then left to the hold.
  let sfuRebuiltFor = null;
  let callingSheet = null; // the "Calling..." sheet, while it is up
  let offerSheet = null; // the ringing sheet, while it is up
  let afterCall = false; // a call just ended: search again once the panel closes
  // My community. `communityCode` is a /c/CODE link waiting for a session (or
  // an Open pressed before there was one); `communityState` is the last
  // COMMUNITY_STATE for the room we are in; the panel and the card redraw
  // from it. Everything else about communities goes over HTTP.
  let communityCode = null;
  let communityState = null;
  let communityPanel = null; // the in-room panel's api, while in a community room
  let communityCard = null; // the card in the regulars panel, while it is open
  let communitySection = null; // the hub's "Your community" section, while on the hub
  const communityClient = createCommunityClient({ base: SERVER_HTTP, headers: personHeaders });
  // Lumi Staff's conversation with this person: HTTP only, person-authenticated
  // as the regulars list is (see "Lumi Staff" below).
  const staffInbox = createStaffInboxClient({ base: SERVER_HTTP, headers: personHeaders });
  let staffAsked = 0; // the newest question about the thread; a peek answered after a newer one is stale
  let videoWaiting = false; // camera on, but every slot was taken when we asked
  let shareKey = null; // proves a file upload belongs to this session (from WELCOME)
  // ---- Lower-pressure starts ----
  let selfBand = 'adult'; // from WELCOME; a minor never auto-reveals, whatever the browser remembers
  let cameraStart = 'agree'; // 'agree' (default): cameras open when both tap; 'open': REVEAL on match
  let timedPref = false; // asked for ten-minute chats on the landing (HELLO { timed: true })
  // The explicit lane (adults, operator switch on). Chosen in ONE place -- the
  // in-chat settings popover -- because it is not advertised anywhere on the
  // site, least of all the landing, which minors see. Remembered per TAB, like
  // the age proof: it should not outlive the visit on a shared device.
  let explicitAvailable = false; // this session may have it at all (from WELCOME)
  let explicitPref = false; // which lane to be matched into next
  // The language this person reads messages in: the site's guess to begin
  // with (chat-lang.js), then whatever they pick in the chat header; null is
  // "don't translate". Sent as HELLO lang and, on a change, SET_LANG.
  let chatLang = null;
  // The languages the server translates between (WELCOME); empty means none,
  // and no control is shown.
  let translateLangs = [];

  /** Give the screen on show the languages and the current choice. */
  function pushLanguageControl() {
    chatApi?.setTranslateLanguages?.(translateLangs, chatLang);
    groupApi?.setTranslateLanguages?.(translateLangs, chatLang);
  }

  /** The person picked a language in a header, or "Don't translate" (null). */
  function chooseLanguage(code) {
    chatLang = code || null;
    rememberLanguage(chatLang);
    signaling?.send(ClientMsg.SET_LANG, { lang: chatLang });
    pushLanguageControl();
  }
  // What it takes to mint a SECOND session token for this visit. A token lives
  // two minutes, so the one the page started with is long dead by the time a
  // connection drops -- a reconnect has to ask for a new one, and these are the
  // answers it would otherwise have to ask the person for again.
  let resumeSeed = null; // { birthYear, effectiveMode } once a session is open
  let reconnecting = false; // one attempt loop at a time, however many closes arrive

  try {
    explicitPref = sessionStorage.getItem('lumi.explicit') === '1';
  } catch {
    /* no storage: start in the ordinary lane */
  }
  function setExplicitPref(on) {
    explicitPref = on === true;
    try {
      sessionStorage.setItem('lumi.explicit', explicitPref ? '1' : '0');
    } catch {
      /* non-fatal */
    }
    signaling?.send(ClientMsg.SET_EXPLICIT, { on: explicitPref });
    chatApi?.setExplicitLane?.({ available: explicitAvailable, on: explicitPref });
  }
  let reveal = { me: false, them: false, both: false }; // the pair's camera reveal, per match
  let producingVideo = false; // a camera produce is in flight (REVEAL_STATE and start() can race)
  let explicitAgreed = false; // MY half of the consent in the current pair
  let slowDownTimer = null; // retry READY after a slow_down (the per-hour cap on minors)

  function applyCosmetics() {
    const s = account.settings || {};
    document.documentElement.dataset.theme = s.theme || '';
    document.documentElement.dataset.orb = s.orbSkin || '';
    document.documentElement.dataset.plus = account.isPlus ? '1' : '';
  }

  // Site-wide announcement banner, set by the admin (Settings tab). Pushed
  // down the page's socket on connect and whenever it changes (it was fetched
  // every two minutes); a changed message reappears even if the user
  // dismissed an earlier one (dismissal is remembered per exact text).
  function showAnnouncement(text) {
    let dismissedFor = '';
    try {
      dismissedFor = sessionStorage.getItem('lumi.announce.dismissed') || '';
    } catch {
      /* storage unavailable */
    }
    const existing = document.querySelector('.announce-banner');
    if (!text || text === dismissedFor) {
      existing?.remove();
      return;
    }
    if (existing) {
      if (existing.dataset.text !== text) {
        existing.dataset.text = text;
        existing.querySelector('span').textContent = text;
      }
      return;
    }
    const banner = el('div', { class: 'announce-banner', role: 'status', dataset: { text } }, [
      el('span', { text }),
      el('button', {
        class: 'announce-close',
        'aria-label': 'Dismiss announcement',
        text: 'x',
        onClick: () => {
          try {
            sessionStorage.setItem('lumi.announce.dismissed', banner.dataset.text || '');
          } catch {
            /* not remembered, still dismissed */
          }
          banner.remove();
        },
      }),
    ]);
    document.body.append(banner);
  }

  // Grab the accused's CURRENT video frame for a report. In 1:1 that is the one
  // remote video; in a group room it is that member's tile. Returns a base64
  // JPEG, or null when there is nothing on screen (audio/text chat, camera off).
  // This is the reporter's own view, so treat it as their claim -- evidence for
  // a human to weigh, never grounds for an automated action.
  function grabPeerFrame(peerId) {
    try {
      const v = peerId
        ? document.querySelector(`.tile[data-peer="${peerId}"] video`)
        : document.getElementById('remoteVideo');
      if (!v || !v.videoWidth || !v.videoHeight) return null;
      const c = document.createElement('canvas');
      const ctx = c.getContext('2d');
      // Native size first, made smaller until it fits what the server takes
      // with a report, or left off. See report-picture.js.
      return pickReportPicture((scale, quality) => {
        c.width = Math.max(1, Math.round(v.videoWidth * scale));
        c.height = Math.max(1, Math.round(v.videoHeight * scale));
        ctx.drawImage(v, 0, 0, c.width, c.height);
        return c.toDataURL('image/jpeg', quality).split(',')[1] || null;
      });
    } catch {
      return null; // a report must never fail because the snapshot did
    }
  }

  // ---- Admin drop-in: a consent-first live session with an administrator ----
  let dropInPc = null;
  let dropInMedia = null;
  function stopDropIn() {
    try { dropInPc?.close(); } catch { /* closed */ }
    dropInPc = null;
    dropInMedia?.getTracks().forEach((track) => track.stop());
    dropInMedia = null;
    document.querySelector('.dropin-overlay')?.remove();
  }
  function showAdminDropIn(m) {
    if (!m || !m.offer) return;
    stopDropIn(); // a new request replaces any previous one
    // mode describes what the ADMIN sends: 'video' means they appear on camera
    // too, 'audio' means you hear their voice only. Either way you join with
    // camera and microphone, which the consent text states plainly.
    const adminOnCamera = m.mode !== 'audio';
    const status = el('div', {
      class: 'dropin-status',
      text: adminOnCamera
        ? 'An administrator would like to video chat with you. They will see and hear you, and you will see them.'
        : 'An administrator would like to talk with you. They will see and hear you; you will hear their voice.',
    });
    const remote = el('video', { autoplay: '', playsinline: '', class: 'dropin-remote hidden' });
    const self = el('video', { autoplay: '', playsinline: '', muted: '', class: 'dropin-self hidden' });
    const startBtn = el('button', { class: 'btn btn-primary btn-sm', type: 'button', text: t('Start') });
    const endBtn = el('button', { class: 'btn btn-ghost btn-sm', type: 'button', text: t('Not now'), onClick: stopDropIn });
    const overlay = el('div', { class: 'dropin-overlay' }, [
      el('div', { class: 'dropin-card' }, [
        el('div', { class: 'dropin-title', text: t('Lumi admin') }),
        remote,
        self,
        status,
        el('div', { class: 'dropin-actions' }, [startBtn, endBtn]),
      ]),
    ]);
    startBtn.addEventListener('click', async () => {
      startBtn.disabled = true;
      status.textContent = 'Starting your camera...';
      try {
        // Camera + mic in BOTH modes -- the admin always sees you; the mode
        // only decides whether you see them.
        try {
          dropInMedia = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: true });
        } catch {
          dropInMedia = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
        }
        const cfg = m.ice ? { iceServers: m.ice.iceServers, iceTransportPolicy: m.ice.iceTransportPolicy } : {};
        dropInPc = new RTCPeerConnection(cfg);
        for (const track of dropInMedia.getTracks()) dropInPc.addTrack(track, dropInMedia);
        dropInPc.ontrack = (e) => {
          if (!e.streams[0]) return;
          // The element also plays the admin's VOICE, so it stays in the DOM
          // either way; it is only shown once their video actually arrives.
          remote.srcObject = e.streams[0];
          if (e.streams[0].getVideoTracks().length) remote.classList.remove('hidden');
        };
        dropInPc.onconnectionstatechange = () => {
          if (['failed', 'disconnected', 'closed'].includes(dropInPc?.connectionState)) stopDropIn();
        };
        await dropInPc.setRemoteDescription(JSON.parse(m.offer));
        const answer = await dropInPc.createAnswer();
        await dropInPc.setLocalDescription(answer);
        await new Promise((resolve) => {
          if (dropInPc.iceGatheringState === 'complete') { resolve(); return; }
          const iceTimer = setTimeout(resolve, 4000);
          dropInPc.addEventListener('icegatheringstatechange', () => {
            if (dropInPc.iceGatheringState === 'complete') { clearTimeout(iceTimer); resolve(); }
          });
        });
        signaling?.send(ClientMsg.ADMIN_LIVE_ANSWER, { answer: JSON.stringify(dropInPc.localDescription) });
        self.srcObject = dropInMedia;
        self.classList.remove('hidden');
        startBtn.classList.add('hidden');
        endBtn.textContent = 'End';
        const hasMic = (dropInMedia.getAudioTracks?.() || []).length > 0;
        status.textContent = hasMic
          ? 'Live with the admin: they can see and hear you.'
          : 'Live with the admin: they can see you (your mic is blocked).';
      } catch (err) {
         
        console.warn('admin drop-in failed', err);
        stopDropIn();
        toast(
          err && err.name === 'NotAllowedError'
            ? 'Your camera and microphone are blocked for this site: allow them in your browser settings to talk with the admin.'
            : `Could not start the chat with the admin (${(err && err.name) || 'error'}).`,
        );
      }
    });
    document.body.append(overlay);
  }

  // A celebratory, energetic banner shown when an admin grants the user Lumi Plus. It
  // acknowledges the notice (so it shows once), auto-dismisses, and animates in.
  function showPlusBanner() {
    if (document.querySelector('.plus-banner')) return;
    const banner = el('div', { class: 'plus-banner' }, [
      el('div', { class: 'plus-banner-glow' }),
      el('div', { class: 'plus-banner-body' }, [
        el('div', { class: 'plus-banner-title', text: t('You have been given Lumi Plus!') }),
        el('div', {
          class: 'plus-banner-sub',
          text: t('Priority matching, higher video quality, and exclusive cosmetics are now yours. Enjoy!'),
        }),
      ]),
      el('button', { class: 'plus-banner-close', text: t('Nice!'), onClick: dismiss }),
    ]);
    let dismissed = false;
    function dismiss() {
      if (dismissed) return;
      dismissed = true;
      banner.classList.add('out');
      setTimeout(() => banner.remove(), 450);
      account.ackNotice().catch(() => {});
    }
    document.body.append(banner);
    requestAnimationFrame(() => banner.classList.add('in'));
    setTimeout(() => banner.isConnected && dismiss(), 9000);
  }

  function checkNotice() {
    if (account.notice && account.notice.type === 'plus_granted') showPlusBanner();
    // A new device signed in to the account: said on every other device of
    // the owner's until it is dismissed on one of them (ui/devices.js).
    if (account.deviceNotice) {
      showDeviceNotice(account.deviceNotice, {
        onReview: () => openAccountPanel(),
        onDismiss: () => account.ackDeviceNotice().catch(() => {}),
      });
    }
  }

  // A session answer that says the account token here was signed out, or that
  // the kept name was signed in to from another device (obtainSession). The
  // session was minted without the account already, so the page only has to
  // stop offering it; the socket is not touched mid-start.
  sessionHeard = (body, sentToken) => {
    // Only the token the door refused: one stored meanwhile is a new sign-in.
    if (body.accountSignedOut && account.token && account.token === sentToken) {
      account.logout();
      toast('This device was signed out of your account. Sign in again to use it here.');
      applyCosmetics();
      syncAccountLink();
      wasSignedIn = false;
    }
    if (body.keepsakeNotice) {
      showDeviceNotice(body.keepsakeNotice, {
        onReview: () => openAccountPanel(),
        onDismiss: () => ackKeepsakeNotice({ base: SERVER_HTTP }).catch(() => {}),
      });
    }
  };

  /**
   * The server says this device was signed out (ServerMsg.SIGNED_OUT): by its
   * owner on another device, a change or reset of the password, or one
   * device too many. Asked again rather than taken on trust, so a device that
   * has just signed back in keeps its new sign-in; then the page is whoever
   * it is now.
   */
  async function signedOutHere(m) {
    if (m?.what === 'keepsake') {
      // Only the key that was asked about: one stored meanwhile (this device
      // signing back in) is its new sign-in.
      const held = getKeepsake()?.key || null;
      if ((await keepsakeStatus({ base: SERVER_HTTP })) !== 'gone' || (getKeepsake()?.key || null) !== held) return;
      clearKeepsake();
      dropCachedSession();
      toast('This device was signed out of your name. Sign in again to use it here.');
      keepsakeChanged();
      return;
    }
    if (!account.token) return;
    await account.refresh().catch(() => null);
    if (account.token) return;
    dropCachedSession();
    toast('This device was signed out of your account. Sign in again to use it here.');
    accountChanged();
  }

  /**
   * The account as the server now has it, pushed down the page's socket the
   * moment anything about it changes (ServerMsg.ACCOUNT): a Plus grant, a
   * purchase settling, a badge. This used to be asked for every minute and on
   * every return to the tab.
   */
  function accountPushed(m) {
    if (!account.token || !m?.account) return;
    account.account = m.account;
    applyCosmetics();
    checkNotice();
    syncAccountLink();
  }

  /**
   * Signing in or out changes who the page's socket is. A lobby socket is
   * opened again with the account, or without it; a visitor socket likewise.
   * A socket with a chat on it is left alone: a reconnect carries the account.
   */
  function refreshSocketIdentity() {
    dropCachedSession();
    if (signaling && !helloSent) {
      const old = signaling;
      signaling = null;
      channels.unbind(old);
      old.close();
    } else if (signaling && helloSent && !chatApi && !groupApi && !pairedNow && !inCall && !callingSheet && !currentRoom) {
      // Past the gate but IDLE -- on the hub, or between chats -- and the
      // socket was minted as somebody else (a keepsake, or the old
      // account). It used to be left as it was, because dropping a socket
      // mid-chat ends the chat; nobody is mid-chat here. The raw ws is
      // closed rather than signaling.close(), which would mark the instance
      // dead for good; reconnect() then mints a fresh session AS THE
      // ACCOUNT, re-sends HELLO, and WELCOME puts them back where they
      // were. The old socket's onclose is generation-guarded, so it cannot
      // tear down its own replacement.
      channels.unbind(signaling);
      try {
        signaling.ws?.close();
      } catch {
        /* already gone */
      }
      reconnect();
      return;
    }
    if (!signaling) visitor.stop();
    joinLobby().then(ensureSocket);
    ensureSocket();
  }

  /** What every door that can sign somebody in does afterwards. */
  function accountChanged() {
    applyCosmetics();
    checkNotice(); // a fresh sign-in / claim may carry an admin-granted Plus notice
    syncAccountLink();
    refreshSocketIdentity();
    peekStaffInbox(); // who the page is has changed, and the thread is a person's
    if (account.loggedIn && !wasSignedIn) offerAdoption();
    wasSignedIn = account.loggedIn;
  }

  /**
   * What this device did before you signed in: the waves, the regulars and
   * the chats made as its keepsake. A keepsake is the device, and a device
   * can be shared, so signing in does not carry them into the account by
   * itself -- but a person who chatted, waved, and then signed in (or
   * forgot to first) has lost nothing yet, and can say the rows are theirs.
   * Asked once per sign-in, only when there is something to carry, in the
   * band the session is in (server /api/regulars/adopt). Creating an
   * account carries them without asking: the account is new, and theirs.
   */
  async function offerAdoption() {
    const ks = getKeepsake();
    if (!ks || !account.token) return;
    try {
      // The keepsake alone: what is on this device and not on the account.
      const r = await fetch(`${SERVER_HTTP}/api/regulars/list`, { headers: { 'X-Lumi-Keepsake': ks.key } });
      if (!r.ok) return;
      const mine = await r.json();
      const regulars = (mine.regulars || []).length;
      const recent = (mine.recent || []).length;
      if (!mine.social || regulars + recent === 0) return;
      const what = [
        regulars ? `${regulars} regular${regulars === 1 ? '' : 's'}` : '',
        recent ? `${recent} recent chat${recent === 1 ? '' : 's'} you could still wave at` : '',
      ].filter(Boolean).join(' and ');
      if (!window.confirm(`Before you signed in, this device made ${what}. Bring them into your account? They will follow you to any device you sign in on.`)) return;
      const res = await fetch(`${SERVER_HTTP}/api/regulars/adopt`, {
        method: 'POST',
        headers: { ...personHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ keepsake: ks.key }),
      });
      if (!res.ok) {
        toast('Could not bring them into your account just now.');
        return;
      }
      toast('Brought into your account.');
      refreshRegularsOverHttp();
    } catch {
      /* a courtesy: the rows are still on the keepsake this browser holds */
    }
  }

  /**
   * This device's keepsake was named, signed in to, or forgotten (server
   * keepsake-auth.js), so the person the socket speaks for may have changed
   * -- a different keepsake id -- and a session minted for the old key is
   * stale. The same door as a sign-in -- accountChanged is the one place the
   * socket is reopened -- and the name on the account link.
   */
  function keepsakeChanged() {
    refreshKeepsakeName();
    accountChanged();
  }

  /** The regulars panel's nudge: the account panel, with the keep form already open. */
  function keepIdentity() {
    regularsPanel?.close?.();
    openAccountPanel({ keep: true });
  }

  /** The name on this device's keepsake, for the account link and the handle offer. */
  function refreshKeepsakeName() {
    keepsakeMe({ base: SERVER_HTTP })
      .then((me) => {
        keepsakeName = me?.username || null;
        keepsakeUnnamed = !!me && !me.username;
        syncAccountLink();
        offerAccountName();
      })
      .catch(() => {});
  }

  function openPlus() {
    openBilling(account, { onChange: accountChanged });
  }

  /** The shop, opened at the promotion section for one room. */
  function openPromo(roomCode) {
    openBilling(account, { promoRoom: roomCode, onChange: accountChanged });
  }

  // Keep the topbar's account button honest without re-rendering the landing,
  // which would discard a half-typed name, birth year, or interests.
  // The text-only layout hands the whole screen to the chat while there is no
  // video, and gives it back the moment a camera comes on -- either camera.
  // Evaluated from the live streams rather than from the chosen mode, because a
  // text chat where someone later enables video must not stay text-shaped.
  let remoteHasVideo = false;
  // Whether the other person's picture is really arriving, not just a track:
  // a camera turned off arrives as black frames. See video-presence.js.
  let remoteVideoLive = false;
  let remoteWatch = null;
  // How long the "Say hi to Bea!" greeting stays up before taking itself away.
  // Long enough to read a name, short enough that it is never the thing you are
  // staring at while you wait to see somebody.
  const GREETING_MS = 3000;

  /**
   * Keep an eye on whether the other person's picture is arriving, and redraw
   * the layout when that changes. Started when their stream arrives (once, for
   * however many tracks it gains) and stopped when it goes.
   */
  function watchRemoteVideo() {
    const video = chatApi?.remoteVideo;
    if (!remoteHasVideo || !video) {
      remoteWatch?.stop();
      remoteWatch = null;
      remoteVideoLive = false;
      refreshTextOnlyLayout();
      return;
    }
    if (!remoteWatch) {
      remoteWatch = createRemoteVideoWatch({
        video,
        onChange: (live) => {
          remoteVideoLive = live;
          refreshTextOnlyLayout();
        },
      });
    }
    refreshTextOnlyLayout();
  }

  function refreshTextOnlyLayout() {
    if (!chatApi) return;
    // A camera counts when somebody can actually see it (the operator,
    // 2026-09-15: "only when someone has a camera on is the text chat window
    // small"). Mine: on, and the pair has agreed to show cameras, so the other
    // person sees it; a camera captured but not yet shown used to hand the
    // window to a black stage. Theirs: picture really arriving, not merely a
    // track, because a camera they turned off still arrives as black frames.
    const mineOn = (localStream?.getVideoTracks?.() || []).some((track) => track.enabled && track.readyState === 'live');
    const mine = mineOn && !!reveal.both;
    chatApi.setVideoActive(mine || (remoteHasVideo && remoteVideoLive));
  }

  function syncAccountLink() {
    const link = root.querySelector('#accountLink');
    if (!link) return;
    // The account's name, else the name on this device's keepsake: a kept
    // identity is signed in the same way an account is, as far as the person
    // is concerned.
    const name = account.username || keepsakeName;
    link.textContent = name ? `@${name}` : t('Sign in');
    link.title = account.username ? 'Your account' : name ? 'Your kept identity' : 'Sign in or create an account';
  }

  // The landing usually renders before the account fetch returns (and a person
  // can sign in later without leaving it), so the account name is offered IN
  // PLACE -- re-rendering would discard anything typed in the meantime. Only
  // when the field is untouched AND they have never made a choice (no stored
  // key at all, not even an empty one, which records "I want to be anonymous").
  function offerAccountName() {
    const name = account.username || keepsakeName;
    if (!name) return;
    let chosen = null;
    try {
      chosen = localStorage.getItem('lumi.handle');
    } catch {
      /* storage unavailable */
    }
    const input = root.querySelector('#handle');
    if (!input || input.value || chosen !== null) return;
    input.value = name;
    const hint = root.querySelector('#handleHint');
    if (hint) {
      hint.textContent = account.username
        ? 'This is your account name. Clear it to stay anonymous: it is only shown if you leave it here.'
        : 'This is the name you kept. Clear it to stay anonymous: it is only shown if you leave it here.';
    }
  }

  /**
   * Person auth for the HTTP routes that know who you are across visits: the
   * account token when signed in, the keepsake key when there is one, both
   * when both (the server prefers the account). Empty means nobody to ask about.
   */
  function personHeaders() {
    const headers = {};
    if (account.token) headers.Authorization = `Bearer ${account.token}`;
    const ks = getKeepsake();
    if (ks) headers['X-Lumi-Keepsake'] = ks.key;
    return headers;
  }

  /** `keep`: open with the "keep this identity" form already unfolded (the regulars panel's nudge). */
  function openAccountPanel({ keep = false } = {}) {
    openAccount(account, {
      onPlus: openPlus,
      keep,
      // The band decides which doors the sign-in form has (a teen has no
      // account to try); from the session, else from the stored proof.
      band: myBand || getStoredAge()?.band || null,
      onKeepsake: keepsakeChanged,
      // The door to the regulars panel: shown when the feature is on for this
      // person (a live socket said so), the landing found they have some, or
      // they are signed in (an account holder can start a community there).
      // And when Lumi Staff has written, which is its own reason to open it.
      onRegulars: social || landingRegularsCount > 0 || account.loggedIn || hasStaffThread(regularsState.staff) ? openRegularsPanel : null,
      regularsCount: regularsState.regulars.length || landingRegularsCount,
      onChange: () => {
        accountChanged();
        offerAccountName();
      },
    });
  }

  // ---- Regulars ----
  // One bell per screen that has a header; all of them show the same count.
  function makeBell() {
    const bell = createBell({ onOpen: openRegularsPanel });
    bells.add(bell.api);
    bell.api.setUnread(unreadNotices, { messages: messagesWaiting() });
    return bell.root;
  }

  /**
   * Put a bell in the screen that is up, if this band has regulars at all.
   *
   * Called twice on purpose. The 1:1 chat screen is built BEFORE the socket
   * connects, so at render time nothing yet knows whether the wave exists --
   * `social` arrives with WELCOME, a moment later. Asking once at render meant
   * the answer was always "no" and the bell never appeared for anybody.
   * Idempotent: a slot that already holds a bell is left alone.
   */
  function ensureBell() {
    // Whether the wave exists for this band, or Lumi Staff has written: a note
    // from staff reaches a person whatever the band's switches say, so the
    // bell that says it is waiting does too.
    if (!social && !hasStaffThread(regularsState.staff)) return;
    // The screen that is up: the 1:1 chat, or the group room.
    const slot = chatApi?.bellSlot || groupApi?.bellSlot;
    if (slot && !slot.firstChild) slot.append(makeBell());
  }

  /**
   * The same for the rooms hub, whose bell sits in its top bar and is put
   * there from here (the hub knows nothing about regulars). Idempotent.
   */
  function ensureHubBell() {
    if (!roomsApi || (!social && !hasStaffThread(regularsState.staff))) return;
    const bar = root.querySelector('.topbar');
    if (bar && !bar.querySelector('.bell')) bar.append(makeBell());
  }

  /**
   * Messages from regulars nobody has read yet, counted from the rows the
   * SERVER sent. Not a tally kept here: a message waits across a reload, and
   * a count that started at zero every time was the reason one could sit
   * there unseen (the operator, 2026-09-23).
   */
  function regularsWaiting() {
    return (regularsState.regulars || []).reduce((n, r) => n + (Number(r.unread) || 0), 0);
  }

  /**
   * Everything waiting for this person, which is the number on the bell: the
   * regulars' messages, and the lines from Lumi Staff -- not a row in the list
   * but waiting all the same, and counted from what the server said.
   */
  function messagesWaiting() {
    return regularsWaiting() + staffUnread(regularsState.staff);
  }

  function syncBells() {
    const messages = messagesWaiting();
    for (const b of bells) b.setUnread(unreadNotices, { messages });
  }

  /** Walk into the room a notice or a regular's row named. By id, else by code. */
  function joinFromAround(around) {
    if (!signaling || !around) return;
    intent = 'group';
    if (around.roomId) signaling.send(ClientMsg.JOIN_ROOM, { roomId: around.roomId });
    else if (around.code) signaling.send(ClientMsg.JOIN_ROOM, { code: around.code });
  }

  /**
   * Enter a community's room. With a socket, the message; without one (the
   * landing), the code is kept and the next start opens it -- the same path a
   * /c/CODE link takes.
   */
  function openCommunity(code) {
    if (!code) return;
    if (signaling) {
      intent = 'group';
      regularsPanel?.close?.();
      signaling.send(ClientMsg.COMMUNITY_JOIN, { code });
      return;
    }
    communityCode = String(code).toUpperCase();
    regularsPanel?.close?.();
    toast('Pick your birth year and press Group rooms: your community opens from there.');
  }

  /**
   * The regulars panel. `thread` is a personId to open the conversation with
   * straight away: the toast that says a regular wrote to you goes to their
   * messages, not to a list the message is hidden one tap inside.
   */
  function openRegularsPanel({ thread = null } = {}) {
    unreadNotices = 0;
    syncBells();
    regularsPanel?.close?.();
    // The community card lives in this panel. Built once per opening and
    // handed over as a node, so the panel's own redraws (each REGULARS) move
    // it rather than rebuilding a half-typed session form.
    communityCard = renderCommunityCard({
      client: communityClient,
      connected: () => !!signaling,
      regulars: () => regularsState.regulars,
      onOpen: openCommunity,
      // The hub behind this panel draws the same community from its own copy
      // of /mine. Starting one here used to leave "Start a community" sitting
      // on the hub until a reload.
      onChanged: () => communitySection?.api.refresh(),
    });
    const opened = openRegulars({
      state: regularsState,
      // A socket to act through: what Block, Invisible, quiet hours, Join,
      // Call and Message each need, and what none of them can fake.
      //
      // Whether there is a LIST to draw is a different question, and the
      // panel answers that one itself, on every redraw, from the state it is
      // handed. It has to: this used to be one flag meaning both, worked out
      // once when the panel opened, so a panel opened from the landing before
      // the HTTP list arrived kept saying there was nothing after it came.
      connected: !!signaling && social,
      count: landingRegularsCount,
      onBlock: (personId) => signaling?.send(ClientMsg.REGULAR_BLOCK, { personId }),
      // Lifting a block: down the socket in a chat, over HTTP from the
      // landing -- the same two doors as a late wave.
      onUnblock: (personId) => {
        if (socketIsMine()) signaling.send(ClientMsg.REGULAR_UNBLOCK, { personId });
        else unblockOverHttp(personId);
      },
      // Off the list of people talked to this week (HTTP; a socket is sent the new list).
      onRemoveRecent: (personId) => removeRecentOverHttp(personId),
      // A private note about a regular: one door, HTTP, from a chat or the landing.
      // The note's limit is the band's: shorter in 13-17. From the session,
      // else the stored proof; not knowing gets the shorter one, and the
      // server holds the line either way.
      notes: { save: saveNoteOverHttp, maxChars: () => noteLimitFor(myBand || getStoredAge()?.band || null) },
      onJoin: joinFromAround,
      onCall: callsOn ? startCall : null, // calls (5.B4)
      canVideo: videoAllowed,
      onInvisible: (invisible) => signaling?.send(ClientMsg.PRESENCE_SET, { invisible }),
      // Quiet hours ride the same message; the server reads them off the same
      // prefs row and gates every notice on them.
      onQuietHours: (quietHours) => signaling?.send(ClientMsg.PRESENCE_SET, { quietHours }),
      onRefresh: () => {
        if (socketIsMine()) signaling.send(ClientMsg.REGULARS_LIST);
        else refreshRegularsOverHttp();
        peekStaffInbox();
      },
      // The nudge to name this device's keepsake, for a person whose regulars
      // hang off it alone: no account, and a keepsake the server honours that
      // nobody has named (refreshKeepsakeName). An account holder's regulars
      // are the account's; a named keepsake is already kept.
      onKeep: !account.loggedIn && keepsakeUnnamed ? keepIdentity : null,
      // A wave sent later, from the list of people you talked to this week.
      // Same wave, same seven days the site has always promised -- just not
      // only in the twenty seconds after the chat ended.
      onWavePerson: (personId) => {
        // A lobby socket has said no HELLO, so the server would refuse a wave
        // down it. HTTP is the door that is open from the landing.
        if (socketIsMine()) signaling.send(ClientMsg.WAVE, { person: personId });
        else waveOverHttp(personId);
      },
      communityCard: communityCard.root,
      onClose: () => {
        regularsPanel = null;
        // A call just ended and you looked at your regulars; closing the
        // panel without ringing anyone means back to finding someone new.
        if (afterCall) {
          afterCall = false;
          if (signaling && inOneOnOne() && !pairedNow && !callingSheet) signaling.send(ClientMsg.READY);
        }
        communityCard = null;
      },
      // Messages between regulars (ui/messages.js): the socket calls the
      // thread view makes. Null when the switch is off for this band, and the
      // rows simply have no Message button.
      messages:
        signaling && social && messagesOn
          ? {
              list: (withPersonId, before) => signaling?.send(ClientMsg.MSG_LIST, before ? { with: withPersonId, before } : { with: withPersonId }),
              send: (to, text, id, slow) => signaling?.send(ClientMsg.MSG_SEND, { to, text, id, slow: !!slow }),
              remove: (id) => signaling?.send(ClientMsg.MSG_DELETE, { id }),
              report: (id, reason) => signaling?.send(ClientMsg.MSG_REPORT, { id, reason }),
              newId: newMessageId,
            }
          : null,
      // Lumi Staff (ui/messages.js, staff mode). Over HTTP, so it is here
      // whatever this band's switches say, from the landing as from a chat.
      staff: {
        list: readStaffInbox,
        send: (text, id) => staffInbox.send(text, id),
        // Hiding changes what the person has to see, so the row and the door are asked again.
        hide: async (id) => {
          const r = await staffInbox.hide(id);
          if (r.ok) peekStaffInbox();
          return r;
        },
        newId: newMessageId,
      },
    });
    regularsPanel = opened.api;
    // Straight into their messages, when that is what was asked for. The row
    // is what the thread view names itself from; without one there is nothing
    // to open and the list stands as it is. Staff has no row in the list to
    // find: it is its own door.
    if (thread === STAFF_PERSON) regularsPanel?.openStaff?.();
    else if (thread && regularsPanel?.messages) {
      const row = (regularsState.regulars || []).find((r) => r.personId === thread);
      if (row) regularsPanel.messages.open(row);
    }
    // A fresh list, so the panel is not showing a stale "around". Down the
    // socket only when the socket is this person; else over HTTP, which
    // always is.
    if (socketIsMine() && social) signaling.send(ClientMsg.REGULARS_LIST);
    else if (account.loggedIn || social) refreshRegularsOverHttp();
    // And where the thread with staff stands -- unless it is the thread being
    // opened, whose read says so itself and must not be answered over.
    if (thread !== STAFF_PERSON) peekStaffInbox();
  }

  // ---- Calls (5.B4) ----------------------------------------------------------

  /**
   * Ring a regular. The sheet goes up at once; the server's first CALL_STATE
   * (ringing) names the call, and every later one moves the sheet along.
   * The server decides everything else, and says so in CALL_STATE or ERROR.
   */
  function startCall(personId, mode, handle) {
    if (!signaling || !callsOn) return;
    callingSheet?.close();
    callingSheet = showCallingSheet({
      handle,
      mode,
      onCancel: (callId) => {
        if (callId) signaling?.send(ClientMsg.CALL_CANCEL, { callId });
      },
      onClose: ({ connecting }) => {
        callingSheet = null;
        // A call that came to nothing leaves us idle on the 1:1 screen: the
        // server held us out of the queue while it rang, and puts us back
        // only if that is where we were. Asking again is harmless if it did.
        if (!connecting && signaling && inOneOnOne() && !pairedNow && !inCall) signaling.send(ClientMsg.READY);
      },
    });
    signaling.send(ClientMsg.CALL_INVITE, { personId, mode });
  }

  /** Answer an offer, or let the sheet answer for us (a timeout, a match arriving). */
  function respondToCall(callId, accept) {
    signaling?.send(ClientMsg.CALL_RESPOND, { callId, accept });
  }

  /**
   * A call ended, from either side. Not a search: you are back with your
   * regulars -- the panel opens over the chat, or over the rooms hub if that
   * is where you came from -- and the search resumes only when you close it
   * without ringing anyone (see openRegularsPanel). No Wave: you already are.
   */
  function endCallUi() {
    inCall = false;
    pairedNow = false;
    forgetChat();
    teardownPeer();
    resetPairUi();
    setCallChrome(root, null);
    toast('Call ended');
    if (intent === 'group') {
      // Back to the rooms you were browsing; the 1:1 screen the call drew is gone.
      chatApi = null;
      enterRoomsHub();
      openRegularsPanel();
      return;
    }
    chatApi?.setSkipEnabled(false);
    chatApi?.setStatus('Call ended', 'Your regulars are a tap away. Close the list to find someone new.');
    openRegularsPanel();
    afterCall = true; // after the open, so a panel that was already up does not close into a search
  }

  /**
   * The chat just ended: offer the wave, for this pair only. `reason` is the
   * server's word on how it ended; a report is never wavable, and the server
   * would refuse anyway. Nothing is offered when the feature is off for this
   * band (WELCOME.social) -- the controls simply do not exist for a minor.
   */
  function offerWave({ pairId, canWave = true, reason = null, line } = {}) {
    if (!chatApi || !pairId) return;
    // Never at a regular. They waved at each other already, the server would
    // refuse the wave, and being asked again reads as the site having
    // forgotten them (the operator, 2026-09-21).
    const already = regularPairId !== null && regularPairId === pairId;
    const ok = social && canWave && !already && reason !== 'report';
    chatApi.showEnded?.({
      pairId,
      canWave: ok,
      onWave: ok ? (id) => signaling?.send(ClientMsg.WAVE, { pairId: id }) : null,
      line,
    });
  }

  /**
   * The landing has no socket, so it asks over HTTP how many regulars this
   * person has, and shows "Regulars (N)" only when N > 0. Person auth is the
   * account token when signed in, else the keepsake key; with neither there
   * is nobody to ask about.
   */
  async function fetchRegularsCount() {
    const headers = {};
    if (account.token) headers.Authorization = `Bearer ${account.token}`;
    const ks = getKeepsake();
    if (ks) headers['X-Lumi-Keepsake'] = ks.key;
    if (!Object.keys(headers).length) return 0;
    try {
      const r = await fetch(`${SERVER_HTTP}/api/regulars/count`, { headers });
      if (!r.ok) return 0;
      const body = await r.json();
      return body.social ? Number(body.count) || 0 : 0;
    } catch {
      return 0;
    }
  }

  /**
   * The panel's data without a socket.
   *
   * It used to arrive only over the chat socket, so the panel worked only from
   * inside a chat -- which is the one place waving LATER is no use. The whole
   * point of the seven-day window is coming back to it from the home page.
   */
  async function fetchRegularsList() {
    const headers = personHeaders();
    if (!Object.keys(headers).length) return null;
    try {
      const r = await fetch(`${SERVER_HTTP}/api/regulars/list`, { headers });
      if (!r.ok) return null;
      const body = await r.json();
      return body.social ? body : null;
    } catch {
      return null;
    }
  }


  /** The panel's data over HTTP, for a landing whose socket has no session. */
  async function refreshRegularsOverHttp() {
    const next = await fetchRegularsList();
    if (!next) return;
    regularsState = {
      regulars: next.regulars || [],
      invisible: !!next.invisible,
      quietHours: next.quietHours || null,
      recent: next.recent || [],
      blocked: next.blocked || [],
      staff: regularsState.staff || null,
    };
    regularsPanel?.update(regularsState);
    showRegularsLink();
  }

  /** A wave with no socket to send it down. Same rules, server-side. */
  /**
   * Keep, change or delete the private note this person keeps about one of
   * their regulars (shared regular-notes.js). Always over HTTP, socket or
   * not: the server pushes the new list to this person's own tabs, and a
   * panel with no socket of its own is refreshed here.
   * @returns {Promise<{ ok: boolean, note?: string|null, reason?: string }>}
   */
  async function saveNoteOverHttp(personId, { nickname = '', text = '' } = {}) {
    const headers = { ...personHeaders(), 'Content-Type': 'application/json' };
    try {
      const r = await fetch(`${SERVER_HTTP}/api/regulars/note`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ person: personId, nickname: String(nickname || ''), text: String(text || '') }),
      });
      if (r.status === 429) return { ok: false, reason: 'rate_limited' };
      const body = await r.json().catch(() => ({}));
      if (!r.ok || !body.ok) return { ok: false, reason: body.reason || 'failed', limit: body.limit };
      if (!socketIsMine()) await refreshRegularsOverHttp();
      return body;
    } catch {
      return { ok: false, reason: 'failed' };
    }
  }

  /** Lift a block over HTTP, for a landing whose socket has no session. */
  async function unblockOverHttp(personId) {
    const headers = { ...personHeaders(), 'Content-Type': 'application/json' };
    try {
      const r = await fetch(`${SERVER_HTTP}/api/regulars/unblock`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ person: personId }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok || !body.ok) {
        toast('That block could not be lifted.');
        return;
      }
      toast('Unblocked.');
      await refreshRegularsOverHttp();
    } catch {
      toast('That block could not be lifted.');
    }
  }

  async function removeRecentOverHttp(personId) {
    const headers = { ...personHeaders(), 'Content-Type': 'application/json' };
    try {
      const r = await fetch(`${SERVER_HTTP}/api/regulars/recent/remove`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ person: personId }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok || !body.ok) toast('That could not be removed. Try again in a moment.');
    } catch {
      toast('That could not be removed. Try again in a moment.');
    }
    // What the server has now, either way: a socket of this person's is sent it.
    if (!socketIsMine()) await refreshRegularsOverHttp();
  }

  async function waveOverHttp(personId) {
    const headers = { ...personHeaders(), 'Content-Type': 'application/json' };
    try {
      const r = await fetch(`${SERVER_HTTP}/api/regulars/wave`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ person: personId }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok || !body.ok) {
        toast('That wave could not be sent.');
        return;
      }
      toast(body.mutual ? 'You are now regulars.' : 'Waved.');
      const next = await fetchRegularsList();
      if (next) {
        regularsState = {
          regulars: next.regulars || [],
          invisible: !!next.invisible,
          quietHours: next.quietHours || null,
          recent: next.recent || [],
          blocked: next.blocked || [],
          staff: regularsState.staff || null,
        };
        regularsPanel?.update(regularsState);
      }
    } catch {
      toast('That wave could not be sent.');
    }
  }


  // ---- Lumi Staff -------------------------------------------------------------
  // The operator can write to a person as Lumi Staff, in either band, and the
  // person can answer (ui/messages.js, staff mode). It is not a regular: it
  // needs no wave, no band match and no socket, and the social and messages
  // switches do not apply to it -- a note from staff is often the very thing
  // explaining a switch. So it is asked over HTTP like the landing's regulars
  // list, and what is known of it lives on regularsState.staff, where
  // everything that reads the regulars (the panel, the bells, the landing's
  // door) reads it too. A person can only ever REPLY; the server refuses a
  // first message, and the panel only has a row once staff has written.

  /**
   * What the page now knows of the thread, drawn wherever it shows: the panel's
   * pinned row, the bells, and the landing's door. A repeat of what is already
   * known draws nothing, and nor does a thread that holds no lines: staff never
   * wrote, which is the same as not knowing yet.
   */
  function setStaffThread(thread) {
    const next = hasStaffThread(thread) ? thread : null;
    if (sameStaffThread(regularsState.staff || null, next)) return;
    regularsState.staff = next;
    regularsPanel?.update(regularsState);
    syncBells();
    ensureBell();
    ensureHubBell();
    showRegularsLink();
  }

  /**
   * Ask how the thread stands, without marking anything seen. Asked when the
   * landing draws, on sign-in, when the socket is welcomed, and after a line
   * arrives. An answer that a newer question has overtaken is dropped.
   */
  async function peekStaffInbox() {
    const asked = ++staffAsked;
    const r = await staffInbox.peek();
    if (asked !== staffAsked) return;
    // A person with no identity at all has no thread; one that could not be
    // reached keeps what was known.
    if (r.ok) setStaffThread(r.thread);
  }

  /**
   * Open the thread: every line, and the server marks them seen, so the row
   * and the bell agree at once. Its answer is always believed -- it is the
   * newest word on what is unread -- and it overtakes any earlier peek.
   */
  async function readStaffInbox() {
    staffAsked += 1;
    const r = await staffInbox.read();
    if (r.ok) setStaffThread(r.thread);
    return r;
  }

  /**
   * A line from Lumi Staff, live, on every open tab of the person it is for.
   * With the thread open it goes into it, and a read tells the server it was
   * seen. Otherwise the row's badge and the bell go up, with the soft tone
   * the other message notices use and a toast that opens the thread.
   */
  function staffWrote(m) {
    // Lines Lumi Staff deleted: gone from an open thread, and the counts asked again.
    if (m && Array.isArray(m.removed)) {
      regularsPanel?.staff?.onStaffRemoved?.(m.removed);
      peekStaffInbox();
      return;
    }
    if (!m || typeof m.text !== 'string') return;
    if (regularsPanel?.staff?.onStaffMsg(m)) {
      noteMessage(); // the thread is open, but nobody may be looking at it
      readStaffInbox();
      return;
    }
    const was = regularsState.staff || { count: 0, unread: 0, lastAt: null };
    setStaffThread({ count: was.count + 1, unread: was.unread + 1, lastAt: typeof m.at === 'string' ? m.at : was.lastAt });
    noteMessage();
    playNoticeTone();
    showNoticeToast({ text: t('Lumi Staff sent you a message') }, { onAction: () => openRegularsPanel({ thread: STAFF_PERSON }), actionLabel: 'Read', ms: 12000 });
    peekStaffInbox(); // what the server says is the number that stands
  }

  /** How many people are on the site right now, as the server last said. */
  let lobbyCount = { here: 0, waiting: 0, inRooms: 0 };
  /** Whether the operator wants visitors told at all (a Settings switch). */
  let lobbyShown = false;
  /**
   * Has this socket said HELLO?
   *
   * The lobby opens one from the LANDING and deliberately does not -- HELLO is
   * what starts a chat. But nearly everything the server accepts is behind
   * _requireHello, so a landing that merely HAS a socket must not assume it
   * can talk down it. Waving from the home page broke the moment the lobby
   * shipped, for exactly this reason: it stopped using HTTP and started
   * sending down a socket that refuses it.
   */
  let helloSent = false;
  /**
   * The account token the live socket was minted with, or null.
   *
   * A socket is one person for its whole life: whoever /api/session said
   * you were when it opened. Sign in afterwards and the HTTP door answers
   * as the account while the socket still answers as the keepsake it was
   * born with -- "No regulars yet", Invisible ticked -- and whichever lands
   * last wins the panel (the operator, 2026-09-23, fresh browser, two
   * screenshots of the same person). So every socket is stamped, and one
   * that predates the sign-in is never asked for, or believed about, the
   * regulars.
   */
  let socketAccountToken = null;
  // And the keepsake it was minted for, for the same reason: signing in to a
  // named keepsake changes who the page is without an account moving.
  let socketKeepsakeId = null;
  // iOS Safari severs the socket whenever the page leaves the foreground and
  // may not deliver onclose until it is back. The moment the tab is visible
  // again, a socket that is not open is reconnected NOW rather than after the
  // backoff, so the server's hold is not spent waiting (Idk's iPhone, seven
  // drops in one chat, 2026-09-24).
  try {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      if (signaling && helloSent && resumeSeed && !reconnecting && !signaling.isOpen()) reconnect();
    });
    // And the other way. A page RELOAD is the one drop the hold could not
    // forgive: pull-to-refresh, or a phone discarding the tab and reloading
    // it on return, came back to the landing with no memory of the chat
    // (Poli, 2026-09-26). So the moment the tab is hidden, or is about to
    // go, the seed's clock is set, and the load below walks back in while
    // the server still holds the pair (chat-seed.js).
    const stampChat = () => {
      if (pairedNow && resumeSeed && !groupApi) rememberChat({ birthYear: resumeSeed.birthYear, mode: resumeSeed.effectiveMode });
    };
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') stampChat();
    });
    window.addEventListener('pagehide', stampChat);
  } catch {
    /* a page without a document (a test) has no tab to watch */
  }
  /** A lobby socket being opened (joinLobby), or null. */
  let lobbyJoining = null;
  /** In the current group room as an invisible staff drop-in: nothing is sent. */
  let roomInvisible = false;
  /** May we send things that need a session, or must we use HTTP? */
  const socketReady = () => !!signaling && helloSent;
  /** A ready socket that is the SAME person the page is signed in as. */
  const socketIsMine = () => socketReady() && socketAccountToken === (account.token || null) && socketKeepsakeId === (getKeepsake()?.id || null);

  /**
   * Join the lobby: open a socket from the LANDING, before anything is started.
   *
   * The site's whole failure is that people are present at the same time
   * without knowing it -- under three at once on average, a median visit of 42
   * seconds, and more than half leaving without ever joining the queue. Three
   * strangers each deciding the place is empty, at the same moment.
   *
   * A socket here costs the server almost nothing and buys the one thing
   * missing: everybody can be told the others are there. It needs no new
   * permission and no camera. HELLO is deliberately NOT sent -- that is what
   * starts a chat, and this person has started nothing.
   */
  async function joinLobby() {
    if (signaling) return; // already here, or already chatting
    // One join at a time. Every landing render asks, and so does a lobby
    // socket that dropped; a second caller waits for the first.
    if (lobbyJoining) return lobbyJoining;
    lobbyJoining = openLobbySocket().finally(() => {
      lobbyJoining = null;
    });
    return lobbyJoining;
  }

  async function openLobbySocket() {
    const proof = getStoredAge();
    if (!proof?.token) return; // not verified yet: nothing to connect with
    let session;
    try {
      const fingerprint = await getFingerprint();
      session = await obtainSession(resumeSeed?.birthYear, fingerprint, proof.token, account.token);
    } catch {
      return; // the lobby is a courtesy; never let it break the landing
    }
    if (session?.status !== 'ok' || !session.token) return;
    // Asked again after the waits above. A socket opened meanwhile would be
    // replaced here and left open on the server, still queued, with nothing on
    // the page holding it: the same person waiting to be paired two and three
    // times over, and a match on the lost socket answered down this one, which
    // never said HELLO (2026-09-15).
    if (signaling) return;
    const mine = new Signaling();
    try {
      signaling = mine;
      helloSent = false; // a lobby socket: connected, but it has started nothing
      wireSignaling();
      await mine.connect(session.token);
      socketAccountToken = account.token || null;
      socketKeepsakeId = getKeepsake()?.id || null;
      // The page's socket now: it carries the channels, and the visitor
      // socket, if one was held, goes.
      channels.bind(mine);
      ensureSocket();
    } catch {
      if (signaling === mine) signaling = null;
      // As in reconnect: the socket may have refused the token itself, so the
      // next attempt must not be handed the same one.
      dropCachedSession();
    }
  }

  /**
   * Who else is here, on the landing.
   *
   * This used to hide itself whenever nobody else was about, on the reasoning
   * that "0 other people here" is the one thing a quiet site must never say.
   * That reasoning was right and the conclusion was wrong: it meant that most
   * of the time the feature was invisible, including to the person who owns
   * the site, and an empty landing said nothing at all -- which is exactly
   * what it said before any of this existed.
   *
   * The real failure is not that the site is empty, it is that people leave
   * after 42 seconds without knowing whether anybody is coming. So being first
   * is a thing to be TOLD, with a reason to stay: keep the tab open and you
   * will know the moment somebody arrives. That is a use for the quiet.
   */
  function showLobbyCount() {
    const host = root.querySelector('#lobbyCount');
    if (!host) return;
    if (!lobbyShown) {
      host.hidden = true;
      host.textContent = '';
      return;
    }
    // A live socket counts YOU; the public poll does not.
    const others = Math.max(0, (lobbyCount.here || 0) - (signaling ? 1 : 0));
    const waiting = lobbyCount.waiting || 0;
    if (others > 0) {
      host.classList.remove('lobby-empty');
      host.textContent = waiting
        ? t('{n} here now, {w} waiting to chat', { n: others, w: waiting })
        : t('{n} other {people} here right now', { n: others, people: others === 1 ? 'person' : 'people' });
    } else {
      // Never "0 people here". An invitation, and a true one: the tab is told
      // the moment the count moves.
      host.classList.add('lobby-empty');
      host.textContent = t('You are first. Keep this tab open and you will see when someone arrives.');
    }
    host.hidden = false;
  }



  /**
   * What the landing shows, on whichever socket the page holds.
   *
   * Before a session there is the visitor socket (visitor.js); after, the
   * session socket. Both are pushed the same frames: who else is here, the
   * front-page rooms, the announcement, whether admin chat is open, and the
   * channels the page watches. The landing used to poll for the first two
   * every twenty and two seconds, for somebody who had not verified and so
   * had no socket; that somebody is exactly who needs telling the place is
   * not empty, in the first seconds, and now they are told as everybody is.
   */
  let lastRooms = null;
  let supportEnabled = false;
  function wireLandingFrames(sig) {
    sig.on(ServerMsg.ROOMS_OPEN, (m) => {
      lastRooms = m;
      root.querySelector('#openRooms')?.__roomCounts?.apply?.(m);
    });
    sig.on(ServerMsg.LOBBY, (m) => {
      // The operator's switch, off: the pill does not exist. Not "0 people",
      // not an invitation, nothing.
      if (m.shown === false) {
        lobbyShown = false;
        showLobbyCount();
        return;
      }
      lobbyShown = true;
      lobbyCount = { here: m.here || 0, waiting: m.waiting || 0, inRooms: m.inRooms || 0 };
      showLobbyCount();
    });
    sig.on(ServerMsg.ANNOUNCEMENT, (m) => showAnnouncement(typeof m.text === 'string' ? m.text : ''));
    sig.on(ServerMsg.SUPPORT, (m) => {
      supportEnabled = !!m.enabled;
      showSupportEntry();
    });
    sig.on(ServerMsg.ACCOUNT, accountPushed);
    // This device signed out elsewhere: see signedOutHere.
    sig.on(ServerMsg.SIGNED_OUT, signedOutHere);
    // A line from Lumi Staff: on every open tab of the person, the landing's included.
    sig.on(ServerMsg.STAFF_MSG, staffWrote);
    sig.on(ServerMsg.PUB, (m) => channels.deliver(m));
  }

  /** The visitor socket: held while the page has no session socket. */
  const visitor = createVisitor({
    open: () => new Signaling(),
    offer: async () => ({
      keepsakeKey: getKeepsake()?.key || null,
      fingerprint: await getFingerprint().catch(() => ''),
      accountToken: account.token || null,
    }),
    wire: wireLandingFrames,
    onOpen: (s) => channels.bind(s),
    onClose: (s) => channels.unbind(s),
  });

  /**
   * One socket per page: the session socket when there is one, else the
   * visitor socket. Asked whenever that may have changed: a landing render, a
   * lobby join settling, a session socket opening or going.
   */
  function ensureSocket() {
    if (signaling) visitor.stop();
    else if (!lobbyJoining) visitor.want();
  }

  /** "Chat with an Admin" on the landing, while the operator has it on. */
  function showSupportEntry() {
    const had = root.querySelector('.support-entry-row');
    if (!supportEnabled) {
      had?.remove();
      return;
    }
    const legal = root.querySelector('.landing-inner .legal');
    if (!legal || had) return;
    legal.before(
      el('div', { class: 'support-entry-row' }, [
        el('button', { class: 'btn btn-ghost btn-sm support-entry', text: t('Chat with an Admin'), onClick: showSupport }),
      ]),
    );
  }

  function showRegularsLink() {
    const link = root.querySelector('#regularsLink');
    if (!link) return;
    // Somebody with no regulars YET but a chat they can still wave at needs
    // this door too -- it is the only way back to that person, and hiding it
    // was why a seven-day window may as well have been twenty seconds.
    const waveable = (regularsState.recent || []).length;
    if (landingRegularsCount > 0) {
      link.textContent = t('Regulars ({n})', { n: landingRegularsCount });
      link.title = t('Your regulars');
      link.hidden = false;
    } else if (waveable > 0) {
      link.textContent = t('Regulars');
      link.title = t('People you talked to this week, and anyone you have both waved at');
      link.hidden = false;
    } else if (hasStaffThread(regularsState.staff)) {
      // Nothing but a note from Lumi Staff, which is no regular and reaches a
      // person whatever the band's switches say. The panel opens on it.
      link.textContent = t('Messages');
      link.title = t('Messages from Lumi Staff');
      link.hidden = false;
    } else {
      link.hidden = true;
    }
    // A line from staff waiting is said on the door itself: the landing has no bell.
    link.querySelector('.regular-unread')?.remove();
    const waiting = staffUnread(regularsState.staff);
    if (waiting > 0 && !link.hidden) {
      link.setAttribute('aria-label', `${link.textContent}, ${waiting} new from Lumi Staff`);
      link.append(el('span', { class: 'regular-unread', text: waiting > 9 ? '9+' : String(waiting) }));
    } else {
      link.removeAttribute('aria-label');
    }
  }

  // A group invite link looks like https://host/r/CODE (also accept ?room=CODE and
  // #/r/CODE) so people can be linked straight to a room -- they verify their age first,
  // then auto-join that room.
  /**
   * A 1:1 invite from the URL: /i/CODE, ?chat=CODE, or #/i/CODE.
   *
   * Its own parser rather than a flavour of the room one: the codes have
   * different alphabets and confusing a room code for a person's code would
   * send somebody to the wrong place entirely.
   */
  function parseDirectInvite() {
    try {
      const m = location.pathname.match(/\/i\/([A-Za-z0-9]{8})\/?$/);
      if (m) return m[1].toUpperCase();
      const q = new URLSearchParams(location.search).get('chat');
      if (q && /^[A-Za-z0-9]{8}$/.test(q)) return q.toUpperCase();
      const h = (location.hash || '').match(/^#\/?i\/([A-Za-z0-9]{8})$/);
      if (h) return h[1].toUpperCase();
    } catch {
      /* ignore */
    }
    return null;
  }

  function parseInviteCode() {
    try {
      const m = location.pathname.match(/\/r\/([A-Za-z0-9]{4,12})\/?$/);
      if (m) return m[1].toUpperCase();
      const q = new URLSearchParams(location.search).get('room');
      if (q && /^[A-Za-z0-9]{4,12}$/.test(q)) return q.toUpperCase();
      const h = (location.hash || '').match(/^#\/?r\/([A-Za-z0-9]{4,12})$/);
      if (h) return h[1].toUpperCase();
    } catch {
      /* ignore */
    }
    return null;
  }
  joinCode = parseInviteCode();
  arrivingInvite = joinCode ? null : parseDirectInvite();

  // What the Lumi Link points at: the room's name and KIND, asked for before
  // there is a session so the landing can preselect Text for a text room
  // and never ask for a camera on the way in. Best effort and bounded: with
  // no answer the landing is the ordinary invite, which is what it was.
  let inviteRoom = null;
  async function peekRoom(code) {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), 1500);
    try {
      // The keepsake goes along: a room is described only to its own band, so
      // this is how a teenager following a link to a teen room is told what it is.
      const ks = getKeepsake()?.key;
      const res = await fetch(`${SERVER_HTTP}/api/rooms/peek?code=${encodeURIComponent(code)}`, { signal: ctl.signal, ...(ks ? { headers: { 'X-Lumi-Keepsake': ks } } : {}) });
      if (!res.ok) return null;
      const body = await res.json();
      return body && body.ok && typeof body.mode === 'string' ? { name: String(body.name || ''), mode: body.mode } : null;
    } catch {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }
  if (joinCode) {
    peekRoom(joinCode).then((room) => {
      inviteRoom = room;
      // The landing was drawn before the answer came. Draw it again with the
      // room known, unless the person has already started typing into it.
      const landing = root.querySelector('.landing');
      const typing = landing && landing.contains(document.activeElement) && /^(INPUT|SELECT|TEXTAREA)$/.test(document.activeElement?.tagName || '');
      if (room && landing && !typing) showLanding();
    });
  }

  // A community invite link looks like https://host/c/CODE (also ?community=CODE
  // and #/c/CODE). It does not join anything: the person verifies their age,
  // sees the community's name and size, and asks. See landOnCommunity.
  function parseCommunityCode() {
    try {
      const m = location.pathname.match(/\/c\/([A-Za-z0-9]{4,12})\/?$/);
      if (m) return m[1].toUpperCase();
      const q = new URLSearchParams(location.search).get('community');
      if (q && /^[A-Za-z0-9]{4,12}$/.test(q)) return q.toUpperCase();
      const h = (location.hash || '').match(/^#\/?c\/([A-Za-z0-9]{4,12})$/);
      if (h) return h[1].toUpperCase();
    } catch {
      /* ignore */
    }
    return null;
  }
  communityCode = joinCode ? null : parseCommunityCode();

  /**
   * Arrived by a /c/CODE link (or pressed Open before there was a socket):
   * ask the server where this person stands. A member or the owner walks in;
   * anyone else sees the landing with one button, and the plain words about
   * what asking does. Never a member list.
   */
  async function landOnCommunity(code) {
    communityCode = null;
    try {
      if (/\/c\//.test(location.pathname)) history.replaceState({}, '', '/');
    } catch {
      /* ignore */
    }
    if (!signaling) return;
    const r = await communityClient.byCode(code);
    if (r.status !== 200) {
      toast(r.status === 404 ? 'That community link does not work.' : 'Communities are not available for you yet.');
      return;
    }
    const info = r.data;
    if (info.you === 'member' || info.you === 'owner') {
      openCommunity(code);
      return;
    }
    openCommunityLanding({
      code,
      info,
      onAsk: () => communityClient.request(code, handle || undefined),
      onAccept: () => communityClient.accept(info.id),
      onOpen: () => openCommunity(code),
    });
  }

  function showLanding() {
    cleanup();
    mount(
      root,
      renderLanding({
        onStart: start,
        onPlus: openPlus,
        onAccount: openAccountPanel,
        onRegulars: openRegularsPanel,
        invite: joinCode ? { code: joinCode, room: inviteRoom } : communityCode ? { code: communityCode, kind: 'community' } : null,
        // Signed in -> offer their account name, which they can clear.
        accountName: account.username,
        // Asked when Start is pressed, not now: the account may arrive after the landing.
        isStaff: () => account.isStaff,
      }),
    );
    // The account pill, from live state rather than from whatever was known
    // when this render was queued. renderLanding is handed accountName, but
    // at boot the landing and the account arrive in either order: the sync
    // inside initAccount runs against a landing that may not be mounted yet,
    // and then the pill said "Sign in" to somebody who was signed in for the
    // rest of the visit. Syncing here means every path that draws the landing
    // corrects it, including the redraw when a Lumi Link resolves its room.
    syncAccountLink();
    // Somebody already verified is HERE, and the site should know it. Costs
    // no permission and no camera; see joinLobby. Somebody who is not holds
    // a visitor socket instead (ensureSocket), and what either socket has
    // already been pushed is drawn now, not on the next push.
    showLobbyCount();
    if (lastRooms) root.querySelector('#openRooms')?.__roomCounts?.apply?.(lastRooms);
    showSupportEntry();
    joinLobby().then(() => {
      showLobbyCount();
      ensureSocket();
    });
    ensureSocket();
    // "Regulars (N)" in the top bar, once the server says N > 0.
    showRegularsLink();
    fetchRegularsCount().then((n) => {
      landingRegularsCount = n;
      showRegularsLink();
    });
    // And the list itself, so "Regulars" opens on to something from here --
    // including the people you talked to this week and can still wave at.
    fetchRegularsList().then((body) => {
      if (!body) return;
      regularsState = {
        regulars: body.regulars || [],
        invisible: !!body.invisible,
        quietHours: body.quietHours || null,
        recent: body.recent || [],
        blocked: body.blocked || [],
        staff: regularsState.staff || null,
      };
      regularsPanel?.update(regularsState);
      showRegularsLink();
    });
    // Whether Lumi Staff has written: its own question, because it does not
    // depend on the list, on the band's switches, or on there being any regulars.
    peekStaffInbox();
  }

  function showSupport() {
    cleanup();
    let savedHandle = '';
    try {
      savedHandle = localStorage.getItem('lumi.handle') || '';
    } catch {
      /* fine */
    }
    mount(root, renderSupport({ onBack: showLanding, handle: savedHandle }).root);
  }

  // The selfie age check, shown full-screen. The shared verifyAge() handles capture,
  // submission, review polling, AND storing the resulting token so it is reused.
  async function runAgeVerification(birthYear, fingerprint, who = {}) {
    let cancelled = false;
    let refusals = 0; // photos the server refused (retake, liveness, image) -- not network hiccups
    // Offered the birth year after a mismatch, and left it as it was. The year
    // stands and the face will read the same, so the next attempt asks for a
    // person instead of cycling through photos that cannot change the answer.
    let yearKept = false;
    // The watch on the last refused check, and the proof it brought. An
    // operator can let the person in from that check's card, and the watch
    // used to be let go the moment Retry was pressed: the ruling then arrived
    // while the next photo was being taken, reached nobody, and the person
    // went round again (the operator, 2026-10-01). It now stays up through
    // the next attempt, and a proof that arrives ends that attempt.
    let watching = null;
    let ruled = null;
    const verify = renderVerify({
      onCancel: () => {
        cancelled = true;
        showLanding();
      },
    });
    mount(root, verify.root);
    // Loop so a not-good-enough photo (or a slow/failed check) shows a "Try again" on
    // THIS screen instead of leaving the user stuck on "Checking..." forever. The
    // finally guarantees a live-check camera/mic never outlives this screen.
    try {
    for (;;) {
      try {
         
        return await verifyAge(birthYear, fingerprint, {
          onVideo: (s) => verify.api.setVideo(s),
          // The screen as a light, only when the camera's picture is dim, and
          // only while the photos are taken (captureBurst in age.js).
          onLight: (on) => verify.api.setLight(on),
          onStatus: (statusTitle, sub) => {
            verify.api.setStatus(statusTitle, sub);
            if (statusTitle === 'Checking...' || /review queue/.test(statusTitle)) verify.api.showSpinner(true);
          },
          // Human review: a moderator can message the waiting user and read
          // their reply right on this screen -- or run a brief live camera check.
          onChatReady: (send) => verify.api.enableChat(send),
          // The check was decided while they were looking somewhere else. The
          // tab says so and the tone plays, exactly as being paired does: a
          // person who took the screen's advice and left the tab open had
          // nothing at all to bring them back (the operator, 2026-09-20).
          onDecided: () => noteDecided(),
          onMessages: (msgs) => verify.api.setChatMessages(msgs),
          onLiveCheck: (req) => verify.api.offerLiveCheck(req),
          handle: who.handle, // shown to the reviewer with the selfie
          accountToken: who.accountToken,
          // Also when a ruling on the last refused check has let them in.
          isCancelled: () => cancelled || !!ruled,
          // A human after THREE refusals, matching RETAKES_BEFORE_HUMAN on the
          // server (age-funnel.js). This used to say `>= 1`, which put every
          // second attempt in front of a person no matter what the server had
          // counted -- so the three-retake rule never actually ran.
          //
          // The server keeps its own count, so a page reload between tries
          // does not put anyone back at the start of the loop; this one is the
          // backstop for when it cannot (no fingerprint, a changed address).
          // Counted on refusals only: two network hiccups are not two bad
          // photos.
          escalate: refusals >= RETAKES_BEFORE_HUMAN || yearKept,
        });
      } catch (err) {
        // Let in by a ruling while this attempt was under way: whatever the
        // attempt came to, the proof is what counts.
        if (ruled && !cancelled) return ruled;
        if (cancelled || /cancelled/.test(String(err?.message))) throw err;
        verify.api.showSpinner(false);
        const retryable = !!err?.retry;
        // A person may still decide this check while the retry screen is up:
        // the operator setting a band on the card approves it, and whoever is
        // waiting here is let in at once rather than asked for another photo
        // (2026-09-15). The screen waits on their choice OR that, whichever
        // comes first.
        watching?.stop();
        watching = retryable && err?.checkId ? watchForApproval(err.checkId) : null;
        const approval = watching;
        approval?.promise.then((token) => {
          ruled = token;
        });
        let choice;
        try {
          choice = await Promise.race([
            verify.api.showError(
              err?.message || 'Something went wrong. Please try again.',
              {
                retry: retryable,
                // A photo that does not match the birth year is told to check the
                // year, so the year is put right there to check.
                birthYear: err?.reason === 'claim_mismatch' ? birthYear : null,
              },
            ),
            approval ? approval.promise.then((token) => ({ token })) : new Promise(() => {}),
          ]);
        } finally {
          // The watch is NOT let go here. It stays up through the next
          // attempt and is replaced by the next refusal, or stopped when
          // this screen ends (the finally at the bottom).
        }
        if (choice && typeof choice === 'object' && choice.token) return choice.token;
        if (choice === 'retry' && retryable) {
          if (err?.refusal) refusals += 1;
          const corrected = verify.api.birthYear();
          yearKept = err?.reason === 'claim_mismatch' && corrected === birthYear;
          if (corrected && corrected !== birthYear) {
            birthYear = corrected;
            // Remembered as the landing remembers it, so the next visit and
            // the session opened after this check both use the corrected year.
            try {
              localStorage.setItem('lumi.birthYear', String(corrected));
            } catch {
              /* storage unavailable: this visit still uses it */
            }
            who.onBirthYear?.(corrected);
          }
          continue;
        }
        showLanding(); // message already shown; return to the start
        throw Object.assign(new Error('cancelled'), { handled: true });
      }
    }
    } finally {
      verify.api.setLight?.(false);
      verify.api.stopLive?.();
      // The watch on the last refused check ends with the screen.
      watching?.stop();
      // Each burst gives the camera back itself. This is the backstop for a
      // flow that ends in the middle of one, and it is the one place that
      // always runs, whether the check passed, was refused, was cancelled, or
      // threw.
      releaseSelfieCamera();
    }
  }

  // Render the 1:1 chat screen. Used at the start of a 1:1 session AND when a group
  // member transitions to a private 1:1 (the "go private" feature).
  function showOneOnOneChat() {
    teardownPeer();
    roomsApi = null;
    groupApi?.dispose?.(); // let the room take its window listeners with it
    groupApi = null;
    currentRoom = null;
    const chat = renderChat({
      // Read when a message is drawn, so it is the band of THIS session.
      get externalLinks() {
        return myBand === 'adult';
      },
      onStop: showLanding,
      onSkip: () => signaling?.send(ClientMsg.SKIP),
      onGoBack: () => signaling?.send(ClientMsg.GOBACK),
      onReport: (reason, image) => {
        // Attach what is being complained about: a reported FILE sends that
        // file's own still, otherwise the reporter's current view of the peer.
        // Without either, a video report reaches the queue with nothing to see.
        signaling?.send(ClientMsg.REPORT, { reason, image: image || grabPeerFrame() });
        toast('Thanks for looking out. We are on it.');
      },
      onSend: (text, replyTo) => {
        // While staff have a text drop-in open there is no partner to relay to
        // -- being unpaired is why they are talking to you -- so what you type
        // goes to them instead of nowhere.
        if (adminChatOpen) {
          signaling?.send(ClientMsg.ADMIN_CHAT_SAY, { text });
          chatApi?.addText('me', text, { id: newMessageId() });
          return;
        }
        // The id is minted HERE and shown on the local copy too, so both sides
        // name the same message when replying, reacting, or unsending.
        const id = newMessageId();
        signaling?.send(ClientMsg.CHAT, { text, id, replyTo: replyTo || null });
        chatApi?.addText('me', text, { id, replyTo });
      },
      onReact: (id, emoji, on) => signaling?.send(ClientMsg.REACT, { id, emoji, on }),
      onUnsend: (id) => signaling?.send(ClientMsg.UNSEND, { id }),
      onTyping: (state) => signaling?.send(ClientMsg.TYPING, { state }),
      onFile: (file) => sendFile(file),
      onMicVolume: (gain) => {
        if (micGain) micGain.gain.value = gain;
      },
      canVideo: videoAllowed,
      onEnableMic: () => enableMedia({ audio: true }),
      onEnableCam: async () => {
        await enableMedia({ video: true });
        refreshTextOnlyLayout();
      },
      onCamToggle: (on) => {
        if (inOneOnOne()) {
          if (!on && (reveal.both || reveal.me)) {
            // My camera off closes both cameras, or before they opened takes
            // back my tap. The server stops showing each camera to the other;
            // with the operator's switch off it changes nothing.
            closingMine = reveal.both;
            revealWanted = false;
            stopPersonCheck();
            signaling?.send(ClientMsg.REVEAL, { off: true });
          } else if (on && !reveal.both) {
            requestReveal(); // turning it back on is saying yes again
          }
        }
        refreshTextOnlyLayout();
      },
      onReveal: () => requestReveal(), // "Show my camera" (camera-off start)
      // Agree to explicit content with this person, or stop it. The same button
      // either way: stopping must never be harder than starting, and must be
      // one tap at any moment.
      onExplicit: () => {
        explicitAgreed = !explicitAgreed;
        signaling?.send(ClientMsg.EXPLICIT, { on: explicitAgreed });
      },
      onExplicitLane: (on) => setExplicitPref(on),
      onLanguage: chooseLanguage, // "Translate to" in the header
      // The close buttons on the waiting screen's two notices.
      onCloseHint: () => {
        rememberClosed(CLOSED_KEY.hint);
      },
      onCloseInvite: () => {
        rememberClosed(CLOSED_KEY.invite);
        chatApi?.setInviteLink?.(null);
      },
      onEffect,
      onVideoPin: (url) => signaling?.send(ClientMsg.VIDEO_PIN, { url }),
      onVideoControl: (action, atMs) => signaling?.send(ClientMsg.VIDEO_CONTROL, { action, atMs }),
      canModerate: isModerator,
      onKick: (reason, note) => signaling?.send(ClientMsg.KICK, { reason, note }),
    });
    chatApi = chat.api;
    pushLanguageControl(); // WELCOME may already have said which languages
    ensureBell();
    // Show yourself what your partner sees on you.
    chatApi.setSelfBadges({ staff: isStaff, moderator: isModerator, plus: isPlusMember });
    mount(root, chat.root);
    chatApi.setLocalStream(localStream);
    refreshTextOnlyLayout();
    refreshSelfView(); // show any saved blur/filter in the user's own preview too
    chatApi.initEffects(effectPrefs);
    refreshSelfView(); // if an effect was already on, show the processed video immediately
    // The explicit lane, if this session may have it at all.
    //
    // This used to be drawn only on MATCHED, so the control that decides WHO
    // YOU MEET NEXT first appeared after you had already met somebody -- and
    // never at all for a person sitting in the queue, which is exactly where
    // you would go looking for it. Drawn with the screen now; setExplicitLane
    // removes its own row when the session is not eligible, so a minor and a
    // site with the switch off still see nothing.
    chatApi.setExplicitLane?.({ available: explicitAvailable, on: explicitPref });
  }

  async function start(opts) {
    try {
      return await startInner(opts);
    } catch (err) {
      if (err?.handled || /cancelled/.test(String(err?.message || ''))) throw err;
      // A failure AFTER the landing was replaced (e.g. the verify screen took
      // over during an age check) used to write its message onto the detached
      // old landing -- invisible, so the user saw a frozen "review queue".
      // Remount the landing and show the error where the user actually is.
      showLanding();
      const line = root.querySelector('.gate .error');
      if (line) line.textContent = err?.message || 'Something went wrong. Please try again.';
      throw Object.assign(err instanceof Error ? err : new Error(String(err)), { handled: true });
    }
  }

  async function startInner({
    birthYear,
    mode,
    intent: intentArg,
    handle: handleArg,
    interest: interestArg,
    cameraStart: cameraStartArg,
    timed: timedArg,
  }) {
    intent = joinCode || communityCode || intentArg === 'group' ? 'group' : '1on1';
    chosenMode = mode || chosenMode;
    handle = handleArg || null;
    interest = interestArg || null;
    cameraStart = cameraStartArg === 'open' ? 'open' : 'agree';
    timedPref = timedArg === true;
    // The last certain user gesture before somebody sits waiting with the tab
    // in the background. A browser will not start an AudioContext without one,
    // and every tone we might need is played long after this point.
    primeAudio();
    const fingerprint = await getFingerprint();

    // 1. Get a session, running the self-hosted age check if the server asks for it.
    // Reuse a stored age proof (this device, within the token's two-hour life)
    // so the user is not re-prompted; only run the selfie check when there is
    // no valid proof yet.
    let session = await obtainSession(birthYear, fingerprint, getStoredAge()?.token, account.token);
    if (session.status === 'verification_required') {
      const ageToken = await runAgeVerification(birthYear, fingerprint, {
        handle: handleArg || handle || undefined,
        accountToken: account.token || undefined,
        // A year corrected on the retry screen is the year from here on.
        onBirthYear: (y) => {
          birthYear = y;
        },
      });
      session = await obtainSession(birthYear, fingerprint, ageToken, account.token);
    }
    if (session.status !== 'ok') throw new Error('Verification did not complete. Please try again.');
    const { token, policy } = session;
    myBand = session.band === 'minor' ? 'minor' : session.band === 'adult' ? 'adult' : null;

    // Apply the minor-pool policy: minors do not get unmoderated live video.
    let effectiveMode = mode;
    if (mode === ChatMode.VIDEO && policy && policy.videoAllowed === false) {
      effectiveMode = ChatMode.AUDIO;
      toast('Video chat for under-18 is moderated and coming soon: connecting by audio.');
    }
    // Whether this user may later turn the camera on mid-chat (gated by policy).
    videoAllowed = !(policy && policy.videoAllowed === false);
    // Everything a reconnect needs that is not already module state. The age
    // proof and the account token are read from storage at the time, so they
    // are deliberately NOT captured here: a proof that expired in the meantime
    // must send the person back through the check, not be replayed.
    resumeSeed = { birthYear, effectiveMode };

    // 2. Capture local media for the chosen mode. Coming straight from an age
    // check, the camera was released only milliseconds ago (selfie burst or a
    // live check) and phones often have not freed the hardware yet -- so a
    // busy-camera failure retries with a pause instead of giving up.
    const acquireMedia = async (constraints) => {
      let lastErr;
      for (let attempt = 0; attempt < 4; attempt += 1) {
        try {
           
          return await navigator.mediaDevices.getUserMedia(constraints);
        } catch (err) {
          lastErr = err;
          if (err && (err.name === 'NotReadableError' || err.name === 'AbortError')) {
             
            await new Promise((r) => setTimeout(r, 600));
            continue;
          }
          throw err;
        }
      }
      throw lastErr;
    };
    try {
      if (effectiveMode === ChatMode.VIDEO) {
        // facingMode 'user': on phones a bare { video: true } can open the
        // REAR camera. Soft preference here (desktop webcams have no facing).
        try {
          localStream = await acquireMedia({ video: { facingMode: 'user' }, audio: true });
        } catch {
          // A blocked mic must not kill a VIDEO chat: fall back to camera-only
          // (the user can still type) instead of failing the combined request.
          localStream = await acquireMedia({ video: { facingMode: 'user' } });
          toast('Your microphone is blocked, so you are connecting without sound: you can still type.');
        }
      } else if (effectiveMode === ChatMode.AUDIO) {
        localStream = await acquireMedia({ audio: true });
      } else {
        localStream = null;
      }
    } catch (err) {
      // Name exactly what is missing for the mode we actually connect in (a
      // minor's video choice downgrades to audio, which needs only the mic),
      // and give a way OUT: browsers never re-prompt once access is blocked,
      // so without guidance the user is stuck in a retry loop.
      const what = effectiveMode === ChatMode.VIDEO ? 'camera and microphone' : 'microphone';
      if (err && err.name === 'NotAllowedError') {
        throw new Error(
          `Your ${what} is blocked for this site. Tap the lock icon next to the address bar, allow the ${what}, and try again: or switch to Text chat.`,
        );
      }
      throw new Error(
        `Lumi could not start your ${what} (${(err && err.name) || 'unknown error'}). Close other apps that may be using it and try again, or switch to Text chat.`,
      );
    }

    // Route the mic through a gain node so its volume is adjustable, and send THAT
    // (processed) audio to the peer. The raw localStream stays the self-view.
    sendStream = localStream;
    audioCtx = null;
    micGain = null;
    audioEnhancer = null;
    videoEnhancer = null;
    rawCamTrack = (localStream && localStream.getVideoTracks()[0]) || null;
    if (localStream && localStream.getAudioTracks().length > 0) {
      try {
        audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const src = audioCtx.createMediaStreamSource(new MediaStream(localStream.getAudioTracks()));
        micGain = audioCtx.createGain();
        const dest = audioCtx.createMediaStreamDestination();
        // Noise suppression (#9) -- bypassed unless the user has it on.
        audioEnhancer = createAudioEnhancer(audioCtx, src);
        audioEnhancer.setEnabled(!!effectPrefs.denoise);
        audioEnhancer.output.connect(micGain).connect(dest);
        audioCtx.resume?.();
        // A context created behind the camera-permission prompt starts SUSPENDED (the
        // original click is stale by the time getUserMedia resolves), which silences the
        // sent audio and leaves the mic meter + mic-volume gain dead. Resume it on the
        // user's next interaction until it is actually running.
        armAudioContextResume();
        sendStream = new MediaStream([
          ...localStream.getVideoTracks(),
          ...dest.stream.getAudioTracks(),
        ]);
      } catch {
        sendStream = localStream; // Web Audio unavailable -- send the raw mic
      }
    }
    // Background blur (#8) / filters (#11): route the SENT video through the canvas
    // processor when an effect is on (the self-view stays the raw camera).
    if (rawCamTrack && videoFxOn()) {
      if (effectPrefs.blur) ensureMlSegmenter(); // saved pref -> start loading now
      try {
        videoEnhancer = createVideoEnhancer(rawCamTrack, { segmenter: bgSegmenter });
        videoEnhancer.setBlur(!!effectPrefs.blur);
        videoEnhancer.setFilter(effectPrefs.filter);
        await videoEnhancer.start();
        sendStream = new MediaStream([videoEnhancer.track, ...sendStream.getAudioTracks()]);
      } catch {
        videoEnhancer = null;
      }
    }
    // Saved prefs (blur/filter from a previous session) applied only to what the
    // PEER saw; the self-view still showed the raw camera, so the effect looked
    // dead. refreshSelfView() below runs once the chat screen exists.

    // 3. Render the 1:1 chat screen now; group rooms get their own UI after WELCOME.
    if (intent !== 'group') {
      showOneOnOneChat();
      chatApi.setStatus('Connecting...', 'Getting you ready to chat.');
    }

    // 4. Connect signaling -- unless we are already connected.
    //
    // The landing opens a socket as soon as it has a valid age proof, so the
    // server knows somebody is here before they have started anything (that is
    // the lobby). Pressing start then uses THAT socket: opening a second one
    // would leave the first counted as a person who is not there.
    //
    // A lobby socket still being opened is that socket: wait for it, or the
    // join finishing afterwards replaces the one opened here.
    if (lobbyJoining) await lobbyJoining;
    if (!signaling) {
      signaling = new Signaling();
      wireSignaling();
      try {
        await signaling.connect(token);
        socketAccountToken = account.token || null;
        socketKeepsakeId = getKeepsake()?.id || null;
      } catch {
        signaling = null;
        showLanding();
        throw new Error('Could not connect to Lumi. Please try again.');
      }
      channels.bind(signaling);
      ensureSocket();
    }
    // The language to read messages and captions in: what this person chose
    // last time, else the browser's. They can change it in the chat header.
    chatLang = preferredLanguage({ uiLang: currentLanguage() });
    const lang = chatLang;
    helloSent = true;
    signaling.send(ClientMsg.HELLO, {
      mode: effectiveMode,
      handle,
      lang,
      interest,
      timed: timedPref,
      explicit: explicitPref,
      // Used ONCE, at the moment of joining: the server either pairs us with
      // the person whose link this is, or says why not.
      inviteCode: arrivingInvite || undefined,
    });
  }

  /**
   * Open a fresh socket for a session already under way, and pick up where the
   * server left off.
   *
   * The reason this is not simply `signaling.connect(token)` again: a session
   * token lives two minutes. After a ten-minute conversation the page is
   * holding an expired one, and the server would answer 401. So a reconnect
   * mints a NEW token from the same things the first one was minted from --
   * the stored age proof, the account token, the device fingerprint -- none of
   * which asks the person for anything. If the age proof has expired in the
   * meantime, obtainSession says verification is required, and that is a
   * question only the landing can ask: we give up and go there rather than
   * launching a camera at somebody who was in the middle of a chat.
   *
   * Backoff is jittered because the common cause is the SERVER going away --
   * a deploy, or the nightly security upgrade restarting everything -- which
   * means every client on the site is doing this at the same moment. In step
   * they would arrive as one burst and be turned away by the per-address
   * socket cap; spread out, they trickle back.
   */
  const RECONNECT_DELAYS_MS = [400, 1200, 2500, 5000, 9000];
  async function reconnect() {
    if (reconnecting || !signaling || !resumeSeed) return;
    reconnecting = true;
    try {
      for (let attempt = 0; attempt < RECONNECT_DELAYS_MS.length; attempt += 1) {
        const base = RECONNECT_DELAYS_MS[attempt];
        await new Promise((r) => setTimeout(r, base + Math.random() * base));
        if (!signaling || !reconnecting) {
          // The person left, or something else took over, while we waited.
          reconnecting = false;
          return;
        }
        try {
          const fingerprint = await getFingerprint();
          const session = await obtainSession(
            resumeSeed.birthYear,
            fingerprint,
            getStoredAge()?.token,
            account.token,
          );
          if (session.status !== 'ok') break; // needs the age check again: the landing's job
          await signaling.connect(session.token);
          socketAccountToken = account.token || null;
          socketKeepsakeId = getKeepsake()?.id || null;
          channels.bind(signaling);
          chatLang = preferredLanguage({ uiLang: currentLanguage() });
          const lang = chatLang;
          // The same greeting the first connection sent. WELCOME then puts the
          // person back where they belong on its own: into the queue for a 1:1,
          // or on the rooms list for somebody who was in a group.
          helloSent = true;
          signaling.send(ClientMsg.HELLO, {
            mode: resumeSeed.effectiveMode,
            handle,
            lang,
            interest,
            timed: timedPref,
            explicit: explicitPref,
          });
          reconnecting = false;
          return;
        } catch {
          // The socket may have refused the TOKEN: a band set on this person
          // since it was minted is refused at the door. A fresh one next time
          // runs the whole check again, rather than replaying the refused one.
          dropCachedSession();
        }
      }
    } finally {
      if (reconnecting) {
        // Out of attempts. This is the old behaviour, and the right one: the
        // page cannot do anything useful without a server.
        reconnecting = false;
        toast('Lost connection to Lumi. Please start again.');
        showLanding();
      }
    }
  }

  function wireSignaling() {
    signaling.on(ServerMsg.WELCOME, (m) => {
      selfId = m.selfId;
      selfBand = m.band || 'adult'; // decides whether "open from the start" is honored
      rememberBand(selfBand); // so the next landing hides what a minor is never offered
      // Adults only, and only while the operator's switch is on. A minor's
      // WELCOME never carries this, so their client cannot show the option.
      explicitAvailable = !!m.explicit;
      if (!explicitAvailable) explicitPref = false;
      // WELCOME is what SAYS whether this session may have the lane, and it
      // usually lands after the chat screen is already up. Draw the control
      // the moment we know, rather than waiting for a first match -- which is
      // what hid it from everybody sitting in the queue.
      chatApi?.setExplicitLane?.({ available: explicitAvailable, on: explicitPref });
      shareKey = m.shareKey || null; // capability for uploading/fetching files
      sfuEnabled = !!m.sfu;
      maxVideoBitrate = m.maxVideoBitrate || 0; // Plus -> higher ceiling
      isModerator = !!m.moderator; // admin-granted: can kick peers for 15 min
      // Your OWN badges. Everyone else could see these on you; you could not,
      // so there was no way to confirm your staff badge was actually live.
      isStaff = !!m.staff;
      isPlusMember = !!m.plus;
      invitesAllowed = !!m.invites; // may this band hand out a 1:1 invite link
      moderateImages = !!m.moderateImages; // sample camera frames for image moderation
      asrEnabled = !!m.asr; // sample audio for transcription (moderation + captions)
      iceConfig = { iceServers: m.iceServers, iceTransportPolicy: m.iceTransportPolicy };
      // Whether the wave exists for this band (decision H1: adults first). The
      // list is asked for once here so the bell has something to open on.
      social = !!m.social;
      callsOn = social && !!m.calls; // and whether a regular can be called from here
      messagesOn = !!m.messages;
      // The languages messages can be read in. The chat screen is usually up
      // before this lands, so the control is filled in now, not at a match.
      translateLangs = Array.isArray(m.translateLanguages) ? m.translateLanguages : [];
      pushLanguageControl();
      if (social) signaling.send(ClientMsg.REGULARS_LIST);
      // Lumi Staff's thread does not wait on `social`: ask whatever the band says.
      peekStaffInbox();
      // Now that we know, give the screen already on its bell.
      ensureBell();
      if (intent === 'group') {
        // Invited via a /r/CODE link -> join that room straight away; a /c/CODE
        // link -> the hub, with the community's landing over it; otherwise the hub.
        if (joinCode) {
          signaling.send(ClientMsg.JOIN_ROOM, { code: joinCode });
          armJoinWatchdog();
        } else if (communityCode) {
          const code = communityCode;
          enterRoomsHub();
          landOnCommunity(code);
        } else enterRoomsHub();
      } else {
        signaling.send(ClientMsg.READY);
        // The server says whether this band may hand one out; asking when
        // it may not simply comes back null, but not asking is tidier.
        if (invitesAllowed) signaling.send(ClientMsg.INVITE_MINE);
      }
    });

    // ---- Group rooms ----
    // The room's shared video, in full on every change.
    // Stable so the notice can be taken back when the video is unpinned.
    const VIDEO_NOTICE_ID = 'room-video-pinned';
    // The room's shared video. The FIRST one after joining is announced in the
    // chat as well as shown in the panel: a room is ephemeral, so a newcomer
    // sees none of the conversation that led to it and would otherwise have to
    // work out for themselves why a video appeared.
    signaling.on(ServerMsg.ROOM_VIDEO, (m) => {
      const first = !sawVideoState;
      sawVideoState = true;
      // The same message serves a group room and a 1:1 -- whichever the
      // person is actually in.
      if (groupApi) groupApi.setVideo(m);
      else chatApi?.setVideo?.(m);
      if (!m.videoId) {
        // The notice describes a state, not an event: "this room has a video
        // pinned" stops being true the moment it is unpinned, and leaving it
        // there tells people something false about the room they are in.
        groupApi?.removeSystem?.(VIDEO_NOTICE_ID);
        sawVideoState = false; // so the next video announces itself again
        return;
      }
      if (first) {
        // A SoundCloud track is not a video, and calling it one reads as a
        // mistake to the person looking straight at a waveform.
        const audio = videoProvider(m.provider)?.kind === 'audio';
        groupApi?.addSystem(
          m.playing
            ? (audio
              ? 'Listening together. It is already playing, and you are in sync with the room.'
              : 'Watching together. It is already playing, and you are in sync with the room.')
            : (audio
              ? 'Something is pinned to listen to together.'
              : 'A video is pinned to watch together.'),
          { id: VIDEO_NOTICE_ID },
        );
      }
    });
    // A card for a link somebody sent. Arrives after the message, since
    // fetching it takes a moment.
    signaling.on(ServerMsg.LINK_PREVIEW, (m) => {
      if (groupApi) groupApi.linkPreview(m.id, m);
      else chatApi?.linkPreview(m.id, m);
    });
    // Ownership moved -- the owner left and it passed to whoever has been here
    // longest. Without this the controls would stay with somebody who is gone.
    signaling.on(ServerMsg.ROOM_OWNER, (m) => {
      if (currentRoom) currentRoom.ownerId = m.ownerId;
      groupApi?.setVideoOwner(m.ownerId === selfId, m.ownerId, m.ownerId === selfId || isStaff);
      if (m.ownerId === selfId) groupApi?.addSystem('You are now the room host.');
    });
    // The watch-together queue (D7), in full on every change and on join. The
    // screen needs to know which entries are mine and whether the turn is mine.
    signaling.on(ServerMsg.ROOM_VIDEO_QUEUE, (m) => groupApi?.setVideoQueue?.(m, selfId));
    // Activities (D0): the state in full, and relayed events, both handed to
    // the frame by the group screen. slug null means none is running.
    signaling.on(ServerMsg.ACTIVITY_STATE, (m) => groupApi?.setActivity?.(m));
    signaling.on(ServerMsg.ACTIVITY_EVENT, (m) => groupApi?.activityEvent?.(m));

    signaling.on(ServerMsg.ROOM_LIST, (m) => {
      // The staff listing (every band, private rooms included) is a different
      // list from the public directory and must not overwrite it.
      if (m.all) roomsApi?.setAllRooms(m.rooms || []);
      else roomsApi?.setRooms(m.rooms || []);
    });
    signaling.on(ServerMsg.ROOM_JOINED, (m) => {
      clearJoinWatchdog(); // the room opened, which is the other way this ends
      enterGroupRoom(m);
    });
    signaling.on(ServerMsg.ROOM_PEER_LEFT, (m) => {
      roomHandles.delete(m.peerId);
      mutedPeers.delete(m.peerId);
      sfuPeer?.removePeer(m.peerId);
      // Drop their tile too. Without this the member outlives their departure:
      // somebody who left and rejoined appeared TWICE, and the "N here" count
      // kept counting the ghost.
      groupApi?.removePeer(m.peerId);
    });
    signaling.on(ServerMsg.ROOM_VIDEO_SLOTS, (m) => {
      videoSlots = m;
      groupApi?.setVideoSlots(slotsForUi(m));
      // Waiting for a camera slot and one just opened: take it. The camera is
      // on; this is what the person was waiting for.
      if (videoWaiting && m.used < m.max) takeVideoSlot();
    });
    signaling.on(ServerMsg.ROOM_PEER_JOINED, (m) => {
      if (m.handle) roomHandles.set(m.peerId, m.handle);
      if (m.peerId && m.peerId !== selfId) {
        groupApi?.ensurePeer(m.peerId, m.handle);
        if (m.moderator) groupApi?.setModerator(m.peerId, true);
        if (m.staff) groupApi?.setStaff(m.peerId, true); // verified Lumi staff
        if (m.plus) groupApi?.setPlus(m.peerId, true); // subtle Plus mark
        // Everyone arrives quietly listening; the count itself comes in
        // ROOM_OBSERVERS, this only marks the person in the roster.
        if (m.observing) groupApi?.setPeerObserving?.(m.peerId, true);
        if (m.status) groupApi?.setPeerStatus?.(m.peerId, m.status);
        // Someone arrived. The server sends this only to people ALREADY in the
        // room, so a joiner never hears their own entry, and never one tone per
        // member they walked in on -- that list arrives as ROOM_JOINED instead.
        playJoinTone();
      }
    });
    signaling.on(ServerMsg.ROOM_CHAT, (m) => {
      // Speaking ends listening. The server's count arrives separately; this
      // only clears the roster mark on the person who spoke.
      groupApi?.setPeerObserving?.(m.from, false);
      if (mutedPeers.has(m.from)) return; // muted: drop their texts
      // peerId travels with the message so the sender's badges can be drawn
      // beside their name -- in a text room that is the only place identity
      // is visible at all.
      groupApi?.addMessage(m.handle || String(m.from).slice(0, 4), m.text, {
        id: m.id,
        replyTo: m.replyTo,
        peerId: m.from,
        // The line in this person's language, when the server made one.
        translated: m.translated,
        lang: m.lang,
        // Badges now travel WITH the message. They used to be looked up from
        // the join announcement, which an invisible staff member never sends --
        // so their messages arrived unbadged. Carrying them here is sturdier
        // anyway: a missed join no longer means an unbadged staff member.
        flags: { staff: m.staff, moderator: m.moderator, plus: m.plus },
      });
      // A room carries on talking whether or not you are looking at it, and the
      // line you missed may be an answer to something you asked.
      noteMessage();
    });
    signaling.on(ServerMsg.ROOM_ERROR, (m) => {
      clearJoinWatchdog(); // an answer arrived, which is all the watchdog wanted
      // A community's code, tried by somebody who is not in it: the landing,
      // where they can ask, rather than a dead end.
      if (m.error === 'members_only' && m.code) {
        joinCode = null;
        if (!currentRoom && !roomsApi) enterRoomsHub();
        landOnCommunity(m.code);
        return;
      }
      toast(roomErrorText(m.error));
      // Ejected from a community room (removed, or the community closed), or
      // staff closed the room outright: the room is gone from under us, so
      // back to the hub. Without this last one a person turned out of a closed
      // room sat looking at a room that no longer exists, able to type into it.
      if (
        (m.error === 'removed_from_community' || m.error === 'community_closed' || m.error === 'room_closed') &&
        currentRoom
      ) {
        enterRoomsHub();
        return;
      }
      // A bad/expired invite link should not strand the user -- drop into the hub.
      if (joinCode && !currentRoom) {
        joinCode = null;
        enterRoomsHub();
      }
    });
    // The community a room belongs to, in full, on join and on every change.
    signaling.on(ServerMsg.COMMUNITY_STATE, (m) => {
      communityState = m;
      if (currentRoom && currentRoom.community === m.id) {
        currentRoom.name = m.name;
        groupApi?.setRoomName?.(m.name);
        communityPanel?.update(m);
      }
      communityCard?.api.refresh();
    });

    // ---- Going private (from a group room) ----
    signaling.on(ServerMsg.PRIVATE_REQUEST, (m) => {
      // A muted person cannot reach me with a 1:1 request -- auto-decline silently.
      if (mutedPeers.has(m.fromId)) {
        signaling?.send(ClientMsg.GO_PRIVATE_RESPOND, { fromId: m.fromId, accept: false });
        return;
      }
      groupApi?.askPrivate?.(m.fromHandle || 'Someone', m.fromId);
    });
    /**
     * "A room would like you to join them for a few minutes."
     *
     * Deliberately a plain question with a plain no. Nobody is told who
     * declined, declining costs nothing and does not move you in the queue, and
     * it answers itself after thirty seconds so an unanswered invitation cannot
     * sit around waiting to be pressed by accident later.
     */
    const askStageOffer = (m) => {
      document.querySelector('.stage-offer')?.remove();
      let answered = false;
      const answer = (accept) => {
        if (answered) return;
        answered = true;
        clearTimeout(timer);
        box.remove();
        signaling?.send(ClientMsg.STAGE_RESPOND, { accept });
      };
      const people = Number(m.people) || 0;
      const box = el('div', { class: 'stage-offer' }, [
        el('p', {
          class: 'stage-offer-title',
          text: `${m.roomName || 'A room'} would like you to join them`,
        }),
        el('p', {
          class: 'stage-offer-body',
          text: `${people} ${people === 1 ? 'person is' : 'people are'} in there. `
            + `You would be a guest for ${Math.round((Number(m.seconds) || 600) / 60)} minutes, `
            + 'and you can leave at any point.',
        }),
        el('div', { class: 'stage-offer-actions' }, [
          el('button', { class: 'btn btn-primary btn-sm', text: t('Join them'), onClick: () => answer(true) }),
          el('button', { class: 'btn btn-ghost btn-sm', text: t('No thanks'), onClick: () => answer(false) }),
        ]),
      ]);
      document.body.append(box);
      const timer = setTimeout(() => answer(false), 30_000);
    };

    // Somebody's room would like to invite you in. This has to work from
    // wherever you are -- you are in the waiting queue, not in a room -- so
    // it is its own small prompt rather than anything a room owns.
    signaling.on(ServerMsg.STAGE_OFFER, (m) => askStageOffer(m));
    signaling.on(ServerMsg.STAGE_STATE, (m) =>
      groupApi?.setStage?.({ ...m, isMe: !!m.guestId && m.guestId === selfId }));
    signaling.on(ServerMsg.STAGE_ENDED, () => {
      toast('Your ten minutes are up. Thanks for dropping in.');
    });
    signaling.on(ServerMsg.PRIVATE_DECLINED, (m) => toast(`${m.byHandle || 'They'} declined to go private.`));
    // The room is closing in a minute, not this instant. See closeRoom.
    signaling.on(ServerMsg.ROOM_CLOSING, (m) => groupApi?.showClosing?.(m));
    signaling.on(ServerMsg.ROOM_VOTE_STARTED, (m) => groupApi?.showVote?.(m));
    signaling.on(ServerMsg.ROOM_VOTE_UPDATE, (m) => groupApi?.updateVote?.(m));
    signaling.on(ServerMsg.ROOM_VOTE_RESULT, (m) => {
      groupApi?.endVote?.(m);
      toast(m.passed ? 'The room is now private: no new random members.' : 'The vote did not pass.');
    });

    // ---- Pinned rules, the quiet room, and observer mode ----
    // The pinned card, re-sent on the hour when the question rotates.
    signaling.on(ServerMsg.ROOM_PIN, (m) => groupApi?.setPin?.(m));
    // How many are here and have not yet spoken. The server counts; the
    // client only draws.
    signaling.on(ServerMsg.ROOM_OBSERVERS, (m) => groupApi?.setWatching?.(m.watching));
    // The quiet room's shared timer, in full on every change.
    signaling.on(ServerMsg.ROOM_FOCUS, (m) => groupApi?.setFocus?.(m));
    // A member's one-line status. Yours comes back too, so the field shows
    // what actually got through the moderator.
    signaling.on(ServerMsg.ROOM_PEER_STATUS, (m) =>
      groupApi?.setPeerStatus?.(m.peerId === selfId ? 'self' : m.peerId, m.text));

    signaling.on(ServerMsg.SEARCHING, (m) => {
      teardownPeer();
      pairedNow = false;
      forgetChat();
      afterCall = false;
      resetPairUi(); // nothing of the last pair -- clock, reveal, card -- stays on screen
      chatApi?.setStatus('Finding someone new...', 'Hang tight.');
      // Nobody else is in this band's queue, so no match is on its way and
      // there is nothing to be gained by holding the advice back: the person
      // is told at once how this works, and offered their link after it.
      armQuietHint(m?.alone === true);
      armInviteOffer(m?.alone === true);
    });

    signaling.on(ServerMsg.MATCHED, (m) => {
      // Our own chat handed back after a dropped socket. The CHAT is kept --
      // its lines, its name, its greeting -- whatever the media did; the
      // MEDIA is kept only while the SFU peer is still alive, and one the
      // grace already tore down is rebuilt like a new match. Both are read
      // against the LAST pair, before it is overwritten below.
      const keepChat = !!m.resumed && !!m.pairId && m.pairId === lastPairId;
      const keepMedia = keepChat && sfuEnabled && !!sfuPeer && !sfuPeer.closed;
      clearTimeout(mediaGrace);
      mediaGrace = null;
      // Coming from a group room (went private)? Render the 1:1 chat first.
      if (!chatApi) showOneOnOneChat();
      // A match is a pair, call or not: an offer sheet still up is answered
      // no (the server would refuse the accept anyway), and a "Calling..."
      // sheet that is not this call is taken down.
      pairedNow = true;
      // Kept across a reload while the server holds the pair (chat-seed.js).
      if (resumeSeed && !groupApi) rememberChat({ birthYear: resumeSeed.birthYear, mode: resumeSeed.effectiveMode });
      afterCall = false;
      if (offerSheet) {
        const id = offerSheet.callId;
        offerSheet.close();
        respondToCall(id, false);
      }
      if (callingSheet && !m.call) {
        const id = callingSheet.callId;
        callingSheet.close();
        if (id) signaling?.send(ClientMsg.CALL_CANCEL, { callId: id });
      }
      inCall = !!m.call;
      // The pair a wave will be for. The control from the LAST pair stays:
      // it belongs to that conversation, it runs on its own timer, and the
      // next match arriving is not a reason to withdraw it -- on a quiet night
      // that match lands in a few seconds and used to take the offer with it.
      lastPairId = m.pairId || null;
      if (!keepChat) chatApi?.clearMessages(); // a chat handed back keeps what was said in it
      chatApi?.clearGoBack();
      chatApi?.setSkipEnabled(false);
      // The greeting is a NOTICE, not a state: it says who you got, and then it
      // gets out of the way. It used to be dismissed only by a remote stream
      // arriving or the peer connection reaching 'connected' -- and a pair with
      // no cameras between them produces neither, so "Connecting you..." sat
      // over a working conversation for its entire length. Text rides the
      // signalling socket, which is already up: the moment you are matched you
      // can talk, and saying so is the honest thing to put there. Video is the
      // only thing with anything left to connect, and the reveal placeholder
      // explains that better than an overlay can.
      cancelQuietHint();
      cancelInviteOffer();
      // The whole point of telling somebody to leave the tab open: when the
      // wait ends, the tab has to say so. Sounds and badges the title only
      // when they are actually looking elsewhere -- if they are watching the
      // screen, the chat appearing IS the notification.
      noteMatched();
      const videoComing = !!m.reveal?.both || hasLocalVideo();
      const connectingLine = videoComing ? 'Connecting you...' : 'You are connected. Say hello.';
      // A chat handed back is not a new one: "Say hi" over a conversation
      // twenty lines deep said the chat had dropped when it had not.
      if (keepChat) chatApi?.setStatus('Reconnected', `Back with ${m.peerHandle || 'them'}.`, GREETING_MS);
      else if (inCall) chatApi?.setStatus(`On a call with ${m.peerHandle || 'your regular'}`, connectingLine, GREETING_MS);
      else chatApi?.setStatus(m.peerHandle ? `Say hi to ${m.peerHandle}!` : 'Say hi!', connectingLine, GREETING_MS);
      // The watermark names WHOEVER IS WATCHING -- you -- not the person on
      // screen. It is drawn on your screen over their video, so a recording
      // made here carries your name: that is what makes a leaked clip traceable
      // and is why it deters. Naming them instead labelled the victim in the
      // recording and identified the recorder not at all. Nothing can stop a
      // screen recording (/safety says so); this makes one answerable for.
      chatApi?.setWatermark(`Lumi - ${m.selfHandle || handle || selfId.slice(0, 6)}`);
      chatApi?.setPeerName(m.peerHandle || 'a stranger'); // visible for the whole chat
      chatApi?.setPartnerModerator(!!m.peerModerator); // unfakeable verified-mod badge
      chatApi?.setPartnerStaff(!!m.peerStaff); // verified Lumi staff -- conspicuous
      chatApi?.setPartnerPlus(!!m.peerPlus); // the subtle Plus mark, quiet by design
      // A new chat is not a regular's until the server says so, which it does
      // in its own message a moment later (PAIR_REGULARS).
      chatApi?.setPartnerRegular?.(regularPairId !== null && regularPairId === m.pairId);
      // The pin box is only offered to a pair that may watch together: adults,
      // and teens when the operator's switch for 13-17 is on. The server says
      // which; a server that does not say is one where only adults may.
      chatApi?.setVideoAllowed?.(m.watchTogether ?? m.band === 'adult');
      // Lower-pressure starts. MATCHED says where the cameras stand from the
      // first moment: closed until both tap (the operator's switch on), or
      // open from the start (the switch off) -- without that, a pair the
      // server had already opened sat behind a "Show my camera" placeholder
      // nothing could dismiss. The header says what they would rather speak
      // and, for a ten-minute chat, how long is left. A remembered "open from
      // the start" sends our half of the reveal now -- for adults only,
      // whatever the browser remembers, because a minor never has that option.
      reveal = m.reveal
        ? { me: !!m.reveal.me, them: !!m.reveal.them, both: !!m.reveal.both }
        : { me: false, them: false, both: false };
      chatApi?.setPeerLang?.(m.peerLang || null);
      chatApi?.setPeerInterest?.(m.peerInterest || null);
      chatApi?.setTimed?.(m.timed ? { endsAt: m.timed, warnAt: null } : null);
      // Explicit consent is per pair and starts at nothing, whatever the last
      // pair had agreed to. The control is absent unless BOTH chose the lane.
      chatApi?.setExplicit?.(m.explicitAvailable ? { available: true, me: false, them: false, both: false } : null);
      chatApi?.setExplicitLane?.({ available: explicitAvailable, on: explicitPref });
      pushRevealUi();
      refreshTextOnlyLayout(); // a pair born with cameras open takes the video layout at once
      // A call: "Call with Bea" in the header, and Skip reads Hang up. Applied
      // after setPeerName so it is the last word on the title.
      setCallChrome(root, inCall ? m.peerHandle || 'your regular' : null);
      if (cameraStart === 'open' && selfBand !== 'minor' && hasLocalVideo()) requestReveal();
      if (keepMedia) {
        // The transports never went. Only the samplers -- which need the
        // socket, or wait for it to start -- are told the chat is on again;
        // both are no-ops when they are already running.
        if (!inOneOnOne() || reveal.both) startModSampler();
        startAudioSampler();
        chatApi?.hideStatus();
      } else if (sfuEnabled) setupSfuPeer();
      else setupPeer(m.role === 'caller');
    });

    signaling.on(ServerMsg.SIGNAL, (m) => handleSignal(m));
    signaling.on(ServerMsg.SFU, (m) => handleSfuMessage(m));
    signaling.on(ServerMsg.CHAT, (m) => {
      // `translated` is the line in this person's language, when the server
      // made one; the text itself is what was typed.
      chatApi?.addText('them', m.text, { id: m.id, replyTo: m.replyTo, translated: m.translated, lang: m.lang });
      // Somebody answered while this tab was behind something else. The title
      // counts it and a short tone plays; coming back clears both.
      noteMessage();
    });
    // A reaction or an unsend from the other side. The server stamps `from`, so
    // these can only ever act on the sender's own message.
    signaling.on(ServerMsg.REACTION, (m) => {
      groupApi?.setPeerObserving?.(m.from, false); // a reaction is a first word too
      if (mutedPeers.has(m.from)) return;
      groupApi?.peerReaction(m.id, m.emoji, m.from, m.on);
      chatApi?.peerReaction(m.id, m.emoji, m.from, m.on);
    });
    signaling.on(ServerMsg.UNSENT, (m) => {
      groupApi?.peerUnsend(m.id);
      chatApi?.peerUnsend(m.id);
    });
    signaling.on(ServerMsg.SYSTEM, (m) => {
      // A notice about a message between regulars (a refused one, a thanks
      // for a report) belongs in the thread it is about, not in the chat.
      if (m.scope === 'messages') {
        if (!regularsPanel?.messages?.notice(m)) toast(m.text, 4000);
        return;
      }
      // Notices go through addSystem, never addMessage: system styling must
      // not be reachable by anything a person can type or call themselves.
      const opts = { level: m.level, id: m.id };
      if (groupApi) groupApi.addSystem(m.text, opts);
      else chatApi?.addSystem(m.text, opts);
    });
    // The interests box was refused, or a moderator emptied it. Take the words
    // out of the field and out of storage, so the next hello does not send them
    // again: the box lives here, and leaving it full meant somebody who typed
    // one wrong word in a list of five carried it into every room they entered,
    // filing a report each time (the operator, 2026-09-17).
    signaling.on(ServerMsg.INTEREST_CLEARED, () => {
      interest = null;
      try {
        localStorage.removeItem('lumi.interest');
      } catch {
        // A browser with storage off keeps nothing to clear.
      }
    });
    // Live captions: a partner's transcribed speech (muted peers are not shown).
    signaling.on(ServerMsg.CAPTION, (m) => {
      if (mutedPeers.has(m.from)) return;
      const who = m.handle || (m.from ? String(m.from).slice(0, 4) : 'They');
      if (groupApi) groupApi.showCaption?.(who, m.text);
      else chatApi?.showCaption?.(who, m.text);
    });
    signaling.on(ServerMsg.TYPING, (m) => {
      // A group room names who; a 1:1 has only one other person, so it does not
      // need to.
      if (groupApi) groupApi.setTyping(m.from, m.handle || roomHandles.get(m.from), !!m.state);
      else chatApi?.setTyping(!!m.state);
    });

    // Admin drop-in: an administrator asks to talk live (e.g. while the user
    // waits in the queue). Consent-first -- nothing starts until Start is
    // tapped -- then camera+mic stream to the admin over a direct session.
    signaling.on(ServerMsg.ADMIN_LIVE_OFFER, (m) => showAdminDropIn(m));

    // A moderator's live look while you wait. The camera is already open for
    // the self-view; this sends it, and the microphone, to the server's SFU
    // for as long as the look lasts, and stops when told. It draws nothing:
    // oversight of a queue is there to see what actually goes on in it. An
    // age-safe video chat is monitored; the site does not announce each look.
    signaling.on(ServerMsg.QUEUE_MEDIA, (m) => {
      if (m.on) startQueueMedia();
      else stopQueueMedia();
    });

    // A TEXT drop-in from Lumi staff while you wait to be paired. No camera and
    // no consent prompt, because nothing is captured -- somebody is typing to
    // you, and you can answer or not. Rendered as a staff message rather than a
    // system notice: it IS a person, and it expects a reply.
    signaling.on(ServerMsg.ADMIN_CHAT, (m) => {
      if (m.open === false) {
        chatApi?.addSystem('Lumi Staff ended the conversation.');
        adminChatOpen = false;
        return;
      }
      adminChatOpen = true;
      if (m.text) chatApi?.addStaffMessage(m.text);
    });

    // A file cleared moderation and is waiting on the server.
    signaling.on(ServerMsg.FILE_SHARED, (m) => receiveSharedFile(m));

    signaling.on(ServerMsg.SKIP_UNLOCKED, () => chatApi?.setSkipEnabled(true));

    // Your own "come and talk to me" link. Arrives on request and again
    // unasked after a reconnect, so a page that came back still knows it.
    signaling.on(ServerMsg.INVITE_LINK, (m) => {
      myInviteCode = m.code || null;
      showInviteIfWaiting();
    });

    // You arrived through somebody's link. 'ok' needs nothing said: MATCHED
    // is already on its way. Everything else is a reason, and the person came
    // here expecting a specific human, so it is said in words.
    signaling.on(ServerMsg.INVITE_RESULT, (m) => {
      arrivingInvite = null;
      if (m.ok) return;
      // No reason means the server decided this one is not ours to explain:
      // an expired or unknown code, or anything that would give away who the
      // link belonged to. Say nothing and let them have an ordinary visit --
      // once a link dies it IS just a link to Lumi. See _refuseInvite.
      const said = {
        busy: 'They are already in a chat. You are in the queue, so try their Lumi Link again in a minute.',
        away: 'They are not here right now. You are in the queue, so you will still meet somebody.',
        self: 'That is your own Lumi Link. Share it with somebody else!',
      };
      if (said[m.reason]) toast(said[m.reason]);
    });

    // ---- Lower-pressure starts ----
    // Where the pair stands on cameras. When both have said yes, the camera
    // that has been waiting (captured, shown to you, sent to nobody) goes up.
    signaling.on(ServerMsg.REVEAL_STATE, (m) => {
      const wasThem = reveal.them;
      reveal = { me: !!m.me, them: !!m.them, both: !!m.both, unseen: m.unseen || null };
      if (reveal.both) stopPersonCheck(); // open now: nothing watches from here on
      if (m.closed) {
        // One camera going off closed both. Nothing of mine is switched off
        // here: my camera keeps reaching the server, where an administrator
        // may be watching; it is only not shown to them until we both agree
        // again.
        revealWanted = false;
        stopPersonCheck();
        if (!closingMine) toast('Their camera went off, so both cameras closed. Tap Show my camera to open them again.');
        closingMine = false;
      }
      pushRevealUi();
      refreshTextOnlyLayout(); // both agreeing is what lets my camera take the window
      // With no camera of your own the placeholder is not on screen (the
      // text-only layout hides the stage), so their readiness is said aloud.
      if (reveal.them && !wasThem && !reveal.me && !hasLocalVideo()) toast('They are ready to show their camera.');
      if (reveal.both) {
        ensureVideoProduced();
        startModSampler(); // the camera is shown now, so it is moderated now
      }
    });
    // Explicit consent in this pair. `both` false again is a stop, and takes
    // effect on the server the moment it is sent -- this only redraws.
    signaling.on(ServerMsg.EXPLICIT_STATE, (m) => {
      chatApi?.setExplicit?.({ available: true, me: !!m.me, them: !!m.them, both: !!m.both });
    });
    // One card after twenty seconds of silence. System-styled by construction
    // (see ui/chat.js): the words are the server's deck, never a person's.
    signaling.on(ServerMsg.ICEBREAKER, (m) => {
      if (groupApi) return; // a room pins its question instead
      chatApi?.showIcebreaker?.(m.text, () => signaling?.send(ClientMsg.ICEBREAKER_SKIP));
    });
    signaling.on(ServerMsg.TIMED_STATE, (m) => chatApi?.setTimed?.({ endsAt: m.endsAt, warnAt: m.warnAt }));
    // The gentle end. No go-back and no PEER_LEFT: the time was the agreement.
    // The server's SEARCHING follows and takes over the status overlay.
    signaling.on(ServerMsg.TIMED_END, (m) => {
      teardownPeer();
      pairedNow = false;
      forgetChat();
      offerWave({ pairId: m.pairId || lastPairId, canWave: !!m.canWave });
    });

    // They moved on (or were removed). The wave is offered for the chat that
    // just ended -- unless it ended in a report, which is never wavable.
    // A call ending is its own thing: no search, no wave (you are regulars).
    signaling.on(ServerMsg.PEER_LEFT, (m) => {
      if (inCall || m?.reason === 'call_ended') return endCallUi();
      teardownPeer();
      pairedNow = false;
      forgetChat();
      chatApi?.setStatus('Finding someone new...', 'Hang tight.');
      return offerWave({ pairId: lastPairId, reason: m?.reason || null, line: 'The chat ended.' });
    });

    // Their connection died, but the chat is being held open for them.
    //
    // This used to be PEER_LEFT: a dropped signal and a person walking out
    // looked identical, so a phone changing network ended the conversation and
    // put both of them back in a queue that rarely has anyone else in it. Now
    // the chat stands for a moment and this says so, rather than lying about
    // somebody having left.
    // What the landing shows: who else is here, the front-page rooms, the
    // announcement, admin chat, the channels. The same frames a visitor
    // socket gets; see wireLandingFrames.
    wireLandingFrames(signaling);
    signaling.on(ServerMsg.PEER_DROPPED, (m) => {
      const seconds = Math.max(1, Number(m?.seconds) || 45);
      chatApi?.setStatus(
        'They dropped out',
        `Their connection went. Holding the chat for ${seconds} seconds in case they get back. You can skip if you would rather not wait.`,
      );
    });
    // They made it back. A MATCHED follows immediately and rebuilds the call.
    signaling.on(ServerMsg.PEER_RETURNED, () => {
      chatApi?.setStatus('They are back', 'Reconnecting...');
    });

    // You skipped. You can still wave: a skip is not a verdict.
    signaling.on(ServerMsg.GOBACK_OFFER, (m) => {
      pairedNow = false;
      forgetChat();
      const duration = Math.max(0, (m.until || 0) - Date.now());
      chatApi?.setStatus('Finding someone new...', 'Skipped by mistake?');
      chatApi?.offerGoBack(duration || 3000, () => signaling?.send(ClientMsg.GOBACK));
      offerWave({ pairId: lastPairId, reason: 'skip', line: 'You moved on.' });
    });
    signaling.on(ServerMsg.GOBACK_FAILED, () => {
      chatApi?.clearGoBack();
      toast('They already moved on.');
    });

    // ---- Regulars ----
    // You are already regulars with the person you have just been put with.
    // The chat says so, and no wave is offered for this pair when it ends.
    signaling.on(ServerMsg.PAIR_REGULARS, (m) => {
      if (!m?.pairId || !m.regular) return;
      regularPairId = m.pairId;
      chatApi?.setPartnerRegular?.(true);
    });
    signaling.on(ServerMsg.WAVED, (m) => chatApi?.waveResult?.(m));
    signaling.on(ServerMsg.REGULARS, (m) => {
      // A socket minted before the sign-in is somebody else's list. The
      // HTTP door has the right one, and it must not be painted over.
      if (!socketIsMine()) return;
      regularsState = {
        regulars: Array.isArray(m.regulars) ? m.regulars : [],
        invisible: !!m.invisible,
        quietHours: m.quietHours || null,
        recent: Array.isArray(m.recent) ? m.recent : [],
        blocked: Array.isArray(m.blocked) ? m.blocked : [],
        staff: regularsState.staff || null,
      };
      landingRegularsCount = regularsState.regulars.length;
      regularsPanel?.update(regularsState);
      // The server's own unread counts came with that list: the bell says how
      // many messages are waiting, including the ones that arrived while this
      // tab was closed.
      syncBells();
    });
    // A notice: the bell's dot, a soft tone, and a toast. A "regular is around"
    // notice carries the room, so its toast has a Join.
    signaling.on(ServerMsg.NOTIFY, (m) => {
      // A call is a moment, not something the panel lists: no unread dot for
      // it. The toast ("Leave the room to pick up") is for a tab in a room;
      // anywhere else the ringing sheet IS the notice, so nothing doubles it.
      if (m.kind === 'call') {
        if (!groupApi) return;
        playNoticeTone();
        showNoticeToast(m);
        return;
      }
      // A message from the person whose thread is open on screen is already
      // on screen: no bell, no toast for it.
      if (m.kind === 'message' && m.personId && regularsPanel?.messages?.current() === m.personId) return;
      unreadNotices += 1;
      // A message is the one notice that is still true later, so it is the
      // one that gets said loudest: the bell carries the count (the rows
      // arrive with it), the toast stands twice as long, and it opens the
      // conversation rather than a list.
      if (m.kind === 'message' && m.personId) {
        // The count itself is the MSG handler's business (the row carries it,
        // and the server's own count arrives in REGULARS); this is the noise.
        syncBells();
        playNoticeTone();
        showNoticeToast(m, { onAction: () => openRegularsPanel({ thread: m.personId }), actionLabel: 'Read', ms: 12000 });
        return;
      }
      syncBells();
      playNoticeTone();
      // Somebody asked to join a community you own. The thing to do about it
      // is Accept, which lives in the panel -- not join the room, which is
      // yours already and answers nobody.
      if (m.kind === 'community_ask') {
        showNoticeToast(m, { onAction: () => openRegularsPanel(), actionLabel: 'Open' });
        return;
      }
      showNoticeToast(m, {
        onAction: m.roomId || m.code ? (n) => joinFromAround({ roomId: n.roomId, code: n.code }) : null,
      });
    });

    // ---- Calls (5.B4) ----
    // Somebody is ringing. Declined unseen when we are mid-chat or in a room:
    // the server did not offer it to such a session, but a client is not a
    // rule, and a sheet over a conversation would be the wrong surprise.
    signaling.on(ServerMsg.CALL_OFFER, (m) => {
      if (!m?.callId) return undefined;
      if (pairedNow || groupApi) return respondToCall(m.callId, false);
      offerSheet?.close();
      offerSheet = showOfferSheet({
        callId: m.callId,
        fromHandle: m.fromHandle || null,
        mode: m.mode,
        until: m.until,
        onRespond: respondToCall,
        onClose: () => {
          offerSheet = null;
        },
      });
      return undefined;
    });
    // Where a call stands. Matched to a sheet by callId: the ringing sheet
    // knows its call from the offer; the "Calling..." sheet learns its call
    // from the first of these. Anything else is a call we are no longer
    // showing, and is ignored.
    signaling.on(ServerMsg.CALL_STATE, (m) => {
      if (offerSheet && m.callId && offerSheet.callId === m.callId) {
        // cancelled, expired, connecting: the sheet goes; MATCHED follows a connecting.
        offerSheet.close();
        return;
      }
      callingSheet?.setState(m);
    });
    // ---- Messages between regulars (ui/messages.js) ----
    // A message for the open thread is appended there. Otherwise the row's
    // badge goes up; the NOTIFY that travels with it rings the bell.
    signaling.on(ServerMsg.MSG, (m) => {
      // Our own copy from another tab is not a message TO us: it must not
      // count, or writing on your phone would badge the tab on your desk. The
      // server puts clientId on the sender's copy and on no other, and sends it
      // to every one of the sender's tabs, so it is exactly this test.
      const mine = m.clientId != null;
      if (regularsPanel?.messages?.onMsg(m)) {
        if (!mine) noteMessage(); // the thread is open, but nobody is looking at it
        return;
      }
      const row = regularsState.regulars.find((r) => r.personId === m.from);
      if (!row) return; // our own copy from another tab, or nobody we list
      row.unread = (Number(row.unread) || 0) + 1;
      regularsPanel?.update(regularsState);
      syncBells();
      noteMessage();
    });
    signaling.on(ServerMsg.MSG_LIST, (m) => {
      regularsPanel?.messages?.onList(m);
      // Listing marks the thread seen on the server; the row agrees at once.
      const row = regularsState.regulars.find((r) => r.personId === m.with);
      if (row && row.unread) {
        row.unread = 0;
        regularsPanel?.update(regularsState);
        syncBells();
      }
    });
    signaling.on(ServerMsg.MSG_DELETED, (m) => regularsPanel?.messages?.onDeleted(m));

    signaling.on(ServerMsg.BANNED, (m) => {
      const mins = m.until ? Math.max(1, Math.round((m.until - Date.now()) / 60000)) : 0;
      toast(
        mins
          ? `A moderator removed you for ${mins} minute${mins === 1 ? '' : 's'} (${m.reason || 'rules violation'}).`
          : 'Access to Lumi has been restricted.',
        5000,
      );
      showLanding();
    });

    signaling.on(ServerMsg.ERROR, (m) => {
      // An open thread shows its own refusals (not regulars, the daily cap,
      // slow down) under the composer, where the person is looking.
      if (regularsPanel?.messages?.error(m)) return;
      if (m.code === 'rate_limited') toast('Slow down a moment.');
      // An invite refused for something about us: the sheet says so and goes.
      if (callingSheet) {
        const why = callErrorText(m.code);
        if (why) callingSheet.fail(why);
      }
      // The per-hour cap on new contacts (a minor in ten-minute mode). We are
      // not in the queue, so nothing happens until we ask again: say so, and
      // ask again in a minute. The server keeps saying no until the hour has room.
      if (m.code === 'slow_down') {
        chatApi?.setStatus('Take a breather', m.message || 'You have met a lot of people this hour. Come back in a bit.');
        scheduleReadyRetry();
      }
      // One place in the queue per person: this tab's search has moved to the
      // tab they are using now. Back to the start here, so that nothing this
      // tab does later (a reconnect, a closed panel) asks for a match again
      // and takes the search back.
      if (m.code === 'searching_elsewhere' && inOneOnOne() && !pairedNow) {
        showLanding();
        toast(m.message || t('You started looking for someone in another tab, so this one has stopped.'));
      }
      // And one live 1:1: a chat is going on in another tab, so this one does
      // not look for anybody. Said where the waiting screen says everything.
      if (m.code === 'in_another_chat') {
        chatApi?.setStatus(t('Already chatting in another tab'), m.message || t('Finish that chat, or close that tab, to look for someone here.'));
      }
    });

    // The signaling socket is the ONLY thing that lets the server see, moderate,
    // or end a chat -- but WebRTC media keeps flowing without it. So losing the
    // socket (a server restart during a deploy, a dropped network) must end the
    // call on this side too, in a GROUP room just as much as in 1:1: otherwise
    // people keep talking in a session the server no longer knows exists.
    signaling.on('__close', () => {
      if (reconnecting) return; // already on it; a second close is the same news
      // The channels the page watches have no carrier until the next socket.
      channels.unbind(signaling);
      // A live chat ENDS here, always. That rule has not changed and must not:
      // WebRTC media keeps flowing without the socket, so a pair whose server
      // has forgotten them would otherwise keep talking in a session nothing is
      // moderating. The peer goes down before anything else happens.
      const wasChatting = !!(chatApi || groupApi);
      // A 1:1 the server will HOLD for us keeps its media. reconnect() says
      // HELLO with the same identity and the server hands the same chat back
      // (matchmaker resume); the pair and its SFU room stand untouched for
      // RECONNECT_GRACE_MS, so for every one of those seconds the server
      // knows exactly whose media this is and nothing flows unmoderated. At
      // the end of them the old rule applies in full. A TEXT chat is held
      // the same way: there is no media to keep, but there is a screen --
      // the name, the timer, the lines said so far -- and resetting it on
      // every blip read as the chat dropping when the server had held it
      // (the operator, 2026-09-24, a text pair). A room, or a chat that
      // cannot be resumed, ends at once as it always did.
      //
      // Before this, every blip -- an iPhone locking its screen for two
      // seconds -- tore the call down on both sides and rebuilt it, which
      // both people saw as "disconnected" (the operator, 2026-09-24).
      const holdable = !!chatApi && !groupApi && pairedNow && !!resumeSeed;
      if (wasChatting && !holdable) {
        teardownPeer(); // stops the samplers and the media; keeps the camera open
        pairedNow = false;
        forgetChat();
        inCall = false;
        resetPairUi();
      } else if (holdable) {
        clearTimeout(mediaGrace);
        mediaGrace = setTimeout(() => {
          mediaGrace = null;
          // Nobody handed the chat back in time. The media goes now, as it
          // always went, and the page waits for its next match like anybody.
          teardownPeer();
          pairedNow = false;
          forgetChat();
          inCall = false;
          resetPairUi();
          chatApi?.setStatus('Finding someone new...', 'That chat could not be picked up again.');
        }, RECONNECT_GRACE_MS);
      }
      // What HAS changed is where that leaves you. A dropped socket used to
      // mean the homepage, so a three-second library patch at 06:18 put
      // everybody on the site back to the front door. Now the page gets itself
      // a new connection and the server puts you back in the queue.
      // A LOBBY socket has nothing to resume -- no queue place, no chat, no
      // resumeSeed -- so reconnect() rightly declines it and used to leave it
      // dead. That made a deploy, or any blip, quietly delete everyone
      // standing on the landing: invisible to the console, uncounted by each
      // other, for the rest of the visit. Which is the exact failure the
      // lobby exists to prevent. It just rejoins.
      //
      // Jittered, because the usual cause is the SERVER going away and every
      // client on the site is doing this in the same second.
      if (!helloSent && !wasChatting) {
        signaling = null;
        // A visitor socket meanwhile, so the page is never without one; the
        // rejoin replaces it.
        ensureSocket();
        setTimeout(() => {
          // Still on the landing, and nothing has taken the socket since.
          if (signaling || !root.querySelector('#lobbyCount')) return;
          joinLobby().then(() => {
            showLobbyCount();
            ensureSocket();
          });
        }, 800 + Math.random() * 2500);
        return;
      }
      if (chatApi) chatApi.setStatus('Reconnecting...', 'The connection dropped. Getting you back.');
      else if (wasChatting) toast('Connection lost. Reconnecting...');
      reconnect();
    });
  }

  // ---- SFU (mediasoup) media path -----------------------------------------

  /** Send one SFU request over the WS and resolve with the server's reply. */
  function sfuRpc(op, args = {}) {
    const reqId = (sfuReqId += 1);
    return new Promise((resolve, reject) => {
      sfuPending.set(reqId, { resolve, reject });
      signaling?.send(ClientMsg.SFU, { op, reqId, ...args });
      setTimeout(() => {
        if (sfuPending.has(reqId)) {
          sfuPending.delete(reqId);
          reject(new Error('sfu_timeout'));
        }
      }, 15000);
    });
  }

  /** Route an incoming SFU message: an RPC reply (has reqId) or a server event. */
  function handleSfuMessage(m) {
    if (m.reqId != null) {
      const pending = sfuPending.get(m.reqId);
      if (!pending) return;
      sfuPending.delete(m.reqId);
      if (m.ok) pending.resolve(m);
      else pending.reject(new Error(m.error || 'sfu_error'));
      return;
    }
    if (m.op === 'newProducer') sfuPeer?.onNewProducer(m.producerId, m.peerId);
    else if (m.op === 'producerClosed') sfuPeer?.onProducerClosed(m.producerId);
    else if (m.op === 'newDataProducer') sfuPeer?.onNewDataProducer(m.dataProducerId, m.peerId);
  }

  function setupSfuPeer() {
    teardownPeer();
    // Camera-off start is the SERVER's rule about who receives, not ours
    // about what to send: the camera goes up from the start, so a moderator
    // can watch any chat, and the partner is shown it only once both have
    // tapped "Show my camera". Frame moderation waits for that too (below).
    const full = sendStream || localStream;
    const initial = full;
    sfuPeer = new SfuPeer(
      (op, args) => sfuRpc(op, args),
      { localStream: initial, maxBitrate: maxVideoBitrate },
      {
        onPeerStream: (_peerId, stream) => {
          chatApi?.setRemoteStream(stream);
          remoteHasVideo = !!stream && (stream.getVideoTracks?.() || []).length > 0;
          watchRemoteVideo(); // the layout follows the picture, not the track
          chatApi?.hideStatus();
          chatApi?.pulse();
          chatApi?.focusInput();
        },
        onPeerGone: () => {
          chatApi?.setRemoteStream(null);
          remoteHasVideo = false;
          watchRemoteVideo(); // stops watching and hands the window back to the text
        },
        onFile: (_peerId, { mime, blob, name }) =>
          chatApi?.addFile('them', { mime, url: URL.createObjectURL(blob), name }),
        onState: (state) => {
          if (state === 'connected') chatApi?.hideStatus();
          // Media kept through a dropped socket can still have died with the
          // network -- a phone that came back on another address. A transport
          // that FAILED while the pair stands is rebuilt once, through the same
          // path a new match uses. Once: one that keeps failing is left to the
          // hold and the queue rather than looped on.
          if (state === 'failed' && pairedNow && inOneOnOne() && socketReady() && sfuRebuiltFor !== lastPairId) {
            sfuRebuiltFor = lastPairId;
            setupSfuPeer();
          }
        },
      },
    );
    sfuPeer
      .start()
      // Both may already have revealed by the time the transports exist.
      .then(() => ensureVideoProduced())
      .catch(() => toast('Could not connect the call. Finding someone new...'));
    // A camera nobody has opened is not moderated: in a 1:1 the sampler starts
    // when REVEAL_STATE says both (or now, if the pair was born revealed).
    if (!inOneOnOne() || reveal.both) startModSampler();
    startAudioSampler();
  }

  // ---- A moderator's look while waiting (ServerMsg.QUEUE_MEDIA) -----------
  // True while our media goes up for it. A pair or a room has its own SFU peer
  // and tears this one down on its way in (setupSfuPeer -> teardownPeer).
  let queueMediaOn = false;
  function startQueueMedia() {
    if (pairedNow || groupApi || !sfuEnabled) return; // a chat is already sending
    const full = sendStream || localStream;
    if (!full || !full.getTracks().length) return; // text mode: nothing to send
    // Already sending for this look: every Watch click says "on" again, and
    // rebuilding here put two connections for one person in the room at once,
    // where the newer closes the older and can take the camera with it. A
    // start that failed has set queueMediaOn back to false, so it still retries.
    if (queueMediaOn && sfuPeer) return;
    teardownSfuPeer();
    queueMediaOn = true;
    // No callbacks: nothing comes back from a room whose only other peer is
    // recv-only, and nothing about this reaches the screen.
    sfuPeer = new SfuPeer((op, args) => sfuRpc(op, args), { localStream: full, maxBitrate: maxVideoBitrate }, {});
    sfuPeer.start().catch(() => {
      queueMediaOn = false;
    });
  }
  function stopQueueMedia() {
    if (!queueMediaOn) return;
    queueMediaOn = false;
    if (!pairedNow) teardownSfuPeer();
  }

  function teardownSfuPeer() {
    queueMediaOn = false;
    sfuPeer?.close();
    sfuPeer = null;
    for (const { reject } of sfuPending.values()) reject(new Error('closed'));
    sfuPending.clear();
  }

  // ---- Lower-pressure starts (client side) ---------------------------------

  /** In a 1:1, as opposed to a group room or the rooms hub. */
  function inOneOnOne() {
    return !!chatApi && !groupApi;
  }
  function hasLocalVideo() {
    return (localStream?.getVideoTracks?.() || []).some((track) => track.readyState === 'live');
  }
  /** Tell the chat screen where the pair stands on cameras. */
  function pushRevealUi() {
    if (!inOneOnOne()) return;
    chatApi?.setReveal?.({
      ...reveal,
      hasVideo: hasLocalVideo(),
      // Whether this is a chat where cameras are the point. The panel needs
      // it so it can say the cameras are shut before there is one to offer.
      videoChat: resumeSeed?.effectiveMode === ChatMode.VIDEO && videoAllowed,
    });
  }
  // ---- The one-time person check, while a 1:1's cameras are opening --------
  // After "Show my camera", this side's own camera is looked at once a second
  // (person-check.js) and the answer goes to the server with the reveal. The
  // cameras open only when both have tapped and both are in view, and from
  // then on nothing watches: somebody who steps away keeps their camera (the
  // operator, 2026-09-15). The camera keeps reaching the server the whole
  // time, as it always has, so an administrator's view is unchanged.
  let revealWanted = false;
  let personLoop = false;
  let personTimer = null;
  let personProbe = null;
  let personSent = null; // the answer the server has, or null before the first
  let closingMine = false; // this side's camera going off is what closed both
  function cameraTrackForCheck() {
    if (rawCamTrack && rawCamTrack.readyState === 'live') return rawCamTrack;
    return (localStream?.getVideoTracks?.() || []).find((track) => track.readyState === 'live') || null;
  }
  function stopPersonCheck() {
    personLoop = false;
    clearTimeout(personTimer);
    personTimer = null;
    personProbe?.stop();
    personProbe = null;
    personSent = null;
  }
  function stillChecking() {
    return personLoop && inOneOnOne() && revealWanted && !reveal.both;
  }
  async function checkPersonOnce() {
    personTimer = null;
    if (!stillChecking()) return stopPersonCheck();
    const track = cameraTrackForCheck();
    let person = false;
    if (track && track.enabled !== false) {
      const segmenter = await mlSegmenterReady();
      if (!segmenter) {
        person = true; // the model cannot run here: never lock anybody out for it
      } else {
        if (!personProbe || personProbe.video.srcObject?.getVideoTracks?.()[0] !== track) {
          personProbe?.stop();
          personProbe = createProbeVideo(track);
          // A moment for the first frame, or the first answer is "nobody".
          for (let wait = 0; wait < 30 && !personProbe.video.videoWidth; wait += 1) {
            await new Promise((r) => setTimeout(r, 50));
          }
        }
        // A probe that never draws a frame is OUR problem, not theirs: a
        // covered or turned-away camera still delivers frames (black ones),
        // so "no frame at all" means the hidden probe video did not start on
        // this browser. Answering "nobody" there kept a person on "We can't
        // see you yet" for good, with a camera that was reaching the server
        // (2026-09-17). Fail open, as the check does when the model cannot run.
        person = personProbe.video.videoWidth ? await seesPerson({ video: personProbe.video, segmenter }) : true;
      }
    }
    if (!stillChecking()) return stopPersonCheck();
    if (person !== personSent) {
      personSent = person;
      signaling?.send(ClientMsg.REVEAL, { person });
    }
    personTimer = setTimeout(checkPersonOnce, 1000);
  }
  function startPersonCheck() {
    if (personLoop) return;
    personLoop = true;
    personTimer = setTimeout(checkPersonOnce, 0);
  }

  /** "Show my camera": our half of the agreement, the only half we can give. */
  function requestReveal() {
    if (!inOneOnOne() || reveal.me) return;
    revealWanted = true;
    // Tapping it is turning the camera on: a camera switch left off would send
    // black, fail the person check, and leave the switch saying off while the
    // button said shown (the operator, 2026-09-15). As the switch would, but
    // without its off/on messages, since this tap is the yes.
    chatApi?.setCamOn?.(true);
    // Shown at once; the server hears once this camera has been looked at,
    // and REVEAL_STATE confirms it and adds their side.
    reveal = { ...reveal, me: true };
    pushRevealUi();
    refreshTextOnlyLayout();
    startPersonCheck();
  }
  /**
   * Make sure the camera is produced to the SFU, once. Called when the SFU
   * finishes starting and again when REVEAL_STATE says both, because a camera
   * can appear between the two. The reveal no longer gates this: the server
   * decides who receives the track.
   */
  async function ensureVideoProduced() {
    if (!inOneOnOne() || producingVideo) return;
    const track = outboundVideoTrack();
    if (!sfuPeer || !track || sfuPeer.hasProducer('video')) return;
    producingVideo = true;
    try {
      // null means no send transport yet; the call after start() produces.
      await sfuPeer.addTrack(track);
    } catch {
      toast('Could not start your camera. Please try again.');
    } finally {
      producingVideo = false;
    }
    refreshTextOnlyLayout();
  }
  /** A new search: nothing of the last pair stays on screen. */
  // Waiting, with nobody there yet.
  //
  // Lumi is new, and at a quiet hour a person can sit in the queue for
  // minutes. The honest advice is to stop staring at it: leave the tab open,
  // go and do something else, and let the tab shout. That only helps if we
  // actually say so, and only if we say it at the point where somebody has
  // started to wonder whether the site is broken -- not on arrival, which
  // would announce that nobody is here before they have waited at all.
  /**
   * When to offer the "come and talk to me" link.
   *
   * Thirty seconds. Later than the "leave this tab open" hint, because that
   * one is the cheaper thing to try first, and much earlier than the couple
   * of minutes it takes somebody to give up -- by which point they have gone
   * and the link has nobody to be shared by.
   */
  const INVITE_OFFER_AFTER_MS = 30_000;
  // The two waiting-screen notices can be closed, and a close is remembered
  // on this device: on a phone they were most of the screen, and somebody who
  // has read them once does not need them on every wait (the operator,
  // 2026-09-15). Storage is a convenience; without it they simply come back.
  const CLOSED_KEY = Object.freeze({ hint: 'lumi.closed.quietHint', invite: 'lumi.closed.inviteOffer' });
  function wasClosed(key) {
    try {
      const at = Number(localStorage.getItem(key));
      // Not a time: the old "for ever" mark, from before a dismissal lapsed.
      // Treated as lapsed and cleared, so a device that put one of these away
      // months ago gets the advice back once rather than never.
      if (!Number.isFinite(at) || at <= 0) {
        localStorage.removeItem(key);
        return false;
      }
      return Date.now() - at < CLOSED_FOR_MS;
    } catch {
      return false;
    }
  }
  /**
   * How long putting one of these notices away lasts.
   *
   * It used to be for ever, and per device rather than per person: one tap on
   * the cross and nobody using that browser ever learned about the sound or
   * the Lumi Link again (the operator, 2026-09-18). A day is long enough that
   * dismissing it means something for the rest of the sitting, and short
   * enough that it is an answer to today rather than a permanent setting.
   */
  const CLOSED_FOR_MS = 24 * 60 * 60 * 1000;
  function rememberClosed(key) {
    try {
      localStorage.setItem(key, String(Date.now()));
    } catch {
      /* comes back next time, which is harmless */
    }
  }
  let inviteTimer = null;
  function showInviteIfWaiting() {
    if (!chatApi || pairedNow || !myInviteCode || !inviteOfferDue) return;
    if (wasClosed(CLOSED_KEY.invite)) return; // put away on this device
    chatApi.setInviteLink?.(`${location.origin}/i/${myInviteCode}`);
  }
  let inviteOfferDue = false;
  /**
   * How long after the advice the link is offered when the band is empty.
   * Both used to arrive in the same instant, and two notices at once reads as
   * the page talking over itself (the operator, 2026-10-05: "before, one
   * would show and then the other"). The same twelve seconds that separate
   * them when somebody is there: eighteen, then thirty.
   */
  const INVITE_AFTER_HINT_MS = 12_000;
  function armInviteOffer(soon = false) {
    clearTimeout(inviteTimer);
    // A new wait starts the offer over, so it comes after the advice again.
    inviteOfferDue = false;
    chatApi?.setInviteLink?.(null);
    inviteTimer = setTimeout(() => {
      inviteTimer = null;
      inviteOfferDue = true;
      showInviteIfWaiting();
    }, soon ? INVITE_AFTER_HINT_MS : INVITE_OFFER_AFTER_MS);
  }
  function cancelInviteOffer() {
    clearTimeout(inviteTimer);
    inviteTimer = null;
    inviteOfferDue = false;
    chatApi?.setInviteLink?.(null);
  }

  const QUIET_HINT_AFTER_MS = 18_000;
  let quietHintTimer = null;
  function armQuietHint(now = false) {
    clearTimeout(quietHintTimer);
    chatApi?.setWaitHint?.(null); // a new wait says it again, at the bottom
    const say = () => {
      quietHintTimer = null;
      if (!chatApi || pairedNow) return;
      // "Still looking..." above already says it is quiet, and the invite
      // offer below says it again to sell the Lumi Link. Three times on one
      // phone screen is nagging, so this one just gives the instruction.
      //
      // Translated, which it was not: a status set from here goes straight to
      // the DOM, so a Spanish speaker waiting on a Spanish page was read to
      // in English at the one moment they are most likely to leave.
      // The status says only that; the advice is a Lumi notice in the chat
      // (ui/chat.js setWaitHint). Closed once on this device: no advice.
      const closed = wasClosed(CLOSED_KEY.hint);
      chatApi.setStatus(t('Still looking...'), '');
      if (!closed) {
        chatApi.setWaitHint?.(
          getJoinSoundOn()
            ? t('Leave this tab open and go and do something else. You will hear a sound the moment someone arrives.')
            : t('Leave this tab open and go and do something else. The tab title will change the moment someone arrives.'),
        );
      }
    };
    if (now) say();
    else quietHintTimer = setTimeout(say, QUIET_HINT_AFTER_MS);
  }
  function cancelQuietHint() {
    clearTimeout(quietHintTimer);
    quietHintTimer = null;
    chatApi?.setWaitHint?.(null);
  }

  function resetPairUi() {
    reveal = { me: false, them: false, both: false };
    explicitAgreed = false;
    chatApi?.setExplicit?.(null);
    chatApi?.setReveal?.(null);
    revealWanted = false;
    closingMine = false;
    stopPersonCheck();
    refreshTextOnlyLayout(); // no pair, no agreement: back to the text layout
    chatApi?.setTimed?.(null);
    chatApi?.setPeerLang?.(null);
    // Who you were talking to goes when the PAIR goes -- not when the media
    // peer is rebuilt. Clearing it in teardownPeer meant every match wiped its
    // own name a few lines after setting it, because setupPeer/setupSfuPeer
    // begin by tearing the old peer down: the header read a bare "Chat" for
    // the whole conversation and nothing on screen said who this was.
    chatApi?.setPeerName?.(null);
    // And everything else that was about THEM. The interests line and the
    // badges stayed through the skip, so "Finding someone new" sat over the
    // last person's name, interests and marks (the operator, 2026-09-17).
    chatApi?.setPeerInterest?.(null);
    chatApi?.setPartnerModerator?.(false);
    chatApi?.setPartnerStaff?.(false);
    chatApi?.setPartnerPlus?.(false);
    chatApi?.setPartnerRegular?.(false);
    chatApi?.hideIcebreaker?.();
  }
  function scheduleReadyRetry() {
    clearTimeout(slowDownTimer);
    slowDownTimer = setTimeout(() => {
      slowDownTimer = null;
      if (signaling && inOneOnOne()) signaling.send(ClientMsg.READY);
    }, 60_000);
  }

  // ---- Group rooms (client) ----------------------------------------------

  // ---- Following a room link must never end in silence ---------------------
  //
  // Reported on 2026-09-13: a minor tapped a promoted room on the home page and
  // the button sat on "Connecting..." forever. The home page lists promoted
  // rooms to everybody, because it is a public page and does not know who is
  // reading it, so a thirteen-year-old can certainly tap an adult room.
  //
  // The server is not at fault. It answers every refused join with ROOM_ERROR,
  // which is pinned by a test, and the handler below drops out to the hub when
  // it sees one. Following the link in a browser lands in the hub correctly,
  // and the production logs show no refused join at all for that session, so
  // whatever went wrong happened BEFORE the join was sent and was not
  // reproducible from the outside.
  //
  // So rather than guess at the path, this removes the outcome. Once the join
  // has been sent, either a room opens or an error arrives; if neither has
  // happened in fifteen seconds the person goes to the hub, which is somewhere
  // they can act, instead of a button that says Connecting and means nothing.
  //
  // Fifteen seconds is deliberately long: a slow phone on a bad connection is
  // not an error, and this must never pull somebody out of a room that was
  // about to open.
  const JOIN_WATCHDOG_MS = 15_000;
  let joinWatchdog = null;

  function clearJoinWatchdog() {
    if (joinWatchdog) clearTimeout(joinWatchdog);
    joinWatchdog = null;
  }

  function armJoinWatchdog() {
    clearJoinWatchdog();
    joinWatchdog = setTimeout(() => {
      joinWatchdog = null;
      if (currentRoom) return; // it opened after all
      joinCode = null;
      toast(roomErrorText('unavailable'));
      enterRoomsHub();
    }, JOIN_WATCHDOG_MS);
  }

  function roomErrorText(code) {
    switch (code) {
      case 'room_not_found':
        return 'That room does not exist.';
      case 'room_full':
        return 'That room is full.';
      case 'code_required':
        return 'That is a private room: you need its code.';
      case 'band_mismatch':
        return 'That room is for a different age group.';
      // Community rooms. members_only normally carries the code and is routed
      // to the landing before it gets here; this is the wording without one.
      case 'members_only':
        return 'That is a community: only its members can enter.';
      case 'removed_from_community':
        return 'You are no longer a member of this community.';
      case 'community_closed':
        return 'This community has been closed.';
      // Staff ended the room from the console. The server sends the reason as
      // a system notice too, which is the part that says WHY; this is the
      // fallback wording for the toast, so it never reads "Could not join
      // that room" about a room you were sitting in.
      case 'room_closed':
        return 'Lumi Staff closed this room.';
      case 'bad_code':
        return 'That code does not look right.';
      // Inviting a guest fails with its own codes. They used to fall through to
      // the default below, so a failed invite said "Could not join that room" --
      // about a room you are already sitting in. That is worse than silence: it
      // sends you looking at the wrong thing.
      case 'no_room':
        return 'You need to be in a room to invite somebody.';
      case 'adults_only':
        return 'Guests can only be invited into adult rooms.';
      case 'owner_only':
        return 'Only the host can invite somebody in.';
      case 'already_guest':
        return 'There is already a guest here. Wait for their ten minutes to finish.';
      // Activities (D0) and the watch-together queue (D7).
      case 'activity_running':
        return 'Something is already running. Stop it first.';
      case 'not_enough_players':
        return 'That one needs more people in the room.';
      case 'host_only':
        return 'Only whoever is running the activity can do that.';
      case 'not_allowed':
        return 'That is not yours to change.';
      case 'not_available':
        return 'That is not available in this room.';
      case 'no_activity':
        return 'Nothing is running right now.';
      case 'too_large':
        return 'That was too big to send.';
      case 'bad_url':
        return 'That link is not one Lumi can play.';
      case 'queue_full':
        return 'The queue is full.';
      case 'queue_member_full':
        return 'You already have ten in the queue. Let somebody else add one.';
      case 'no_video':
        return 'Nothing is playing to skip.';
      default:
        return 'Could not join that room.';
    }
  }

  function enterRoomsHub() {
    teardownPeer();
    currentRoom = null;
    groupApi?.dispose?.(); // let the room take its window listeners with it
    groupApi = null;
    const hub = renderRooms({
      initialMode: chosenMode,
      // Staff get a second list: every live room, so a drop-in can reach a
      // private one or a minors' room. The server decides whether to answer;
      // this only decides whether to ask.
      isStaff,
      onRefreshAll: () => signaling?.send(ClientMsg.LIST_ALL_ROOMS),
      onCreate: ({ name, visibility, mode }) => signaling?.send(ClientMsg.CREATE_ROOM, { name, visibility, mode }),
      onRandom: (mode) => signaling?.send(ClientMsg.JOIN_RANDOM_ROOM, { mode }),
      // `invisible` is a staff-only request; the server ignores it from
      // anyone else, so passing it through unconditionally is safe.
      onJoin: ({ roomId, invisible }) => signaling?.send(ClientMsg.JOIN_ROOM, { roomId, invisible }),
      onJoinCode: (code) => signaling?.send(ClientMsg.JOIN_ROOM, { code }),
      onRefresh: () => signaling?.send(ClientMsg.LIST_ROOMS),
      onBack: showLanding,
    });
    roomsApi = hub.api;
    mount(root, hub.root);
    // The regulars bell in the hub's top bar, when the wave exists for this
    // band. Placed from here rather than inside rooms.js so the hub knows
    // nothing about regulars.
    ensureHubBell();
    // "Your community", above the rooms the site keeps open. Same reasoning:
    // the hub offers a slot, and knows nothing about communities.
    communityPanel = null;
    communityState = null;
    if (social && hub.api.communitySlot) {
      communitySection = renderCommunitySection({ client: communityClient, onOpen: openCommunity, onManage: openRegularsPanel });
      hub.api.communitySlot.append(communitySection.root);
    }
  }

  function enterGroupRoom(room) {
    teardownPeer();
    roomsApi = null;
    currentRoom = room;
    joinCode = null; // joined -> consume the invite and tidy the address bar
    try {
      if (/\/r\//.test(location.pathname)) history.replaceState({}, '', '/');
    } catch {
      /* ignore */
    }
    roomHandles.clear();
    sawVideoState = false; // a new room announces its video afresh
    mutedPeers.clear(); // mutes are per-room
    for (const mem of room.members || []) {
      if (mem && mem.peerId && mem.handle) roomHandles.set(mem.peerId, mem.handle);
    }
    const screen = renderGroupChat({
      room,
      // Who I am, for the activity frame (D0): the server's participant list
      // is peer ids, and the frame needs to know which one is this screen.
      selfId,
      handle,
      // Activities and the watch-together queue. Every rule is the server's;
      // these carry the request.
      onActivity: (op, slug, data) => signaling?.send(ClientMsg.ACTIVITY, { op, slug: slug || undefined, data }),
      onVideoQueue: (action, extra) => signaling?.send(ClientMsg.VIDEO_QUEUE, { action, ...(extra || {}) }),
      // Reporting the video playing in a teen room. The server stops it for
      // the room and files the report against whoever put it there.
      onVideoReport: (reason) => signaling?.send(ClientMsg.VIDEO_REPORT, { reason }),
      onSend: (text, replyTo) => {
        const id = newMessageId();
        signaling?.send(ClientMsg.CHAT, { text, id, replyTo: replyTo || null });
        return id; // the room renders your own copy with the same id
      },
      onReact: (id, emoji, on) => signaling?.send(ClientMsg.REACT, { id, emoji, on }),
      onUnsend: (id) => signaling?.send(ClientMsg.UNSEND, { id }),
      onFile: (file) => sendFile(file),
      onReport: (peerId, reason, image) =>
        signaling?.send(ClientMsg.REPORT, {
          reason,
          targetId: peerId,
          image: image || grabPeerFrame(peerId),
        }),
      onMicVolume: (gain) => {
        if (micGain) micGain.gain.value = gain;
      },
      onEffect, // background blur / filters / noise suppression -- same handler as 1:1
      // A microphone or camera coming on is the room hearing from you: the
      // server clears observer mode when the track is produced, and the
      // roster mark clears here without waiting for the round trip.
      // Only once a track is really live: enableMedia gives up quietly when
      // the permission is refused, and a "listening" mark that cleared on a
      // refused microphone would say something false about you.
      onEnableMic: async () => {
        if (roomInvisible) return invisibleStaysSilent();
        await enableMedia({ audio: true });
        if ((localStream?.getAudioTracks() || []).some((track) => track.readyState === 'live')) {
          groupApi?.setSelfObserving?.(false);
        }
      },
      onEnableCam: async () => {
        if (roomInvisible) return invisibleStaysSilent();
        await enableMedia({ video: true });
        if (outboundVideoTrack()) groupApi?.setSelfObserving?.(false);
        refreshTextOnlyLayout();
      },
      onCamToggle: (on) => {
        if (on && roomInvisible) return invisibleStaysSilent();
        if (on) groupApi?.setSelfObserving?.(false);
        return onGroupCamToggle(on);
      },
      onToggleMute: (peerId) => {
        if (mutedPeers.has(peerId)) mutedPeers.delete(peerId);
        else mutedPeers.add(peerId);
        groupApi?.setMuted(peerId, mutedPeers.has(peerId));
      },
      onStageRequest: () => signaling?.send(ClientMsg.STAGE_REQUEST, {}),
      // The host's way onto the front page. Opens the shop at the promotion
      // section for THIS room; signing in happens there if it has to.
      onPromote: () => openPromo(room.code),
      onGoPrivate: (peerId) => signaling?.send(ClientMsg.GO_PRIVATE_REQUEST, { targetId: peerId }),
      onPrivateRespond: (fromId, accept) => signaling?.send(ClientMsg.GO_PRIVATE_RESPOND, { fromId, accept }),
      // A community's owner has the moderator's fifteen-minute kick inside
      // their own room (the server checks; this only shows the button).
      canModerate: isModerator || (!!room.community && room.ownerId === selfId),
      onKick: (peerId, reason, note) => signaling?.send(ClientMsg.KICK, { targetId: peerId, reason, note }),
      // Shared video. Every rule about who may do what is enforced on the
      // server; these just carry the request.
      onTyping: (state) => signaling?.send(ClientMsg.TYPING, { state }),
      onLanguage: chooseLanguage, // "Translate to" in the room header
      onVideoPin: (url) => signaling?.send(ClientMsg.VIDEO_PIN, { url }),
      onVideoControl: (action, atMs) => signaling?.send(ClientMsg.VIDEO_CONTROL, { action, atMs }),
      onVideoShare: (on) => signaling?.send(ClientMsg.VIDEO_SHARE, { on }),
      onVoteStart: () => signaling?.send(ClientMsg.ROOM_VOTE_START),
      onVoteCast: (yes) => signaling?.send(ClientMsg.ROOM_VOTE_CAST, { yes }),
      // Observer mode and the quiet room. The text of the hello is fixed and
      // shared; the id is minted here so the local copy and the relayed one
      // are the same message.
      onObserverHello: () => {
        const id = newMessageId();
        signaling?.send(ClientMsg.OBSERVER_HELLO, { id });
        return id;
      },
      onFocusStart: (minutes) => signaling?.send(ClientMsg.FOCUS_START, { minutes }),
      onStatus: (text) => signaling?.send(ClientMsg.ROOM_STATUS, { text }),
      onLeave: () => {
        signaling?.send(ClientMsg.LEAVE_ROOM);
        enterRoomsHub();
      },
    });
    groupApi = screen.api;
    pushLanguageControl(); // WELCOME may already have said which languages
    ensureBell(); // the room has a bell slot too; WELCOME fills it if this comes first
    mount(root, screen.root);
    // A community's room carries its panel: members, who is here, the next
    // session, the owner's controls. Drawn from COMMUNITY_STATE, which follows
    // ROOM_JOINED (or already arrived, if this is a redraw).
    communitySection = null;
    communityPanel = null;
    if (room.community && groupApi.communitySlot) {
      const panel = renderCommunityPanel({
        client: communityClient,
        onKick: (peerId, reason, note) => signaling?.send(ClientMsg.KICK, { targetId: peerId, reason, note }),
        onManage: openRegularsPanel,
      });
      communityPanel = panel.api;
      groupApi.communitySlot.append(panel.root);
      if (communityState && communityState.id === room.community) communityPanel.update(communityState);
    }
    // Show yourself what everyone else in the room sees on you.
    groupApi.setModerator('self', isModerator);
    groupApi.setStaff('self', isStaff);
    groupApi.setPlus('self', isPlusMember);
    // Whoever created the room owns it to begin with.
    groupApi.setVideoOwner(room.ownerId === selfId, room.ownerId, room.ownerId === selfId || isStaff);
    videoSlots = room.videoSlots || null;
    videoWaiting = false;
    groupApi.setVideoSlots(slotsForUi(videoSlots));
    // A TEXT room never opens a camera or a microphone -- and it does not keep
    // one open either. The landing page may have captured both for a video
    // chat; walking into a text room with the camera still live showed the
    // person their own face in a room with no way to turn it off. Release it.
    // A video or audio room joined afterwards asks again through its own
    // camera and microphone buttons.
    if ((room.mode || ChatMode.VIDEO) === ChatMode.TEXT) releaseLocalMedia();
    // An invisible staff drop-in sends nothing. The landing may have captured
    // the camera and microphone for a video chat, and walking into a room
    // publishes whatever is held: the SFU announces it to every peer, whose
    // screens draw a tile for it. So nothing is held, and the microphone and
    // camera buttons refuse until the room is left. The server refuses too.
    roomInvisible = !!room.invisible;
    if (roomInvisible) releaseLocalMedia();
    // The QUIET room starts with the microphone muted and the camera off, and
    // the simplest true form of that is to hold neither: whatever the landing
    // captured is released, nothing is produced on entry, and the microphone
    // and camera buttons ask again when the person means it. Turning either
    // on is also what ends observer mode, on the server and here.
    if (room.quiet) releaseLocalMedia();
    if (localStream) groupApi.setSelf(localStream);
    groupApi.initEffects(effectPrefs); // sync blur/filter/denoise controls to saved prefs
    refreshSelfView(); // if an effect was already on, show the processed video immediately
    // Seed a tile for every existing member so each can be muted, even before media.
    for (const mem of room.members || []) {
      if (mem && mem.peerId && mem.peerId !== selfId) {
        groupApi.ensurePeer(mem.peerId, mem.handle);
        if (mem.moderator) groupApi.setModerator(mem.peerId, true);
        if (mem.staff) groupApi.setStaff(mem.peerId, true); // verified Lumi staff
        if (mem.plus) groupApi.setPlus(mem.peerId, true); // subtle Plus mark
        if (mem.observing) groupApi.setPeerObserving?.(mem.peerId, true);
        if (mem.status) groupApi.setPeerStatus?.(mem.peerId, mem.status);
      }
    }
    // A text room joins no media at all: no SFU transport, no camera or
    // microphone asked for, nothing sampled. Asking for a camera in a room
    // whose whole point is typing is exactly what having room kinds is meant
    // to stop.
    currentRoomMode = room.mode || ChatMode.VIDEO;
    if (currentRoomMode !== ChatMode.TEXT) setupGroupMedia();
  }

  /** The camera or microphone button, pressed by an invisible drop-in. */
  function invisibleStaysSilent() {
    toast('You are here unseen. Your camera and microphone stay off; leave and drop in visibly to speak.');
  }

  // ---- Video slots in a group room ------------------------------------------
  // Membership is wide and cameras are metered (VIDEO_SLOTS). The server
  // refuses a camera when the slots are full; the person stays in with sound
  // and text, and is put on camera the moment a slot opens.
  function groupBitrate() {
    return maxVideoBitrate ? Math.min(maxVideoBitrate, GROUP_VIDEO_MAX_BITRATE) : GROUP_VIDEO_MAX_BITRATE;
  }
  function outboundVideoTrack() {
    const s = sendStream || localStream;
    return s ? s.getVideoTracks().find((track) => track.readyState === 'live') || null : null;
  }
  // The server names people by session id; your own tile is 'self'.
  function slotsForUi(slots) {
    if (!slots) return slots;
    return { ...slots, holders: (slots.holders || []).map((id) => (id === selfId ? 'self' : id)) };
  }
  function isSlotRefusal(err) {
    return /no_video_slot/.test(String(err && err.message ? err.message : err));
  }
  function onVideoRefused() {
    videoWaiting = true;
    groupApi?.setSelfVideoWaiting(true);
    toast(`All ${videoSlots?.max || VIDEO_SLOTS} camera slots are taken. You are in with sound and text, and you will be put on camera when one opens.`);
  }
  async function takeVideoSlot() {
    const track = outboundVideoTrack();
    if (!sfuPeer || !track || sfuPeer.hasProducer('video')) return;
    try {
      await sfuPeer.addTrack(track);
      videoWaiting = false;
      groupApi?.setSelfVideoWaiting(false);
      toast('A camera slot opened. You are on camera now.');
    } catch (err) {
      if (isSlotRefusal(err)) videoWaiting = true; // somebody was quicker; keep waiting
    }
  }
  /**
   * The one-time person check for a room camera (person-check.js): a room has
   * few camera slots, and a covered camera or one pointed at the ceiling would
   * take one somebody in view could have had (the operator, 2026-09-15). Asked
   * when the camera would go live, never afterwards.
   */
  function roomCameraSeesPerson() {
    return lookForPerson({ track: cameraTrackForCheck(), getSegmenter: mlSegmenterReady });
  }
  /** Nobody in front of it: the camera does not go live, and the person is told why. */
  function nobodyOnRoomCamera() {
    groupApi?.setCamOn?.(false);
    toast("We couldn't see anyone in front of your camera, so it didn't go on. Uncover it or face it, then turn it on again.");
  }

  async function onGroupCamToggle(on) {
    if (!sfuPeer) return;
    if (!on) {
      // Off means OFF: the producer closes, the slot frees, and nobody spends
      // bandwidth downloading a black frame from you.
      await sfuPeer.closeKind('video');
      videoWaiting = false;
      groupApi?.setSelfVideoWaiting(false);
      return;
    }
    const track = outboundVideoTrack();
    if (!track || sfuPeer.hasProducer('video')) return;
    if (!(await roomCameraSeesPerson())) return nobodyOnRoomCamera();
    try {
      await sfuPeer.addTrack(track);
    } catch (err) {
      if (isSlotRefusal(err)) onVideoRefused();
    }
  }

  function setupGroupMedia() {
    teardownSfuPeer();
    // An AUDIO room publishes voice only. Someone who chose video on the landing
    // still has a camera track open, and sending it into a room whose kind says
    // "no cameras" would break the promise the room made to everyone in it.
    let publish = sendStream || localStream;
    if (currentRoomMode === ChatMode.AUDIO && publish) {
      publish = new MediaStream(publish.getAudioTracks());
    }
    // A camera walked in with goes live only once the person check has seen
    // somebody in front of it (roomCameraSeesPerson). The voice goes up at once.
    const roomCam = publish ? publish.getVideoTracks().find((track) => track.readyState === 'live' && track.enabled !== false) || null : null;
    if (roomCam) publish = new MediaStream(publish.getAudioTracks());
    sfuPeer = new SfuPeer(
      (op, args) => sfuRpc(op, args),
      { localStream: publish, maxBitrate: groupBitrate() },
      {
        onProduceRefused: (kind, err) => {
          if (kind === 'video' && isSlotRefusal(err)) onVideoRefused();
        },
        onPeerStream: (peerId, stream) => {
          groupApi?.addPeer(peerId, stream, roomHandles.get(peerId));
          groupApi?.setPeerObserving?.(peerId, false); // a voice or a face is not quietly listening
          if (mutedPeers.has(peerId)) groupApi?.setMuted(peerId, true); // re-apply mute to a fresh tile
        },
        onPeerGone: (peerId) => groupApi?.removePeer(peerId),
        onFile: (peerId, { mime, blob, name }) => {
          if (mutedPeers.has(peerId)) return; // muted: drop their images/video/files
          groupApi?.addFile(roomHandles.get(peerId) || String(peerId).slice(0, 4), { mime, url: URL.createObjectURL(blob), name });
        },
        onState: () => {},
      },
    );
    const peerNow = sfuPeer;
    sfuPeer
      .start()
      .then(async () => {
        if (!roomCam || sfuPeer !== peerNow || peerNow.hasProducer('video')) return;
        if (!(await roomCameraSeesPerson())) return nobodyOnRoomCamera();
        if (sfuPeer !== peerNow) return; // left the room while looking
        try {
          await peerNow.addTrack(roomCam);
        } catch (err) {
          if (isSlotRefusal(err)) onVideoRefused();
        }
      })
      .catch(() => toast('Could not connect to the room media. Try rejoining.'));
    // Walking in with a live microphone or camera is not quietly listening:
    // the server clears observer mode when the track is produced, and the
    // roster mark here should agree without waiting for it.
    if (publish && publish.getTracks().some((track) => track.readyState === 'live')) groupApi?.setSelfObserving?.(false);
    startModSampler();
    startAudioSampler();
  }

  function setupPeer(isCaller) {
    teardownPeer();
    remoteReady = false;
    pendingIce = [];
    peer = new PeerConnection(
      {
        iceServers: iceConfig.iceServers,
        iceTransportPolicy: iceConfig.iceTransportPolicy,
        isCaller,
        localStream: sendStream || localStream,
      },
      {
        onRemoteStream: (stream) => {
          chatApi?.setRemoteStream(stream);
          remoteHasVideo = !!stream && (stream.getVideoTracks?.() || []).length > 0;
          watchRemoteVideo(); // the layout follows the picture, not the track
          chatApi?.hideStatus();
          chatApi?.pulse();
          chatApi?.focusInput();
        },
        onIce: (candidate) => signaling?.send(ClientMsg.SIGNAL, { kind: 'ice', data: candidate }),
        onFile: ({ mime, blob, name }) => {
          const url = URL.createObjectURL(blob);
          chatApi?.addFile('them', { mime, url, name });
        },
        onState: (state) => {
          if (state === 'connected') chatApi?.hideStatus();
          if (state === 'failed') toast('Connection trouble. Finding someone new...');
        },
      },
    );

    if (isCaller) {
      peer
        .createOffer()
        .then((offer) => signaling?.send(ClientMsg.SIGNAL, { kind: 'offer', data: offer }))
        .catch(() => toast('Could not start the call.'));
    }
  }

  async function handleSignal({ kind, data }) {
    if (!peer) return;
    try {
      if (kind === 'offer') {
        const answer = await peer.acceptOffer(data);
        signaling?.send(ClientMsg.SIGNAL, { kind: 'answer', data: answer });
        remoteReady = true;
        flushIce();
      } else if (kind === 'answer') {
        await peer.acceptAnswer(data);
        remoteReady = true;
        flushIce();
      } else if (kind === 'ice') {
        if (remoteReady) peer.addIce(data);
        else pendingIce.push(data);
      }
    } catch {
      /* ignore malformed signaling */
    }
  }

  function flushIce() {
    for (const c of pendingIce) peer?.addIce(c);
    pendingIce = [];
  }

  // Files go THROUGH the server, never peer to peer: the server is the only
  // place they can be classified before the other person sees them, and it
  // archives them as evidence. A file the server blocks is simply never
  // delivered -- so the receiving side has nothing to un-see.
  async function sendFile(file) {
    // A browser that does not know HEIC reports no type at all for one, so an
    // iPhone photo copied to a laptop or an Android phone was turned away here
    // as "only images" before the server, which reads the bytes and can now
    // convert it, ever saw it. The name is all there is to go on, and the
    // server decides what the bytes really are.
    const declared = file.type || (/\.(heic|heif)$/i.test(file.name || '') ? 'image/heic' : '');
    if (!isShareableFile(declared, file.size)) {
      // Caught here so a 25 MB video is never uploaded just to be refused.
      toast(
        file.type && !file.type.startsWith('image/')
          ? 'Only images can be shared. Video and audio cannot be sent on Lumi.'
          : 'Only images can be shared, up to 25 MB.',
      );
      return;
    }
    if (!shareKey) {
      toast('Wait until you are connected to share.');
      return;
    }
    toast('Checking that file...');
    let res;
    try {
      res = await fetch(`${SERVER_HTTP}/api/share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/octet-stream',
          'X-Share-Key': shareKey,
          'X-Share-Mime': declared,
          'X-Share-Name': encodeURIComponent(file.name || ''),
        },
        body: file,
      });
    } catch {
      toast('Could not share that file.');
      return;
    }
    // Read the body ONCE, out here, because the success path needs it too. It
    // used to be declared inside the failure branch and read after it, so every
    // accepted upload threw ReferenceError: the file reached the other person
    // and the sender's own copy never appeared, which reads as "it did not
    // send" and invites them to send it again.
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      toast(body.message || 'That file could not be shared.');
      return;
    }
    // Only show it on my own side once the server accepted it. The server's
    // msgId keys my copy the same way it keys everyone else's, so a reaction
    // or an unsend lands on the same message on every screen.
    // The type the server read from the bytes, not the one this browser declared.
    //
    // And the picture the server kept, when that is not the file picked: an
    // iPhone photo is converted to a JPEG on the way (so the other person can
    // see it), and a browser that cannot show a HEIC would otherwise show its
    // own sender a blank square. Anything else is shown from the file itself,
    // which costs no second download.
    let url = URL.createObjectURL(file);
    if (body.mime && declared && body.mime !== declared && body.shareId) {
      try {
        const own = await fetch(`${SERVER_HTTP}/api/share/${encodeURIComponent(body.shareId)}`, { headers: { 'X-Share-Key': shareKey } });
        if (own.ok) {
          URL.revokeObjectURL(url);
          url = URL.createObjectURL(await own.blob());
        }
      } catch {
        /* the local copy stands */
      }
    }
    const me = { mime: body.mime, url, name: file.name };
    const opts = { id: body.msgId };
    if (groupApi) groupApi.addFile('me', me, opts);
    else chatApi?.addFile('me', me, opts);
  }

  /** Fetch a file the server has cleared, and show it. */
  async function receiveSharedFile(m) {
    if (!m || !m.shareId || !shareKey) return;
    try {
      const res = await fetch(`${SERVER_HTTP}/api/share/${encodeURIComponent(m.shareId)}`, {
        headers: { 'X-Share-Key': shareKey },
      });
      if (!res.ok) return;
      const blob = await res.blob();
      // rtc.js checks isShareableFile() on both the metadata and the
      // assembled blob; this path took the type and the name straight from the
      // message. The elements it lands in are inert and the CSP covers blob
      // documents, so the risk is a spoofed filename and a media element
      // pointed at something that is not an image -- but "images only" should
      // mean the same thing whichever way the bytes arrived.
      // The Content-Type the server sent, which it read from the bytes.
      const mime = blob.type || m.mime || '';
      if (!isShareableFile(mime, blob.size)) return;
      const item = { mime, url: URL.createObjectURL(blob), name: m.name || 'file' };
      const opts = { id: m.msgId, peerId: m.from };
      if (groupApi) groupApi.addFile(roomHandles.get(m.from) || m.from || 'them', item, opts);
      else chatApi?.addFile('them', item, opts);
    } catch {
      /* the file simply does not appear */
    }
  }

  /**
   * Turn on mic and/or camera mid-chat (for a user who started in audio or text).
   * Captures the missing tracks, routes new audio through the gain node, adds them
   * to the local + send streams, and produces them to the SFU so the partner sees
   * them without a rematch.
   */
  async function enableMedia({ audio = false, video = false }) {
    if (!sfuEnabled || !sfuPeer) {
      toast('Wait until you are connected.');
      return;
    }
    if (video && !videoAllowed) return;
    const haveAudio = !!(localStream && localStream.getAudioTracks().length);
    const haveVideo = !!(localStream && localStream.getVideoTracks().length);
    const wantAudio = audio && !haveAudio;
    const wantVideo = video && !haveVideo;
    if (!wantAudio && !wantVideo) return;

    let captured;
    try {
      captured = await navigator.mediaDevices.getUserMedia({ audio: wantAudio, video: wantVideo });
    } catch {
      toast(wantVideo ? 'Lumi needs camera permission for that.' : 'Lumi needs microphone permission for that.');
      return;
    }
    if (!localStream) localStream = new MediaStream();
    if (!sendStream) sendStream = new MediaStream();
    const toProduce = [];

    for (const v of captured.getVideoTracks()) {
      rawCamTrack = v;
      localStream.addTrack(v); // self-view stays the raw camera
      let outV = v;
      if (videoFxOn()) {
        try {
          videoEnhancer = createVideoEnhancer(v, { segmenter: bgSegmenter });
          videoEnhancer.setBlur(!!effectPrefs.blur);
          videoEnhancer.setFilter(effectPrefs.filter);
           
          await videoEnhancer.start();
          outV = videoEnhancer.track;
        } catch {
          outV = v;
          videoEnhancer = null;
        }
      }
      sendStream.addTrack(outV);
      toProduce.push(outV);
    }
    for (const a of captured.getAudioTracks()) {
      localStream.addTrack(a); // self-view is muted, so this is harmless
      let outTrack = a;
      try {
        if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        if (!micGain) micGain = audioCtx.createGain();
        const src = audioCtx.createMediaStreamSource(new MediaStream([a]));
        const dest = audioCtx.createMediaStreamDestination();
        // Noise suppression (#9) sits between the mic and the gain; bypassed unless on.
        audioEnhancer = createAudioEnhancer(audioCtx, src);
        audioEnhancer.setEnabled(!!effectPrefs.denoise);
        audioEnhancer.output.connect(micGain).connect(dest);
        audioCtx.resume?.();
        outTrack = dest.stream.getAudioTracks()[0]; // gain-routed -> the volume slider applies
      } catch {
        outTrack = a; // Web Audio unavailable -- send the raw mic
      }
      sendStream.addTrack(outTrack);
      toProduce.push(outTrack);
    }

    let roomCameraRefused = false;
    for (const track of toProduce) {
      // In a 1:1, turning the camera on is your half of the reveal. The track
      // goes up regardless; the server shows it to them only once both have.
      if (track.kind === 'video' && inOneOnOne() && !reveal.both) requestReveal();
      // In a room, a camera goes live only with somebody in front of it.
      if (track.kind === 'video' && !inOneOnOne() && groupApi && !(await roomCameraSeesPerson())) {
        roomCameraRefused = true;
        continue;
      }
      try {
         
        await sfuPeer.addTrack(track);
      } catch (err) {
        // A camera refused for want of a slot is not a failure to report as
        // one: the person is in, with sound, and will be put on camera later.
        if (track.kind === 'video' && isSlotRefusal(err)) onVideoRefused();
        else toast('Could not turn that on. Please try again.');
      }
    }
    chatApi?.setLocalStream(localStream);
    pushRevealUi(); // a camera that just appeared changes what the reveal can offer
    // Last, so the camera button is not rebuilt as "on" after it is set off.
    if (roomCameraRefused) nobodyOnRoomCamera();
  }

  // ---- Media effects (#8 background blur, #9 noise suppression, #11 filters) -------
  // A pluggable selfie segmenter for background blur. Null until a self-hosted
  // segmentation model is bundled (then blur composites a sharp person over a blurred
  // background); without it, blur is a safe no-op and the filters still apply.
  // Resume the mic AudioContext on the user's next interaction (it can start suspended when
  // created behind the camera-permission prompt). Idempotent; self-removes once running.
  function armAudioContextResume() {
    const tryResume = () => {
      if (!audioCtx) return;
      audioCtx
        .resume?.()
        .then(() => {
          if (!audioCtx || audioCtx.state === 'running') {
            document.removeEventListener('pointerdown', tryResume, true);
            document.removeEventListener('keydown', tryResume, true);
          }
        })
        .catch(() => {});
    };
    document.addEventListener('pointerdown', tryResume, true);
    document.addEventListener('keydown', tryResume, true);
  }

  // Background blur uses a pluggable segmenter. The real one is a self-hosted
  // selfie-segmentation model (loaded lazily, on first use, because it pulls a
  // few megabytes of wasm); until it is ready -- or on any device where it
  // cannot run -- the no-model focus segmenter keeps blur working. The enhancer
  // holds THIS stable wrapper, so the upgrade applies to a chat already running.
  const focusSegmenter = createFocusSegmenter();
  let mlSegmenter = null;
  let mlSegmenterLoading = null;
  const bgSegmenter = (video, w, h) => (mlSegmenter || focusSegmenter)(video, w, h);
  /**
   * The real segmentation model, loaded once and shared by background blur and
   * the person check (person-check.js). Resolves to the model, or null when it
   * cannot run here. One promise, so a check that asks while blur is still
   * loading waits for the same model instead of reading the focus fallback,
   * which would say there is a person in front of every camera.
   */
  function mlSegmenterReady() {
    if (!mlSegmenterLoading) {
      mlSegmenterLoading = import('./selfie-segmenter.js')
        .then(({ createSelfieSegmenter }) => createSelfieSegmenter())
        .then((ml) => {
          if (ml) mlSegmenter = ml;
          return ml || null;
        })
        .catch(() => null);
    }
    return mlSegmenterLoading;
  }
  async function ensureMlSegmenter() {
    await mlSegmenterReady(); // a model that cannot load keeps the focus fallback
  }
  function videoFxOn() {
    return !!effectPrefs.blur || (!!effectPrefs.filter && effectPrefs.filter !== 'none');
  }
  // Point the self-view at the PROCESSED video (the enhancer's canvas track) when an effect
  // is active, else the raw camera -- so the user actually sees their filter / blur.
  function refreshSelfView() {
    const vt = (videoEnhancer && videoEnhancer.track) || rawCamTrack;
    if (!vt) return;
    chatApi?.setSelfVideo?.(vt);
    groupApi?.setSelfVideo?.(vt);
  }
  async function onEffect(kind, value) {
    if (kind === 'denoise') {
      effectPrefs.denoise = !!value;
      audioEnhancer?.setEnabled(!!value);
    } else if (kind === 'blur') {
      effectPrefs.blur = !!value;
      if (value) ensureMlSegmenter(); // load the model in the background
      await applyVideoEffects();
    } else if (kind === 'filter') {
      effectPrefs.filter = value || 'none';
      await applyVideoEffects();
    }
    try {
      localStorage.setItem('lumi.effects', JSON.stringify(effectPrefs));
    } catch {
      /* ignore */
    }
  }
  // Bring the live SENT video in line with the current effect prefs -- build or tear
  // down the canvas processor and swap the produced track, without dropping the call.
  async function applyVideoEffects() {
    if (!rawCamTrack) return; // no camera -> prefs apply on the next capture
    const want = videoFxOn();
    if (want && !videoEnhancer) {
      try {
        videoEnhancer = createVideoEnhancer(rawCamTrack, { segmenter: bgSegmenter });
        videoEnhancer.setBlur(!!effectPrefs.blur);
        videoEnhancer.setFilter(effectPrefs.filter);
        await videoEnhancer.start();
        await sfuPeer?.replaceTrack('video', videoEnhancer.track);
      } catch {
        videoEnhancer = null;
      }
    } else if (!want && videoEnhancer) {
      try {
        await sfuPeer?.replaceTrack('video', rawCamTrack);
        videoEnhancer.stop();
      } catch {
        /* ignore */
      }
      videoEnhancer = null;
    } else if (videoEnhancer) {
      videoEnhancer.setBlur(!!effectPrefs.blur);
      videoEnhancer.setFilter(effectPrefs.filter);
    }
    refreshSelfView(); // show the effect on the user's own preview, not just to the peer
  }

  // ---- Image moderation: periodically sample the local camera and send each frame to
  // the server, which classifies it with the self-hosted moderator. Runs only when the
  // server advertises a moderator backend (moderateImages) and a live camera is on.
  const MOD_SAMPLE_MS = 20000;
  // One camera frame is on its way up. See sendFrame: they are never queued.
  let modFrameInFlight = false;
  function startModSampler() {
    if (!moderateImages || modSampler) return;
    if (!modSampleVideo) {
      modSampleVideo = document.createElement('video');
      modSampleVideo.muted = true;
      modSampleVideo.playsInline = true;
    }
    const canvas = document.createElement('canvas');
    const cctx = canvas.getContext('2d');
    // Stay inside the server's frame ceiling (b64 <= 16M chars, about 12 MB of
    // JPEG): a frame that overshoots is re-encoded at a lower quality, and only
    // as a last resort smaller, NEVER dropped. The operator reviews these at
    // the size the camera made them (standing rule, 2026-09-15).
    const B64_BUDGET = 15_000_000;
    const encodeFrame = (scale, quality) =>
      new Promise((resolve) => {
        const vw = modSampleVideo.videoWidth;
        const vh = modSampleVideo.videoHeight;
        canvas.width = Math.max(1, Math.round(vw * scale));
        canvas.height = Math.max(1, Math.round(vh * scale));
        cctx.drawImage(modSampleVideo, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
      });
    // A frame with no variation carries nothing to moderate, and the classifier
    // returns confident nonsense on one -- which is how solid black frames ended
    // up in the queue labelled nsfw, each one a false accusation against a real
    // person sitting next to a Ban button. Checked on a 32x32 copy, so the cost
    // is negligible.
    const probe = document.createElement('canvas');
    probe.width = 32;
    probe.height = 32;
    const pctx = probe.getContext('2d', { willReadFrequently: true });
    const frameIsBlank = () => {
      try {
        pctx.drawImage(modSampleVideo, 0, 0, 32, 32);
        const { data } = pctx.getImageData(0, 0, 32, 32);
        let sum = 0;
        let sumSq = 0;
        const n = 32 * 32;
        for (let i = 0; i < data.length; i += 4) {
          const luma = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
          sum += luma;
          sumSq += luma * luma;
        }
        const mean = sum / n;
        const variance = sumSq / n - mean * mean;
        return variance < 12; // near-uniform: a lens cap, a warming sensor, a disabled track
      } catch {
        return false; // never let the guard itself stop moderation
      }
    };

    /**
     * The frame, up its own request rather than down the chat socket.
     *
     * A full camera frame is about a megabyte, and almost everybody here is on
     * a phone. Sent inside a socket message every twenty seconds it backed the
     * socket up until the heartbeat could not get through and the chat died
     * (the operator, 2026-09-21: "connections keep dropping on 1:1"). Its own
     * request means a slow upload costs only the upload, and the picture goes
     * as itself instead of base64, which is a third fewer bytes for the same
     * evidence.
     *
     * One at a time: on a weak uplink the next sample comes round before this
     * one is up, and queueing them is how the backlog started. A frame that
     * cannot be sent is dropped rather than retried; another follows in twenty
     * seconds.
     */
    const sendFrame = async (blob) => {
      if (!shareKey || modFrameInFlight) return;
      modFrameInFlight = true;
      try {
        await fetch(`${SERVER_HTTP}/api/mod/frame`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/octet-stream', 'X-Share-Key': shareKey },
          body: blob,
        });
      } catch {
        /* the next sample is twenty seconds away */
      } finally {
        modFrameInFlight = false;
      }
    };

    const sample = async () => {
      const track = localStream?.getVideoTracks?.()[0];
      if (!signaling || !track || track.readyState !== 'live') return;
      if (modFrameInFlight) return; // the last one is still going up
      // A camera nobody has opened is not moderated (the server drops such
      // frames too). Rooms have no reveal.
      if (inOneOnOne() && !reveal.both) return;
      // Camera off. The button DISABLES the track rather than stopping it, so it
      // stays "live" while emitting black -- moderating that is pointless.
      if (track.enabled === false) return;
      try {
        if (modSampleVideo.srcObject !== localStream) {
          modSampleVideo.srcObject = localStream;
          modSampleVideo.play?.().catch(() => {});
        }
        if (!modSampleVideo.videoWidth) return;
        if (frameIsBlank()) return;
        // Evidence-grade capture at the camera's FULL native resolution -- a
        // human judges this exact frame in the admin queue. The classifier
        // downscales internally either way. Quality steps down only if the
        // encoded frame would blow the transport budget, and the frame
        // itself shrinks only when no quality fits, which no camera met so
        // far reaches.
        let blob = null;
        for (const [scale, quality] of [[1, 1], [1, 0.85], [1, 0.7], [1, 0.6], [0.85, 0.6]]) {
           
          blob = await encodeFrame(scale, quality);
          if (blob && (blob.size * 4) / 3 < B64_BUDGET) break;
        }
        if (!blob) return;
        await sendFrame(blob);
      } catch {
        /* a sampling hiccup is non-fatal */
      }
    };
    modSampler = setInterval(sample, MOD_SAMPLE_MS);
    setTimeout(sample, 4000); // first sample a few seconds after connecting
  }
  function stopModSampler() {
    if (modSampler) {
      clearInterval(modSampler);
      modSampler = null;
    }
    if (modSampleVideo) modSampleVideo.srcObject = null;
  }

  // ---- Audio moderation + live captions: record short mic clips and send them to the
  // server, which transcribes via the self-hosted ASR. Runs only when the server
  // advertises an ASR backend and the mic is live.
  const ASR_CLIP_MS = 5000;
  function pickAudioOpts() {
    const cands = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus'];
    for (const c of cands) {
      if (window.MediaRecorder && MediaRecorder.isTypeSupported(c)) return { mimeType: c };
    }
    return {};
  }
  function startAudioSampler() {
    if (!asrEnabled || audioActive || typeof MediaRecorder === 'undefined') return;
    audioActive = true;
    const loop = () => {
      if (!audioActive) return;
      const track = localStream?.getAudioTracks?.()[0];
      if (!signaling || !track || track.readyState !== 'live') {
        audioTimer = setTimeout(loop, 1500);
        return;
      }
      try {
        const rec = new MediaRecorder(new MediaStream([track]), pickAudioOpts());
        const chunks = [];
        rec.ondataavailable = (e) => {
          if (e.data && e.data.size) chunks.push(e.data);
        };
        rec.onstop = () => {
          audioRecorder = null;
          const blob = new Blob(chunks, { type: rec.mimeType || 'audio/webm' });
          if (blob.size) {
            const reader = new FileReader();
            reader.onload = () => {
              const b64 = String(reader.result).split(',')[1] || '';
              if (b64) signaling?.send(ClientMsg.MOD_AUDIO, { audio: b64 });
            };
            reader.readAsDataURL(blob);
          }
          if (audioActive) audioTimer = setTimeout(loop, 400);
        };
        audioRecorder = rec;
        rec.start();
        setTimeout(() => {
          try {
            if (rec.state === 'recording') rec.stop();
          } catch {
            /* ignore */
          }
        }, ASR_CLIP_MS);
      } catch {
        audioTimer = setTimeout(loop, 2000);
      }
    };
    audioTimer = setTimeout(loop, 2500);
  }
  function stopAudioSampler() {
    audioActive = false;
    clearTimeout(audioTimer);
    try {
      if (audioRecorder?.state === 'recording') audioRecorder.stop();
    } catch {
      /* ignore */
    }
    audioRecorder = null;
  }

  function teardownPeer() {
    peer?.close();
    peer = null;
    remoteReady = false;
    pendingIce = [];
    teardownSfuPeer();
    stopModSampler();
    stopAudioSampler();
    if (chatApi) chatApi.setRemoteStream(null);
    remoteHasVideo = false;
    remoteWatch?.stop();
    remoteWatch = null;
    remoteVideoLive = false;
  }

  // Let go of the camera and the microphone: every track stopped, so the
  // camera light goes out, and every processor built on them torn down. Used
  // on leaving, and on walking into a TEXT room -- a room whose whole point is
  // typing must not keep a camera open that was granted for something else.
  function releaseLocalMedia() {
    if (videoEnhancer) {
      videoEnhancer.stop();
      videoEnhancer = null;
    }
    audioEnhancer = null;
    rawCamTrack = null;
    if (localStream) {
      localStream.getTracks().forEach((track) => track.stop());
      localStream = null;
    }
    if (audioCtx) {
      audioCtx.close().catch(() => {});
      audioCtx = null;
    }
    micGain = null;
    sendStream = null;
  }

  function cleanup() {
    // The age proof is NOT cleared here. It lives per tab (sessionStorage) and
    // for two hours, which bounds a shared device well enough; clearing it on
    // every return to the landing would send everyone who changes mode back
    // through the age estimator, and that costs more than it protects for now.
    teardownPeer();
    clearTimeout(slowDownTimer);
    slowDownTimer = null;
    cancelQuietHint();
    cancelInviteOffer();
    signaling?.send?.(ClientMsg.LEAVE);
    signaling?.close();
    channels.unbind(signaling);
    signaling = null;
    // Leaving on purpose is not something to recover from. Signaling.close()
    // already stops the close event, but a reconnect may be mid-flight in its
    // own timer, and it checks these.
    reconnecting = false;
    resumeSeed = null;
    releaseLocalMedia();
    chatApi = null;
    roomsApi = null;
    groupApi?.dispose?.(); // let the room take its window listeners with it
    groupApi = null;
    currentRoom = null;
    roomHandles.clear();
    intent = '1on1';
    // No call survives the socket: the sheets come down with it. Before the
    // panel closes, so its closing cannot read "a call just ended" and search.
    callsOn = false;
    inCall = false;
    pairedNow = false;
    forgetChat();
    afterCall = false;
    callingSheet?.close();
    offerSheet?.close();
    callingSheet = null;
    offerSheet = null;
    // The bells went with their screens; the panel, if open, now has no socket.
    bells.clear();
    lastPairId = null;
    social = false;
    messagesOn = false;
    regularsPanel?.close?.();
    regularsPanel = null;
    communityCard = null;
    communityPanel = null;
    communitySection = null;
    communityState = null;
  }

  async function initAccount() {
    await account.refresh().catch(() => {});
    applyCosmetics();
    refreshKeepsakeName();
    // Handle a return from checkout (stub completes in place; Stripe returns here).
    const params = new URLSearchParams(location.search);
    const flow = params.get('billing');
    if (flow === 'stub' && params.get('token')) {
      const r = await account.stubComplete(params.get('token'));
      if (r.status === 200) toast('Purchase complete.');
      applyCosmetics();
    } else if (flow === 'success') {
      await account.refresh().catch(() => {});
      toast('Thanks! Your purchase is active.');
      applyCosmetics();
    }
    if (flow) history.replaceState({}, '', location.pathname);
    // Deep link from the Lumi Plus page to the in-app shop (cosmetics, Room Pro, donate).
    if (params.get('shop') === 'open') {
      openPlus();
      history.replaceState({}, '', location.pathname);
    }
    offerAccountName();
    syncAccountLink();
    checkNotice(); // celebrate an admin-granted Plus on load; one granted later is pushed (accountPushed)
  }
  initAccount();

  // A reload with a 1:1 still held for us on the server (chat-seed.js):
  // straight back in, past the landing, with the handle the person had. The
  // seed is consumed either way, so a start that fails is an ordinary
  // landing, never a loop. No user gesture has happened yet, so the tones
  // wait for the first tap, as they do after any reload.
  {
    const held = chatToResume(RECONNECT_GRACE_MS);
    if (held) {
      let savedHandle = null;
      try {
        savedHandle = localStorage.getItem('lumi.handle');
      } catch {
        /* storage unavailable */
      }
      start({ birthYear: held.birthYear, mode: held.mode, handle: savedHandle || undefined }).catch(() => {});
    }
  }

  return { showLanding };
}
